import type { GenericNestedObject } from '@common/utils/nestedTypedObject';
import { FundusScreeningThirdParty } from '@common/fundusScreening/thirdParties';
import { FundusScreeningManualImportLogInvitationType } from '@common/interfaces/api/fundusScreeningApi';
import { MINIMUM_PASSWORD_LENGTH } from '@common/practitionerDomain';
import type { DefaultLanguage } from './translate';
import { countries } from './countries/enCountries';

export const en: DefaultLanguage = {
    CareProductTypeEnum: {
        DBC: 'DBC',
        MKC: 'MKC',
        OVP: 'OVP',
    },
    Countries: countries,
    StatusLabelEnum: {
        aborted: 'Aborted',
        accepted: 'Accepted',
        acceptedRequester: 'Accepted',
        analyzed: 'Analyzed',
        answered: 'Answered',
        answeredFirstRound: 'Answered (1st round)',
        answeredSecondRound: 'Answered (2nd round)',
        assetNeeded: 'Diary needed',
        beingGraded: 'Being graded',
        beingPerformed: 'Being performed',
        cancelled: 'Cancelled',
        closed: 'Closed',
        completed: 'Completed',
        dataTransferred: 'Data read',
        dischargeCompleted: 'Discharge completed',
        dischargeDraft: 'Draft discharge',
        dischargeSent: 'Discharge sent',
        draftConsult: 'Draft consult',
        draftContact: 'Draft contact',
        draftCounseling: 'New counseling',
        draftReferral: 'New referral',
        draftRequest: 'Draft request',
        failed: 'Failed',
        inAnalysis: 'In Analysis',
        inConcept: 'Draft',
        inProgress: 'In progress',
        intakeCompleted: 'Intake completed',
        intakeCompletedAdmin: 'Intake',
        intakeDraft: 'Draft intake',
        intakeSent: 'Intake sent',
        invited: 'Invited',
        newContact: 'New contact',
        notYetSent: 'Not yet sent',
        rejected: 'Rejected',
        rejectedAndArchived: 'Archived',
        rejectedAndResent: 'Referred again',
        requested: 'Requested',
        saved: 'Saved',
        sent: 'Sent',
        sentFirstRound: 'Sent (1st round)',
        sentSecondRound: 'Sent (2nd round)',
        started: 'Started',
        successful: 'Successful',
        supervised: 'Supervised',
        toAccept: 'To accept',
        toAcceptReferral: 'To accept',
        toAnalyze: 'To Analyze',
        toAnswer: 'To Answer',
        toGrade: 'To grade',
        toPerform: 'To process',
        toProcess: 'To process',
        toSupervise: 'To supervise',
        withAdmin: 'With admin',
        withAnalysis: 'With analyst',
        withBiometrician: 'With biometrician',
        withColleague: 'With a colleague',
        withGrader: 'With grader',
        withPatient: 'With patient',
        withSpecialist: 'Sent to specialist',
        withSupervisor: 'With supervisor',
    },
    StatusLabelPatientTaskEnum: {
        completed: 'Completed',
        open: 'To be done',
    },
    VatTypesEnum: {
        exempt: 'Exempt',
        general: 'General',
    },
    abortProcessReasonsEnum: {
        cardioHolterTooSmall: 'Cardio: Holter recording too small',
        cardioRecordingNotStarted: 'Cardio: Recording not started',
        doubleEntry: 'Double input',
        doubleReferral: 'Double referral',
        failed: 'Failed',
        languageBarrier: 'Language barrier',
        mistakeInReferral: 'Mistake in referral',
        newQuestionnairesSent: 'New questionnaires have been sent for the patient',
        noPhotosAdded: "No photo's added",
        noSmartphone: 'No smartphone',
        noSuitableSmartphone: 'No suitable smartphone',
        nonContractedCare: 'Non-contracted care',
        other: 'Other',
        otherMedicalPathway: 'Other medical pathway',
        patientRefrainsFromExaminationOrNoInterest: 'Patient refrains from examination / No interest',
        preferenceForPhysicalConsultations: 'Preference for physical consultations',
        softwareTooDificult: 'Software too dificult',
        test: 'Test',
        tooExpensiveOrExcessBurden: 'Too expensive / Excess burden',
        unableContactWithPatient: 'Unable to make contact with patient',
        unknownReason: 'Unknown reason',
        wrongEntry: 'Wrong input',
        wrongPatient: 'Wrong patient',
        wrongPhotos: "Wrong photo's added",
    },
    absence: {
        endTimePeriod: 'end period',
        startTimePeriod: 'start period',
    },
    accountDeactivation: {
        deactivate: 'Remove practitioner',
        employeeFromOrganization: 'Employee from organization',
        hasActiveRequestsGp:
            'Note that this care provider still has active care processes. Please contact Ksyos on 020 600 0060 to fully de-activate the account.',
        hasActiveRequestsSupport: 'Note that this care provider still has active care processes.',
        hasActiveRequestsTitle: 'Active processes',
        infoText:
            'This healthcare provider will no longer have access to the Ksyos platform after the date below (end of day).',
    },
    accountRequest: {
        accountApproved: 'Account approved',
        accountDenied: 'Account denied',
        accountDeniedWithReason: 'The account request has been denied with the following reason: ',
        accountRequest: 'Account request',
        accountType: 'Account type',
        accountTypeEnum: {
            activeUser: 'Active user of the Ksyos platform (with login)',
            withoutAccess: 'Referrer without access to Ksyos platform (e.g., ZorgDomein)',
        },
        addCareProvider: 'Add Care Provider',
        agbCodeOrPracticeHolderInfo:
            'You have chosen a profession that requires an AGB code. Enter an AGB code or choose a practice holder. For support, contact us at 020 600 0060',
        agbCodeOrSupervisorInfo:
            'You have chosen a profession that requires an AGB code. Enter an AGB code, or choose a supervisor.',
        approve: 'Approve',
        approved: 'Approved',
        approver: 'Approver',
        approverPractitioner: 'To be approved by',
        approverPractitionerDescription:
            'Because this healthcare provider will have access to patient data, it is still necessary to request approval from another colleague from your organisation. This colleague will receive an email to review the request in the Ksyos platform.',
        contactSupportInfo: 'For support, contact us at 020 600 0060',
        declineAccount: 'Decline account',
        declineAccountRequest: 'Decline account request',
        details: 'Details',
        gender: 'Gender',
        identifierDeclaration: 'AGB-code',
        introPageTextPartOne: '{0} wants to add',
        introPageTextPartTwo:
            'as {0} to {1}. Because this healthcare provider will have access to patient data, it is necessary to review this request.',
        ksyosReviewsRequest: 'Ksyos will process the request',
        nameAndGenderExistAtSameOrganizationAlert:
            'There is already an account with this name and this gender with this organization. Is it true that you still want to create an account?',
        nameFamily: 'Family name',
        nameGiven: 'First name',
        nameInfix: 'Infix',
        openTheKsyosPlatform: 'Open the Ksyos platform',
        pending: 'Pending review',
        practiceHolder: 'Practice holder',
        practitionerAwaitingApprovalFromKsyosTextForGp:
            'It appears that an account request for this care provider has already been submitted within your organisation, but this has not yet been assessed, please contact KSYOS via 020 600 0060.',
        practitionerAwaitingApprovalModalTitle: 'Account not yet rated',
        practitionerAwaitingApprovalTextForGp:
            'It appears that an account request for this healthcare provider has already been submitted within your organisation, but it has not yet been reviewed by {0}. Please ask the reviewer to review the account application or contact Ksyos on 020 600 0060.',
        practitionerDuplicatedModalText:
            'The data matches an existing care provider. This employee may have worked with Ksyos before. Contact Ksyos at 020 600 0060 for assistance.',
        practitionerDuplicatedModalTitle: 'This healthcare provider already exists',
        practitionerDuplicatedNameAndGenderModalTextForGp:
            'There is already an account within your organization with this last name and this gender. Is it true that you want to create an account with the same data? Contact Ksyos via 020 600 0060.',
        practitionerDuplicatedRegistrationCodeModalTextForGp:
            'An account already exists within this organization with these details. Do you want to create another account with the same data? Please contact Ksyos at 020 600 0060.',
        practitionerExistingAtExternalOrganizationAlertText:
            'These details are already known to Ksyos. If these data are not correct, please contact Ksyos on 020 600 0060.',
        profession: 'Profession',
        registrationCodeExistsAtExternalOrganizationAlert: 'These details already exist at Ksyos.',
        registrationCodeExistsAtSameOrganizationAlert:
            'An account already exists within this organization with these details.',
        reject: 'Reject',
        rejected: 'Rejected',
        rejectedAccountRequest: 'Rejected accountrequest',
        rejectedAt: 'Rejected at',
        rejectionReason: 'Reason for rejection',
        requestedBy: 'Requested by',
        reviewByKsyos: {
            alertWithMatches: {
                description:
                    'Select an existing healthcare provider to add to the organization. If you want to create a new account, do not select a healthcare provider and click approve.',
                title: 'The data corresponds to an existing healthcare provider',
            },
            alertWithoutMatches: {
                title: 'There was a match at the time of the request, but not anymore.',
            },
        },
        reviewRequest: 'Review request',
        reviewedBy: 'Reviewed by',
        selectMatchingPractitionerInfoAlert:
            'The data corresponds to an existing healthcare provider. Select a healthcare provider to add to the organization.',
        startDate: 'Start date',
        understandingText:
            'I am aware that our organization is responsible for creating a new account in the Ksyos platform. This account is exclusively intended for an employee within my organization.',
        waitingForApprovalOnAgbSameOrganizationAlertFirst: 'An ',
        waitingForApprovalOnAgbSameOrganizationAlertSecond:
            ' with this AGB code is still awaiting approval at this organization',
        waitingForApprovalOnNameAndGenderSameOrganizationAlertFirst: 'In this organization there is an ',
        waitingForApprovalOnNameAndGenderSameOrganizationAlertSecond:
            ' with this name and gender in evaluation. Is it true that you still want to create an account?',

        willReceiveNotificationEmail: '{0} will receive an activation email.',
        workEmail: 'Work email',
        wrongApprover: 'Review submitted for a colleague',
        wrongApproverDescription:
            'It seems you used a link that was intended for a colleague from your organization. Check the organization overview to see who may review this account request.',
        zorgMailNumber: 'ZorgMail number',
        zorgmailNummerTooltip: 'Ask the general practitioner or practice manager for the ZorgMail number',
    },
    activity: {
        activityCode: 'Activity code',
        performedDate: 'Performed date',
    },
    activityTypeEnum: {
        biometrics: 'Biometrics',
        consultation: 'Consultation',
        glaucoma: 'Glaucoma',
        grading: 'Grading',
        qualityControl: 'Quality Control',
        request: 'Request',
        respondingConsultation: 'Responding consultation',
    },
    addCarePathSectionTitles: {
        serviceConsumerSettings: 'Requester',
        serviceProviderSettings: 'Performer',
        serviceReaderSettings: 'Reader',
        serviceSupervisorSettings: 'Supervisor',
    },
    addCarePathSettingsHeaders: {
        activities: 'Activities',
        activitiesRights: 'Rights for activities',
        analysis: 'Analysis',
        canEditDashboard: 'Can edit dashboard',
        canEditGroupSession: 'Can edit Group Session management',
        canEditMdo: 'Can edit MDO management',
        canExportForDato: 'Can export for DATO',
        canReadDashboard: 'Can view dashboard',
        canReadGroupSession: 'Can view Group Session management',
        canReadMdo: 'Can view MDO management',
        canReadQuestionnaireResponses: 'Can view questionnaires',
        canReadTotalWeightLoss: 'Can view body weight measurements',
        canRegisterGroupSessionAttendance: 'Can register Group Session attendance',
        closeServiceAndSubsequentService: 'Close service and subsequent service',
        dashboardManagement: 'Dashboard management',
        examinationAndGrading: 'Examination and grading',
        exportManagement: 'Export management',
        followUpMeasurementGgz: 'Follow up measurement',
        groupMeetingManagement: 'Group meeting management',
        monitoringManagement: 'Monitoring management',
        notifications: 'Notifications',
        receiveRequest: 'Receive request',
        selectConsumer: 'Select consumer',
        selectGrader: 'Select grader',
        software: 'Software',
        startRequest: 'Start request',
        txcsIds: "TxCS ID's",
        zorgMailAndNotifications: 'ZorgMail and notifications',
    },
    addCarePathSettingsLabelsServiceConsumer: {
        addAutoSubsequentServices: 'Automaticly start subsequent service',
        autoSubsequentServices: 'Select subsequent service',
        followUpMeasurementGgz: 'Send standard follow up measurement',
        isAutoCloseRequest: 'Automatic closing of finished processes',
        isDeviation: 'Only with deviation',
        isGraderSelector: 'Can select grader',
        isRequestCreator: 'Can start request',
        providerOrganizationId: 'Provider organization',
        providerPractitionerId: 'Provider practitioner',
        referralToSelf: 'Organization can only refer to itself',
        sendAfter: 'Send after',
        sendNotificationPractitionerWhenDeviant: 'Only send notification at abnormalities',
        sendZorgMailOnExaminationRequest: 'Send ZorgMail at consult request',
        sendZorgMailOnReferralIntake: 'Send ZorgMail on referral intake',
        sendZorgMailOnReferralRequest: 'Send ZorgMail with referral request',
        weeksForFollowUpMeasurementGgz: 'Number of weeks',
    },
    addCarePathSettingsLabelsServiceEmployment: {
        canPerform: 'Can perform {0}',
        canRead: 'Can read {0}',
        canReadAndWrite: 'Can read and write {0}',
        canStartProcess: 'Can start a process',
        isAutoCloseRequest: 'Auto close completed requests',
        isRecipient: 'Can be recipient',
        isRequester: 'Can be requester',
        read: 'read',
        sendNotificationPractitionerWhenDeviant: 'Only when examination is deviant',
        write: 'write',
    },
    addCarePathSettingsLabelsServiceProvider: {
        addOnePreferredServiceConsumerOrganization:
            'The practitioner has the ability to select a service provider. Therefore, you need to add at least one organization to the service consumer preference list. You can always change this later in the preference list settings.',
        canSelectCareProviderAsServiceConsumer: 'Can select general practitioner as recipient',
        isAnalyst: 'Can analyse',
        isBiometrician: 'Can examine',
        isGrader: 'Can grade',
        isGraderSelector: 'Can select grader',
        isRequestCreator: 'Can start examination without request',
        isRequestRecipient: 'Can receive request',
        isServiceConsumerSelector: 'Can select recipient/requester',
        noOnlyReaderRole:
            'You need at least one consumer, supervisor or provider role to be able to store the settings.',
        oneMustBeTrue: "At least one of the options 'Can examine' and 'Can grade' must be set to true.",
        oneMustBeTrueWithAnalyst:
            "At least one of the options 'Can analyse', 'Can examine' and 'Can grade' must be set to true.",
        spirometerSoftwareName: 'Spirometer software name',
        usingRestEcgMeter: 'Using rest ECG meter',
        usingSpirometer: 'Using spirometer',
    },
    addCarePathSettingsLabelsShared: {
        sendNotificationEmployment: 'Send notification to work email',
        sendNotificationPractitioner: 'Send notification to personal email',
        spirometerSoftwareName: 'Spirometer software',
        usingRestECGMeter: 'Using rest ECG meter',
        usingSpirometer: 'Using spirometer',
    },
    addRights: {
        consumer: 'Consumer {0}',
        provider: 'Provider {0}',
        supervisor: 'Supervisor {0}',
        whichRightsToAdd: 'Which rights do you want to add?',
    },
    addendum: {
        add: 'Add addendum',
        addendum: 'Addendum',
        authorAndTime: '{0} at {1} - {2} ({3})',
        edifactWarning: 'Please note: This is an adjustment to a previously submitted review of {0} at {1} by {2}.',
        edited: '{0} (edited)',
        formerly: 'Formerly',
        header: 'Add addendum',
        info: 'We will contact the GP about the correction and/or addition within one business day',
        placeholder: 'A correction or addition...',
        previousRequestAbortReason: 'Addendum added, new conclusion is normal',
        selectEdits: 'Select the items you want to edit',
        send: 'Send addendum',
    },
    address: {
        changeAddress: 'Change address',
    },
    adminProcesses: {
        allProcesses: 'All processes',
        exportInProgress: 'Export in progress',
        exportProcessStep: 'Export process step',
        exportType: {
            consultations: {
                consultationsAtSpecialist: 'Consultations at specialist',
                consultationsSecondRoundAtSpecialist: 'Consultations second round at specialist',
                groupName: 'Consultations (Cardio, Pulmo, Oog, Derma, GGZ)',
            },
            holterEcgExamination: {
                groupName: 'Holter ECG examination',
                holterAtAnalyst: 'Holter at analyst',
                holterAtGrader: 'Holter at grader',
                holterRead: 'Holter read',
            },
        },
        openProcesses: 'Open processes',
        processStep: 'Process step',
    },
    agenda: {
        agenda: 'Calendar',
    },
    allergies: {
        allergies: 'Allergies / contraindications',
        codeSystem: 'Code',
        comment: 'Comment',
        description: 'Description',
        displayName: 'Name',
        overview: 'Overview allergies and contraindications',
        statusCode: 'Status',
        valueCode: 'Value',
    },
    allergology: {
        advice: 'Advice',
        allResults: 'All results',
        allergistReferralQuestion: 'Vraag aan allergoloog',
        completeAssessment: 'Complete assessment',
        conclusion: 'Conclusion',
        contraIndications: {
            absoluteContraIndications:
                'Aanwezigheid van dermografie,Gebruik van anti-allergie tabletten (antihistaminica) in de laatste 72 uur voor de huidpriktest,Zwangerschap',
            absoluteContraIndicationsTitle: 'Absolute contra-indicaties',
            disclaimer:
                '*Het gebruik van een onderhoudsbehandeling prednisolon in lage dosering <7,5 mg per dag is wel toegestaan. Betablokkers hoeven eveneens niet te worden gestaakt voorafgaand aan de test.',
            relativeContradictions:
                'Ziekte of nog herstellend van (ernstige) ziekte,Exacerbatie astma (gebruik extra medicatie) of instabiel astma,Urticaria en angio-oedeem vooraf aan de huidpriktest,Eczeem op prikplaatsen (onderarm),Lokaal gebruik van topicaal corticosteroïd (hormoonzalf) op de onderarmen in de afgelopen week,Actuele prednisolon (stoot)kuur of recent afgeronde kuur (< 1 week geleden)*',
            relativeContradictionsTitle: 'Relatieve contra-indicaties',
            subtitle: 'Voor deze zorg is een huidpriktest vereist.',
            title: 'Contra-indicaties voor huidpriktest',
            toggleContraIndicationsClose: 'Relatieve contra-indicaties inklappen',
            toggleContraIndicationsOpen: 'Relatieve contra-indicaties',
        },
        downloadFormBiometry: 'Download skin prick test form',
        fillInSkinPrickTest: 'Fill in skin prick test',
        formBiomerty: 'Skin prick test form',
        gradeExamination: 'Grade examination',
        gradingCompleted: 'Placeholder: Medical specialist assessment',
        negativeResults: 'Negative results',
        positiveResults: 'Positive results',
        printBiometryForm: 'Print biometry form',
        questionFromGP: 'Question from GP',
        reportGrader: 'Report medical specialist',
        reportGraderAlert: 'The complete assessment includes non-pharmacological advice',
        serviceCard: {
            cost: 'Eventuele kosten',
            step1: 'Stap 1',
            step2: 'Stap 2',
            step3: 'Stap 3',
            subTitle: 'Ondertitel',
            title: 'Luchtwegallergie',
            waitingTime: 'Wachttijd',
        },
        waitingForBiometryResults:
            'You are leaving the patient record to fill in the values, it may take some time before the filled-in values appear here.',
        waitingForGradingResult:
            'You are leaving the patient record for grading, once you have sent the assessment, it will appear here',
    },
    allergologyPatientTaskType: {
        questionnaire: 'Questionnaire',
        questionnaireNotReceived: 'Questionnaire not received',
        remaining: 'Other',
        remainingOther: 'Other',
    },
    anamnesis: {
        anamnesis: 'Anamnesis',
        complaints: 'Complaints',
        familyHistory: 'Family history',
        history: 'History',
    },
    askEmploymentForSpecializationsModal: {
        description:
            'Enter your specializations, and the general practitioner and POH-GGZ will be able to send you consults that fit.',
        label: 'My specialization(s)',
        noSpecializations: 'I have no specialization',
        placeHolderMessage: 'Type and find your specialization…',
        thanksMessage: 'Thanks for filling in!',
        title: 'Receive more relevant consults?',
    },
    audiologyPatientTaskType: {
        referral: 'Referral',
        remaining: 'Other',
        remainingOther: 'Other',
    },
    autoSwitchOrganization: {
        warningModalBody:
            'You are now changing organizations. Make sure to save any outstanding work in other tabs before continuing.',
        warningModalTitle: 'Caution!',
    },
    availability: {
        endTime: 'End time',
        startTime: 'Start time',
    },
    bankAccountDetails: {
        cancel: 'Cancel',
        current: 'Current bank account details',
        dataIsProcessedInAFewDays: 'We usually process the request within a few business days.',
        employmentExplanation:
            'You are going to make a request to update your bank account details. It may take several business days for your request to be processed. You can see the status of your request in Payouts or Personal Settings.',
        employmentFollowStatusExplanation:
            "If we've updated your account information you'll see it on the Payouts page or in your Personal Settings. Follow the status of the change there.",
        enterBankAccountNamePlaceholder: "Enter the bank account's name...",
        enterBankAccountNumberPlaceholder: "Enter the bank account's number...",
        new: 'New bank account details',
        organizationExplanation:
            "You are going to make a request to have the organization's bank account details updated. It may take several business days for your request to be processed. You can see the status of your request in Payouts.",
        organizationFollowStatusExplanation:
            'If we have changed the account information you will see it on the Payments page. Follow the status of the change there',
        requestChange: 'Request bank account details change',
        submit: 'Submit',
        successModalTitle: 'We are going to work for you!',
    },
    bankAccountDetailsActionEnum: {
        request: 'Change requested',
        update: 'Updated in Exact',
    },
    bankAccountDetailsLog: {
        action: 'Action',
        bankAccountDetailsChangesLog: 'Bank account details changes logbook',
        details: 'Details',
        modificationDate: 'Modification Date',
        modifiedBy: 'Modified By',
        viewChangesLog: 'View changes log',
    },
    bariGroupSessionGroupMeeting: {
        allPatientsPresent: 'All patients have been to this group session',
        closeGroupSession: 'Close group session',
        closeGroupSessionSuccessMessage: 'The group session is closed',
        emptyWaitingList: 'There are currently no patients who need to be scheduled for a Group Session',
        processAttendance: 'Process attendance',
    },
    bariMdoGroupMeeting: {
        allMdoReportsSent: 'All MDO reports of this MDO have been sent.',
        closeMdo: 'Close MDO',
        closeMdoSuccessMessage: 'The MDO is closed',
        dietitianRemark: 'Dietitian screening',
        emptyWaitingList: 'There are currently no patients who need to be scheduled for an MDO',
        otherRemark: 'Other',
        psychologistRemark: 'Psychologist screening',
        regularMdoRemark: 'Overall assessment',
    },
    bariatricsPatientTaskType: {
        additionalGuidance: 'Additional guidance',
        appointmentDietitian: 'Appointment dietitian',
        appointmentDietitianPostOperation: 'Appointment dietitian post operation',
        appointmentMedicalScreening: 'Appointment medical screening',
        callBackRequest: 'Call back request',
        cancelDietPlanAppointment: 'Cancel dietitian post appointment',
        checkOrChangeData: 'Check or change data',
        general: 'General',
        groupSession: 'Group session',
        individualSession: 'Individual session',
        monitorGeneralPractitionerAccount: 'Monitor general practitioner account',
        onHold: 'On hold',
        planAppointment: 'Plan appointment',
        planning: 'Planning',
        remaining: 'Other',
        remainingOther: 'Other',
    },
    batchType: {
        declaration: 'Declaration',
        invoice: 'Invoice',
        patientInvoice: 'Patient invoice',
        payout: 'Paytout',
        singularPatientInvoice: 'Patient (singular)',
    },
    bittiumRecordingAnalystEnum: {
        dgtlHealth: 'DGTL Health',
        fysiologic: 'Fysiologic',
        ksyosAnalyst: 'Ksyos Analyst Organization',
        meddis: 'Meddis',
    },
    calendarComponent: {
        agenda: 'agenda',
        allDay: 'all day',
        date: 'Date',
        day: 'day',
        event: 'Event',
        month: 'month',
        moveBack: 'Go back',
        moveForward: 'Go forward',
        next: 'next',
        previous: 'back',
        selectDate: 'Select date',
        showMore: '+ {0} ',
        time: 'Time',
        today: 'today',
        tomorrow: 'tomorrow',
        week: 'week',
        yesterday: 'yesterday',
    },
    cancelFormDialog: {
        removeProcess: 'Are you sure you want to cancel {0}?',
        stopCreatingProcess: 'Are you sure you want to cancel the creation of {0}?',
        stopEditingProcess: 'Are you sure you want to stop editing {0}?',
    },
    cardioPerfectEthnicGroupEnum: {
        americanIndian: 'American-Indian',
        asian: 'Asian',
        black: 'Black',
        caucasian: 'Caucasian',
        hispanic: 'Hispanic',
        other: 'Other',
        unknown: 'Unknown',
    },
    cardiologyPatientTaskType: {
        inactivity: 'Inactivity',
        remaining: 'Other',
        remainingOther: 'Other',
    },
    careFusionEthnicGroupEnum: {
        afroCaribbean: 'Afro-Caribbean',
        asian: 'Asian',
        caucasian: 'Caucasian',
        latinAmerican: 'Latin-American',
        nonCaucasian: 'Non-caucasian',
        notSpecified: 'Not specified',
        other: 'Other',
        polynesian: 'Polinesian',
    },
    carePath: {
        addTxcsId: "What is the practitioner's TxCS ID?",
        backToOrganizationOverview: 'Back to organization overview',
        chosenCarePath: 'Chosen care path',
        chosenServices: 'Chosen service(s)',
        missingServiceOrganizationSettings: {
            organization:
                'The care path {0} has not been previously added at the organization level. Enter the settings for the organization here to add the care path to the provider.',
            serviceGroup:
                'Settings for the service {0} are still missing at the organization level. Please fill in these settings here to add the care path to the provider.',
            serviceGroups:
                'Settings for the services {0} are still missing at the organization level. Please fill in these settings here to add the care path to the provider.',
        },
        multipleChoicesPossible: 'Multiple choices possible',
        noEmailSelected: 'No email address has been selected',
        noServiceOrganizationSettings: 'There are no organisation settings for this service',
        organizationCanNowUse: '{0} can now use',
        overlapData: 'Data overlap with existing user. ',
        overlapDataMultipleUsers: 'Data overlap with multiple users. ',
        overlapDataSingleUser: 'Data overlap with existing user: {0}.',
        overlapSamePractitionerSameOrganization: 'This user has already been added for selected organization.',
        overlapSearchProfile: 'Add to organization by searching for profile.',
        practitionerCanNowUse: '{0} at {1} can now use',
        preferredServiceOrganizationConsumersRequiredMessage:
            'There are care providers configured to select requesters. Therefore it is required to add at least one organization to the preference list.',
        requesterPreferenceList: 'Requester preference list',
        returnToPersonalSettings: 'Back to personal settings',
        role: 'Role',
        roleWithin: 'Role in {0}',
        selectAtLeastOneProfileMessage: 'All rights are disabled. Select at least one right to save.',
        selectOrSearchProfession: 'Select a profession or type to search..',
        selectSpecialization: 'Select specialization',
        specializationWithin: 'Specialization within {0}',
        stop: 'Stop',
        stopEpisodeCategory: 'Stop all {0} services',
        stopServiceGroup: 'Stop {0}',
        stopServiceGroupText:
            'Will the practitioner stop {0}? You can stop the service here. The practitioner will not be able to work within this service.',
        turnOffRight: 'Turn right off',
        txcsId: 'TxCS ID',
        whichCarePathToAdd: 'Which care path would you like to add?',
        whichProfessionToAdd: 'Which profession has the care provider?',
        whichSubcategoryToAdd: 'Which service(s) would you like to add?',
    },
    carePathInformationRequest: {
        availableOn: "I'm available on (day/time)",
        interestedInCarePath: "Besides {0} I'm also interested in",
        receiveInformation: 'Receive information on',
        thanks: 'Thank you for requesting information about {0}',
        wantsToBeCalled: 'I would like to be called for more information',
        willReceiveEmail: 'You will receive an email from us in your inbox within several moments',
    },
    carePlan: {
        anamnese: 'Anamnesis',
        consult: 'Consult',
    },
    changeEmailAddresssForInvitationModal: {
        failed: 'One or more of the programs failed to send, please create a new counseling',
        pending: 'Within a couple of moments you can resend the request',
        success: 'You can send the request again',
    },
    checkGeneralPractitionerModal: {
        text: "Check if the practitioner's practice is correct and change if necessary.",
    },
    common: {
        Id: 'ID',
        NrOfResults: '{0} Results',
        PerPage: 'Per page',
        ResultRange: 'Result {0} - {1}',
        ResultRangeOfTotal: 'Result {0} - {1} of {2}',
        aberrant: 'Aberrant',
        accept: 'Confirm',
        actions: 'Action',
        activeSince: 'Active since',
        activeUntil: 'Active up to and including',
        activity: 'Activity',
        activityType: 'Activity type',
        actors: 'Actors',
        add: 'Add',
        addAddendum: 'Add addendum',
        addHere: 'Add this here.',
        addItem: 'Add {0}',
        addTo: 'Add',
        addendum: 'Addendum',
        admin: 'Admin',
        advanced: 'Advanced',
        advancedSettings: 'Advanced settings',
        advancedUpdated: 'Advanced (customized)',
        all: 'All',
        allItems: 'All {0}',
        amount: 'Amount',
        anamnesis: 'Anamnesis',
        and: 'and',
        andOr: 'and/or',
        archived: 'Archived',
        areYouSure: 'Please confirm your choice',
        around: 'Around {0}',
        assign: 'Assign',
        at: 'at',
        authorizations: 'Authorization(s)',
        automatically: 'Automatically...',
        back: 'Back',
        batchRun: 'Batch run',
        batchRunNumber: 'Run number',
        betweenDates: 'Between dates:',
        birthDate: 'Birthdate',
        both: 'Both',
        bsn: 'SSN',
        by: 'by',
        cancel: 'Cancel',
        cancelForm: 'Do you want to cancel?',
        cancelling: 'Cancel',
        capsLockWarning: 'Pay attention, your Caps Lock key is turned on',
        careGroup: 'Care group',
        carePaths: 'Carepaths',
        change: 'Change',
        changePassword: 'Change password',
        checkContactInfo: "Check if the patient's completed contact details are correct",
        chooseItem: 'Choose {0}',
        city: 'City',
        cityPlaceholder: 'Enter city...',
        clearFilters: 'Clear filters',
        clickAndChooseFromList: 'Click and choose from the list',
        clientSafe: 'ClientSafe',
        close: 'Close',
        closeShort: 'Close',
        closedAt: 'Closed before',
        closes: 'Close',
        code: 'Code',
        colleague: 'colleague',
        complaints: 'Complaints',
        completedOn: 'Completed on',
        conclusion: 'Conclusion',
        conditions: 'Conditions',
        configuredOn: 'Configured on',
        confirmRemove: 'Yes, remove',
        consumers: 'Show consumers',
        contactDetails: 'Contact details',
        contactPatient: 'Contact information patient',
        continue: 'Continue',
        controle: 'Controle',
        copy: 'Copy text',
        country: 'Country',
        countryPlaceholder: 'Enter country...',
        created: 'Created',
        creation: 'Creation',
        creationDate: 'Creation date',
        currency: 'USD',
        dashboard: 'Dashboard',
        data: 'Data',
        date: 'Date',
        date_ago: 'ago',
        date_currentMonth: 'This month',
        date_day: 'day',
        date_days: 'days',
        date_hour: 'hour',
        date_hours: 'hours',
        date_last: 'Last',
        date_minute: 'minute',
        date_minutes: 'minutes',
        date_month: 'month',
        date_months: 'months',
        date_seconds: 'seconds',
        date_today: 'Today',
        date_tomorrow: 'Tomorrow',
        date_year: 'year',
        date_years: 'years',
        date_yesterday: 'Yesterday',
        dateAdded: 'Date added',
        dateFormat: 'mm/dd/yyyy',
        datePlaceholder: 'dd-mm-yyyy',
        dateUpdated: 'Updated',
        declarationInsurer: 'Declaration at health insurer',
        decline: 'Reject',
        deleteAll: 'Delete all',
        deleteEndDate: 'Delete end date',
        deleteSearchTerms: 'Delete search terms',
        describeOrChooseFromList: 'Describe or choose from list',
        deselect: 'Deselect',
        details: 'Details',
        detailsItem: '{0} details',
        disabled: 'Disabled',
        download: 'Download',
        edit: 'Edit',
        editItem: 'Edit {0}',
        email: 'Email',
        emailAddress: 'Email address',
        emailForInvitation: 'Email address for the invitation',
        emergencyNumber: 'Emergency number',
        enabled: 'Enabled',
        endDate: 'End date',
        endedBy: 'Ended by',
        enterAnItem: 'please enter a {0}',
        enterValuePlaceholder: '{0}',
        episodeCategory: 'Care path',
        ethnicity: 'Ethnicity',
        example: 'Example',
        export: 'Export',
        exportData: 'Export data',
        exported: 'Exported',
        file: 'File',
        filename: 'File name',
        fillIn: 'Fill in',
        fillInLater: 'Fill in later',
        fillInUsername: 'Fill in username...',
        filter: 'Filter',
        filterByName: 'Filter by name',
        filterOn: 'Filter on',
        filterOnActivity: 'Filter on activity...',
        filterOnEpisodeCategory: 'Filter on care path',
        filterOnGeneralPractitioner: 'Filter on general practitioner...',
        filterOnGroup: 'Filter on group...',
        filterOnOrganization: 'Filter on organization...',
        filterOnPractitioner: 'Filter on practitioner...',
        filterOnService: 'Filter on service',
        filterOnServiceType: 'Filter on type',
        filterOnSingular: 'Filter on',
        financialData: 'Financial data',
        findAPatient: 'Find a patient',
        findItem: 'Find {0}',
        fold: 'Fold',
        for: 'for',
        from: 'from',
        fromCapitalized: 'From',
        functionalStatus: 'Functional status',
        funding: 'Funding',
        gender: 'Gender',
        general: 'General',
        generalError: 'Something went wrong.',
        goBack: 'Go back',
        goTo: 'Go to {0}',
        goToDefaultPage: 'Click here to go to default page',
        hasYetToBeConfigured: 'Has yet to be configured',
        healthInsurer: 'Health insurer',
        healthInsurers: 'Health insurers',
        helpRequest: 'Question',
        hide: 'Hide',
        home: 'Home',
        homeNumber: 'Home number',
        houseLetter: 'House letter',
        houseNo: 'House no.',
        houseNumber: 'House number',
        houseNumberAndSuffix: 'House number and suffix',
        houseNumberSuffix: 'House number suffix',
        ifKnown: 'If known',
        inactivePartner: 'Inactive partner',
        inactiveSince: 'Inactive since',
        inapplicable: 'n/a',
        invoice: 'Invoice',
        invoiceRuleCreated: 'Invoice rule(s) created',
        involvedPractitioner: 'Involved practitioner',
        isActive: 'Active',
        isGenerated: 'Automatically generated',
        isInactive: 'Inactive',
        isNotActive: 'Is not active',
        isSelected: 'Selected',
        kgoStatus: 'Cleaning status',
        label: 'Label',
        last: 'Last',
        lastLogin: 'Last login',
        lastLoginFrom: 'Last login from',
        lastModified: 'Last modified',
        length: 'Length',
        lessInformation: 'Less information',
        liesWith: 'Lies with',
        loading: 'Loading...',
        logging: 'Logging',
        logout: 'Logout',
        makeActive: 'Make active',
        managerAuthorizations: 'Roles & authorizations',
        medicalProtocol: 'Medical protocol',
        mobileNumber: 'Mobile number',
        mobileTelephoneNumber: 'Mobile telephone number',
        modify: 'Modify',
        monitoring: 'Monitoring',
        monthChoice: 'Month choice',
        more: 'More',
        moreInformation: 'More information',
        move: 'Move',
        myPatients: 'My patients',
        name: 'Name',
        nameFamily: 'Family name',
        nameGiven: 'Given names',
        nameInfix: 'Infix',
        nameInfix2: 'Infix',
        namePatient: 'Name patient',
        new: 'New',
        next: 'Next',
        nextStep: 'Next step',
        no: 'No',
        noEmailConfigured: 'No email has been configured at this time',
        noOptionsAvailable: 'No options available',
        noRemarks: 'No remark',
        noResult: 'No result',
        noSearchResults: 'There are no search results',
        noShows: 'No shows',
        none: 'None',
        normal: 'Normal',
        notAvailable: 'Not available',
        notExported: 'Not exported',
        notMigrated: 'Not migrated',
        notRelevant: 'Not relevant',
        notice: 'Notice',
        notifications: 'Notifications',
        now: 'Now',
        numberShort: 'Nr.',
        off: 'Off',
        officeNumber: 'Office number',
        old: 'Old',
        oldAdminPanel: 'Old admin panel',
        on: 'On',
        onDate: 'on',
        oneResult: 'result',
        optional: 'Optional',
        or: 'or',
        organizationType: 'Type of organisation',
        other: 'Other',
        overlapMessage: 'Overlapping configuration found',
        password: 'Password',
        payout: 'Payout',
        payoutsOnly: 'Payouts only',
        pdf: 'PDF',
        performSearch:
            'Search for your process or patient with the search filters above, then your search result will appear here!',
        period: 'Period',
        periodRequestClosed: 'Period in which the request has been closed',
        personal: 'Personal {0}',
        personalData: 'Personal data',
        personalSettings: 'Personal settings',
        phoneNumber: 'Telephone number',
        phoneNumbers: 'Phone number (home/mobile)',
        pid: 'PID',
        postMeasurement: 'post-measurement',
        previous: 'Previous',
        previousExports: 'Previous exports',
        previousStep: 'Previous step',
        price: 'Price',
        primary: 'Primary {0}',
        print: 'Print',
        proceed: 'Proceed',
        processId: 'ProcessID',
        processNumber: 'Process number',
        processType: 'Process type',
        processesBetween: 'Processes between',
        processesCreatedBetween: 'Processes created between',
        profession: 'Profession',
        profile: 'Profile',
        providers: 'Show providers',
        province: 'Province',
        questionnaire: 'Questionnaire',
        readAndWrite: 'Read and write',
        readOnly: 'Read only',
        recordingType: 'Recording type',
        referral: 'Referral',
        referralQuestion: 'Question',
        refresh: 'Refresh',
        register: 'Register',
        relationNumber: 'Relation number',
        remark: 'Remark',
        remarks: 'Explanation',
        remarksOrComments: 'An explanation or remarks...',
        remarksOrCommentsOptional: 'An explanation or remarks (optional)...',
        remarksOrQuestions: 'An explanation, remarks or questions...',
        remove: 'Remove',
        removeForm: 'Do you want to remove?',
        removeItem: 'Remove {0}',
        removePractitionerFirstUseTourSteps: 'Delete tour steps',
        removing: 'Remove',
        repeat: 'Repeat {0}',
        report: 'Report',
        request: 'Request',
        requested: 'Requested',
        requestedOn: 'Requested on',
        result: 'Result',
        results: 'results',
        retrieveTotal: 'Retrieve total',
        role: 'role',
        routeNotFound: 'Requested path not found.',
        save: 'Save',
        saveAndProceed: 'Save and proceed',
        saveButton: 'Save',
        saveDraft: 'Save as draft',
        saveItem: 'Save {0}',
        saved: 'Saved',
        savedData: 'Data has been successfully saved.',
        saving: 'Saving...',
        search: 'Search',
        searchByNameBsnBirthdate: 'Search by name, BSN or date of birth (DD-MM-YYYY)...',
        searchCity: 'Search by city...',
        searchFor: 'Search for',
        searchForSingular: 'Search for',
        searchIn: 'Search only in...',
        searchName: 'Name of the general practitioner or practice...',
        searchNameOrCity: 'Search by city or organization name',
        searchProcessId: 'Search processId',
        searchRefresh: 'Search/Refresh',
        searchResults: 'Search results',
        searchTerm: 'Search term',
        searchTermPlaceholder: 'Type your search term...',
        searchText: 'Search text',
        seeOverview: 'View full overview',
        select: 'Select',
        selectAll: 'Select all',
        selectConsumer: 'Select the service consumer',
        selectGp: 'Select the patients general practitioner',
        selectItem: 'Select {0}...',
        selectOne: 'Select one...',
        selected: 'Selected',
        send: 'Send',
        sendForm: 'Send form',
        sendTo: 'Send to',
        sensitiveData: 'Privacygevoelig',
        sentBeforeOrOn: 'Sent before or on',
        sentConsumer: 'Sent to service consumer',
        sentProvider: 'Sent to service provider',
        sentTo: 'Sent to',
        service: 'service',
        services: 'Show services',
        set: 'Set',
        settings: 'Settings',
        show: 'Show',
        showAll: 'Show all',
        showLess: 'show less',
        showMore: 'show more',
        showPersonalSettings: 'Show personal settings',
        smoker: 'Smoker',
        smokingDuration: 'Smoking duration',
        some: 'More or less',
        specialism: 'Specialism',
        specificItem: 'Specific {0}',
        specificStartAndEndDate: 'Specific start and end date',
        start: 'Start',
        startDate: 'Start date',
        startDateAndEndDate: 'Start and end date',
        startedAt: 'Started before or on',
        startedBy: 'Started by',
        startedOn: 'Started at',
        startingDate: 'Start date',
        state: 'State',
        statistic: 'Statistic',
        status: 'Status',
        stop: 'Stop',
        street: 'Street',
        streetAndHouseNumber: 'Street and house number',
        streetAndNumber: 'Street + number',
        suffix: 'Suffix',
        suffixAbbreviation: 'Suff.',
        summary: 'Summary',
        support: 'Support',
        system: 'System',
        task: 'Task',
        tasks: 'Tasks',
        technicalStatus: 'Technical status',
        telephone: 'Telephone',
        terminate: 'Terminate',
        thirdPartyMessages: 'Third party messages',
        tickTheRightImage: 'Tick the right image',
        time: 'time',
        timestamps: 'Timestamps',
        to: 'to',
        to2: 'to',
        toCapitalized: 'To',
        toTreat: 'To treat',
        treated: 'Treated',
        tryAgain: 'Try again',
        txcs: 'TxCS',
        type: 'Type',
        typeToSearch: 'Type to search...',
        uncertain: 'Uncertain',
        unchanged: 'Unchanged',
        unknown: 'Unknown',
        until: 'until',
        upToAndIncluding: 'up to and including',
        updated: 'Updated',
        uploadOrganizationStamp: 'Upload organization stamp',
        useExistingExamination: 'Use an earlier examination',
        useExistingExaminationOrConsult: 'Use an earlier examination/consult',
        user: 'User',
        userName: 'Username',
        userNumber: 'User number',
        vatRateGroup: 'Vat rate group',
        vektis: 'Vektis',
        view: 'View',
        viewLess: 'View less',
        viewMore: 'View more',
        viewProtocol: 'View protocol',
        website: 'Website',
        weight: 'Weight',
        willSendTo: 'Send to',
        wrong: 'Wrong',
        yearFormat: 'yyyy',
        yes: 'Yes',
        zipcode: 'Postcode',
        zipcodeAndCity: 'Postcode and city',
    },
    companies: {
        ksyosAllergology: 'Ksyos Allergology',
    },
    compensationHistoryHeaders: {
        compensationChangeDesc: 'Change',
        modificationDate: 'Date',
        modifiedBy: 'Modified by',
    },
    compensationPayoutLevels: {
        employment: 'employment',
        group: 'group',
        organization: 'organization',
        service: 'service',
    },
    compensationPayoutRecipientEnum: {
        organization: 'Organization',
        practitioner: 'Practitioner',
    },
    compensationPayouts: {
        addFees: 'Add fees',
        amount: 'Amount',
        current: 'Current',
        fees: 'Fees',
        ignored: 'Ignored',
        ignoredFeesPractitionerDueToProfessionLevelPayout:
            'The fees listed below will be ignored as of {0}. The fees must be set per organization and per profession.',
        organizationPayoutPerProfession:
            'As of {0}, the fees are determined per organization and per profession. Fees that are not set per profession will be ignored.',
    },
    consult: {
        answer: 'Answer',
    },
    contactsAndTasks: {
        availability: 'Availability',
        completeTask: 'Complete task',
        contacts: 'Contacts',
        createTask: 'Create task',
        deleteNote: 'Delete note',
        deleteNoteModalBody: 'This will delete the note. Are you sure?',
        deleteTaskModalText: 'Choose Yes if you want to delete this Task. Choose No if you want to keep it.',
        deleteTaskModalTitle: 'Are you sure you want to delete the task?',
        description: 'Description',
        editNote: 'Edit note',
        editTask: 'Edit',
        emptyPatientTaskListSubtitle: 'Start a care path for a patient',
        emptyPatientTaskListTitle: 'You have no open tasks at the moment',
        expectedDateCompleted: 'Target date',
        newNote: 'New note',
        newTask: 'New task',
        noClosedTasksForThisPatient: 'There are no closed tasks for this patient',
        noContactsForThisPatient: 'There are no contacts for this patient',
        noOpenTasksForThisPatient: 'There are no open tasks for this patient',
        noResultsPatientTaskListSubtitle: 'Check your search term and/or adjust the filter',
        noResultsPatientTaskListTitle: "We couldn't find any results",
        note: 'Note',
        notes: 'Notes',
        openTasks: 'Open tasks',
        patientTaskList: {
            birthDate: 'Birthdate',
            description: 'Type of task',
            patientName: 'Patient',
            performerPractitioner: 'Performer',
            sortDate: 'Last change',
        },
        performer: 'Performer',
        previouslyCompleted: 'Previously completed',
        registerContact: 'Register contact',
        saveTask: 'Save',
        task: 'Task',
    },
    contractSettingLogs: {
        linkToLogs: 'View log changes',
        modalTitle: 'Log changes in price agreements',
        modificationDate: 'Modification date',
        modificationType: 'Type',
        modifiedBy: 'Modified by',
        modifiedFields: {
            contractPrice: 'Price agreement',
            endDate: 'End date',
            startDate: 'Start date',
        },
        newValue: 'Modification',
        serviceType: 'Service',
    },
    contractSettings: {
        addFundingRecord: 'Add funding',
        emptyState: 'This organization does not yet have a subscription to Ksyos',
        endDateIsLastDayOfMonth: 'End date is always on the last day of the month',
        priceAgreement: 'Price Agreement',
        priceAgreements: 'Price Agreements',
        startDateIsFirstDayOfMonth: 'Start date is always on the first day of the month',
        subscription: 'Subscription',
        totalPerPatientPerQuarter: 'Total per patient by quarter',
    },
    costInfoAndConsentModal: {
        costAndPaymentStep: {
            alert: 'The patient should be informed about the costs and consent to pay these costs to continue',
            checkbox: 'The patient consents to the start of this service and to the costs that will follow',
            information1a: 'A(n) ',
            information1b: ' cost ',
            information1c: '(incl. {0}% vat)',
            information2a: 'The patient receives an ',
            information2b: 'invoice via via email or regular mail',
            informationForeigner3a: 'The patient ',
            informationForeigner3b: 'pay themself ',
            informationForeigner3c: 'and ',
            informationForeigner3d: 'declares ',
            informationForeigner3e: 'to the insurance company',
            informationPrisoner2a: 'The ',
            informationPrisoner2b: 'institution ',
            informationPrisoner2c: 'of the patient receives the ',
            informationPrisoner2d: 'invoice',
            informationPrisoner3a: 'The institution submits the costs at the ',
            informationPrisoner3b: 'JenV',
            informationUninsured3a: 'The patient ',
            informationUninsured3b: 'has to pay ',
            informationUninsured3c: 'themself ',
            informationUninsured3d: 'for this service',
            noPatientAgreement: 'Patient agreement not yet filled in',
            patientAgreement: 'Patient agreement',
        },
        costInfo: 'Cost and payment',
        headerStep0: 'Insurance check',
        headerStep1: 'Invoice',
        insuranceCheck: 'Insurance check',
        invoice: 'Invoice',
        patientInvoicePreferenceStep: {
            alert: 'To pay online the patient logs in with his postcode and house number (without extension)',
            askInvoiceAddress: 'We can only send de invoice by mail to an address inside the Netherlands',
            noEmail: 'If the patient has no email address',
            payViaIdeal1: 'Easy',
            payViaIdeal2: 'Payment via iDEAL',
            paymentMethodIdeal: 'iDEAL',
            paymentMethodInvoice: 'Mail',
            receiveVia: 'Receive via',
        },
    },
    dataChecks: {
        checkComment: 'Check comment',
        checkDate: 'Check date',
        checkDetails: 'Check details',
        checkType: 'Check type',
        checks: 'Checks',
        duplicateOrganizations: 'Duplicate organizations',
        filterCheckType: 'Filter check type',
        id: 'ID',
        lastLogin: 'Last login',
        lastLoginTooltip:
            'Last login of a practitioner that falls under this organization. Note: The practitioner may not have logged in specifically to this organization',
        name: 'Naam',
        organizationCheckOverview: 'Organization check overview',
        organizationType: 'Type organization',
        practitionerCheckOverview: 'Practitioner check overview',
        typeTooltip: 'Hover over the types for more information',
    },
    daysEnum: {
        Friday: 'Friday',
        Monday: 'Monday',
        Saturday: 'Saturday',
        Sunday: 'Sunday',
        Thursday: 'Thursday',
        Tuesday: 'Tuesday',
        Wednesday: 'Wednesday',
    },
    decisionSupportHip: {
        text: 'Meets conditions?',
    },
    declarationMethodEnum: {
        generalContractedSettings: 'Declare with general settings (contracted care)',
        generalUncontractedSettings: 'Declare with general settings (uncontracted care)',
        insurerSettings: 'Declare with specific insurer settings',
        none: 'Do not declare',
    },
    dermaApp: {
        deviceId: 'Device ID',
        expirationDate: 'Expiration date',
        logins: 'Derma app logins',
        registrations: 'Derma app registrations',
    },
    dermaModal: {
        checkingForPhotos: 'We are checking for photos...',
        connectedPhotos: 'Photos added to this consult',
        instructions:
            "Open the Dermatology app on the mobile device and add the photos to the consult. When you've finished uploading press Continue.",
        title: 'Upload the photos in the app before you continue',
        uploadedPhotos: 'Uploaded photos',
    },
    desktopProcess: {
        action: 'Action',
        desktopProcesses: 'Desktop processes',
        showContent: 'Show content',
        showDesktopProcessContent: 'Show desktop process content',
        status: 'Status',
    },
    desktopProcessAccessReason: {
        reasonForAccess: 'Reason to view desktop process content',
    },
    deviceAccessories: {
        accessories: 'Accessories',
        accessory: 'Accessory',
        add: 'New accessory',
        details: 'Accessory details',
        edit: 'Edit accessory',
        organizationOrGroup: 'Organization / Group',
        referenceNumber: 'Batch number',
        removeFromGroup: 'Remove from group',
        removeFromOrganization: 'Remove from organization',
        searchPlaceholder: 'Search by organisation (name/ID), group or batch number...',
        serialNumber: 'Serial number',
        type: 'Accessory type',
        types: 'Accessory types',
    },
    deviceAccessoryStatusEnum: {
        inStock: 'In stock',
        sent: 'Sent',
    },
    deviceAccessoryTypes: {
        add: 'Add accessory type',
        details: 'Accessory type details',
        deviceAccessoryTypes: 'Accessory types',
        edit: 'Edit accessory type',
        new: 'New accessory type',
        sureToDelete: 'Are you sure to delete this accessory type?',
    },
    deviceStatusEnum: {
        broken: 'Broken',
        homeExamination: 'Home examination',
        inStock: 'In stock',
        internalUse: 'Internal use',
        issued: 'Issued',
        lost: 'Lost',
        returnToProvider: 'Return to provider',
        sentForRepair: 'Return to repair',
        withPatient: 'With patient',
        writtenOff: 'Written off',
    },
    deviceTypes: {
        add: 'Add device type',
        details: 'Device type details',
        deviceTypes: 'Device types',
        edit: 'Edit device type',
        isFourteenDaysRecordingSupported: 'Supports 14 days recordings',
        manufacturer: 'Manufacturer',
        model: 'Model',
        new: 'New device type',
        sureToDelete: 'Are you sure to delete this device type?',
        version: 'Version',
    },
    devices: {
        add: 'Add device',
        addDeviceAccessorySuccessMessage: 'The accessory has been added.',
        addDeviceModalTitle: 'Add device',
        addDeviceSuccessMessage: 'The device has been added',
        addOrganizationTooltip: 'Select multiple organizations if the device is shared.',
        assistant: 'Assistant',
        details: 'Device details',
        device: 'Device',
        deviceTypePlaceholder: 'Type and choose the device type...',
        devices: 'Devices',
        edit: 'Edit device',
        filterOnEpisodeCategory: 'Filter on E. Category',
        forRepair: 'Sent for repair',
        intakeDate: 'Intake date',
        issueDate: 'Sent date',
        lastUsed: 'Last used',
        logbook: 'View logbook',
        logbookModal: {
            date: 'Date of change',
            modifiedBy: 'Modified by',
            referenceType: 'Organisation/PID/Medewerker/Recipient',
            status: 'Status',
            title: 'Device changes logbook',
        },
        nameOrLocation: 'Name/location',
        new: 'New device',
        newStatus: 'New status',
        noDataAvailable: 'No data available',
        notes: 'Notes',
        numberOfRecordings: 'Number of recordings',
        organizationOrGroup: 'Organization / Group',
        organizationPlaceholder: 'Type and find the organization...',
        partner: 'Partner',
        processId: 'PID',
        registered: 'Registered',
        removeDeviceFromOrganization: 'Disconnect from organization',
        searchPlaceholder: 'Search by organisation (name/ID), group or serial number...',
        serialNumber: 'Serial number',
        serialNumberPlaceholder: 'Serial number...',
        shipmentDate: 'Shipment date',
        sureToDelete: 'Are you sure to delete this organization from this device?',
        type: 'Device type',
        usage: 'Usage',
    },
    diagnosis: {
        deviant: 'Deviant',
        diagnosis: 'Diagnosis',
        diagnosisRemarks: 'Remarks for diagnosis',
        differentialDiagnosis: 'Differential diagnosis',
        noAbnormality: 'No abnormality',
        noDiagnosisYet: 'No diagnosis yet',
        probabilityDiagnosis: 'Probability diagnosis',
        selectOrTypeATerm: 'Select or type a term to find a code...',
        specifyDiagnosis: 'Specify your diagnosis...',
        unableToDiagnose: 'Unable to diagnose',
        workingDiagnosis: 'Working diagnosis',
    },
    diagnosticReport: {
        accept: 'Accept report',
        addAttachment: 'Add attachment',
        addClarification: 'React',
        additionalQuestion: 'Optional additional question about report',
        additionalRemarks: 'Additional remarks',
        advice: 'Advice',
        advisedPolicy: 'Advised policy',
        assessmentBiometrics: 'Assessment biometrics',
        assignColleague: 'This consultation will be assigned to your colleague:',
        assignConsultation: 'Assign consultation',
        attachment: 'Attachment',
        cancelDiagnosticReport: 'Cancel',
        category_consultReport: 'Consult report',
        category_contact: 'Contact',
        category_discharge: 'Discharge',
        category_examinationReport: 'Examination Results',
        category_intake: 'Intake report',
        categoryDraft_consultReport: 'Consult',
        categoryDraft_discharge: 'Discharge',
        categoryDraft_examinationReport: 'Examination',
        categoryDraft_intake: 'Intake',
        clarification: 'Clarification',
        clarification_tooltip: 'Ask clarification about the consult results',
        clarificationReaction: 'Answer',
        clarificationReaction_tooltip: 'Give answer on the clarification question',
        clarificationSentMessage: 'Thanks, your question has been sent',
        confirmCancelReport: 'Are you sure you want to cancel this report?',
        dateConsult: 'Date of consultation with the patient',
        diagnosticReport: 'Diagnostic report',
        dischargeLetter: 'Discharge letter',
        dischargeReport: 'Discharge',
        examination: 'Onderzoek',
        findings: 'Results',
        findingsFrontEyeSegment: 'Front eye segment',
        findingsPlaceholder: 'Describe your findings ...',
        findingsRearEyeSegment: 'Rear eye segment',
        generalRemark: 'General additions to report',
        hideClarification: 'Hide the clarifications',
        hideConsultReport: 'Hide the consult report',
        hideContact: 'Hide the contact',
        hideDischargeReport: 'Hide the discharge',
        hideExaminationReport: 'Hide the report',
        hideIntakeReport: 'Hide intake report',
        intakeHeader: 'Intake',
        intakePlaceholder: 'Type your intake report...',
        intakeReport: 'Intake report',
        noConsumerOfCoupledOphthalmologyConsult:
            'You are not authorized to request consultations, therefore the examination cannot be sent. Please contact the Ksyos service desk via 020 600 0060.',
        noQuestion: 'There are no clarifications',
        optometricDiagnosis: 'Optometrische diagnose',
        orChoosePolicyFromList: 'Or choose policy from list',
        otherRelevantInformation: 'Other relevant information',
        patientEmailAddress: 'Email address of the patient for sending the invitation...',
        policy: 'Policy',
        policyDescription: 'Policy description',
        policyPlaceholder: 'Policy...',
        poligraphyExam: 'Poligraphy exam?',
        qualityPhotosSufficient: 'Quality photos sufficient',
        relationEcgAndComplaints: 'Relation ECG and complaints',
        remarks: 'Remarks',
        remarksGrader: "Grader's remarks",
        report: '+ Report',
        resend: 'Send again',
        saveAndClose: 'Save and close request',
        saveAndSend: 'Save and send',
        saveClarification: 'Send reaction',
        saveClarificationReaction: 'Send answer',
        saveDraft: 'Save as draft',
        selectProgramme: 'Select the programmes you want to send.',
        selectTherapyLandProgramme: 'Select therapy land programmes',
        sendToHis: 'Close',
        showClarification: 'Show all clarifications',
        showConsultReport: 'Show the complete consult report',
        showContact: 'Show the complete contact',
        showDischargeReport: 'Show the discharge',
        showExaminationReport: 'Show the complete report',
        showIntakeReport: 'Show intake report',
        status_closed: 'Closed',
        status_draft: 'Draft',
        status_open_perfomer: 'Sent',
        status_open_perfomer_answer: 'Answer sent',
        status_open_perfomer_question: 'Open question',
        status_open_recipient: 'Received',
        status_open_requester: 'Sent',
        status_open_requester_answer: 'Answer available',
        status_open_requester_question: 'Question sent',
        status_rejected: 'Rejected',
        therapyLandProgramme: 'Therapy land programmes',
        typeConsult: 'Type consult',
        urgency: 'Urgency',
        videoconsult: 'Videoconsult with the patient',
        visitToSpecialistNecessary: 'Visit to specialist necessary',
        visitToSpecialistNecessaryNow: 'Need a specialist visit now?',
        workingDiagnosis: 'Working diagnosis',
    },
    dropzone: {
        addMore: 'Add more files',
        addReferralLetter: 'Add a referral letter',
        array_max: 'Add no more than {0} files',
        array_max_1: 'Add no more than 1 file',
        array_min: 'Add at least {0} files',
        array_min_1: 'Add at least 1 file',
        canOnlyUploadTheseFiles: 'You can only upload the following file types: {0}.',
        dragAndDropUploader: 'Drag and Drop Uploader',
        dragFiles: 'Drag your files here...',
        dropFiles: 'Drop files',
        fileTooBig: 'The file size is too big',
        fileTooSmall: 'The file size is too small',
        maxFilesExceeded: 'You can add at most {0} files',
        serverNotConfigured: 'Server not Configured',
        toUpload: 'to upload',
        uploadError: 'Upload failed',
    },
    embloomGroupCertificateEnum: {
        ksyos: 'Ksyos certificate',
        mediis: 'Mediis certificate',
    },
    embloomQuestionnaires: {
        abbreviation: 'Abbreviation',
        addEmbloomQuestionnaires: 'Add Embloom questionnaire',
        addNewQuestionnaire: 'Add new questionnaire',
        addQuestionnaire: 'Add Questionnaire',
        autoGrading: 'Auto-Grading Embloom',
        certificate: 'Embloom Certificate',
        checkEmail: 'Check with the patient whether the registered email-address is correct',
        chooseDecisionSupport: 'Select decision supports',
        chooseOtherQuestionnaire: 'Select other questionnaires',
        chooseQuestionnaire: 'Choose a Embloom questionnaire',
        contactServiceDesk: 'Contact the servicedesk at 020 600 00 60 for us to assist you.',
        decisionSupportQuestionnaires: 'Decision support questionnaires',
        description: 'Description',
        hasCertificate:
            'A Embloom certificate has been added. Upload a new one below to replace the existing certificate',
        isActive: 'Active',
        isDecisionSupport: 'Decision support',
        isEmbloomGrading: 'Grading by Embloom',
        isStandard: 'Base set',
        label: 'Test name',
        loadingReport: 'Fetching examination report..',
        loadingReportFailed: 'The examination report could not be loaded.',
        loadingReportTakesLonger: "It's taking a little longer than usual.",
        manage: 'Manage questionnaires',
        name: 'Embloom questionnaires',
        nameDescription: 'Name - description',
        noCertificate: 'The selected group has no Embloom certificate',
        noCertificateShort: 'Embloom certificate missing',
        noGroup: 'When no group is selected the user can not use Embloom',
        otherQuestionnaires: 'Other questionnaires',
        patientEmail: 'Email address',
        patientPermission: 'Patient gives permission for the examination',
        pickFromList: 'Or pick from the list',
        refresh: 'Refresh list',
        reload: 'Reload available tests from Embloom',
        reportOnItsWay: "The report is on it's way!",
        saveQuestionnaires: 'Add questionaires',
        searchByName: 'Search by name...',
        selectedQuestionnaire: 'Selected questionnaire',
        sendPostMeasurement: 'Send post-measurement',
        setBasicSet: 'Set base set',
        setToDefaultSet: 'Set to default set',
        standardQuestionnaires: 'Choose the standard collection of Embloom questionnaires',
        testId: 'Test id',
        weeks: 'weeks',
    },
    employment: {
        activeFrom: 'Active from',
        backToProfile: 'Back to profile',
        bankAccountNumber: 'Bank account number',
        bankAccountStatus: 'Bank account status',
        canAfter: 'can after',
        chooseProfession: 'Choose profession',
        dontHaveActiveActivities: 'The care provider has no active processes',
        emailAddress: 'Employment email address',
        emailAddressPayouts: 'Email address for payouts',
        emergencyNumber: 'Emergency number',
        employment: 'Employment',
        employmentAdded: '{0} can work at {1}',
        employmentRole: {
            activeUntil: 'Active up to and including',
        },
        employments: 'Employments',
        end: 'Terminate employment',
        endAll: 'Terminate all employment',
        endAllEmploymentsModalBody:
            'Are you sure you want to terminate all relations of this healthcare provider? The healthcare provider can then no longer log in or be selected as an applicant or referrer.',
        endEmploymentModalBody: 'Are you sure you want to terminate the employment of this healthcare provider?',
        financialEmailAddress: 'Email address for payouts',
        groupManager: 'Group Administrator',
        mayHaveActiveActivities: 'Please note that the healthcare provider may still have active processes',
        mayHaveActiveActivitiesForCareProvider:
            'Please note, there may be ongoing processes in the name of this healthcare provider',
        noEmployments:
            'There are no employments for this practitioner. Before you can add services and care plans you need first to add an employment',
        notWork: 'no longer work at',
        organizationManager: 'Organization manager',
        organizationManagerExplanation:
            'This person can view disbursements to the organisation and request a bank account change.',
        overlapMessage: 'Overlapping configuration found',
        paymentDetails: 'Payment Details',
        profession: 'Profession',
        relationNumber: 'Reference number',
        save: 'Save employment',
        specialism: 'Specialism',
        subscriber: 'Account holder',
        work: 'in Ksyos',
        workEmail: 'Work email',
        zorgmail: 'ZorgMail',
        zorgmailNummer: 'ZorgMail number',
    },
    employmentTaxesDetails: {
        alert: {
            collapsedTextFirstPart:
                'Ksyos recognizes and respects the privacy rights of individuals whose personal data are processed, and undertakes to take appropriate measures. One of the measures is that this additional personal data can only be accessed by Ksyos employees who are required to pass it on to the Tax Office. In addition, this personal data is securely shared with the Tax Office and with no other agencies and/or individuals. Ksyos retains the data according to the statutory period and it is then destroyed.',
            collapsedTextSecondPart:
                'For questions about the Ksyos privacy policy, please contact the Ksyos Data Protection Officer at ',
            completeDetailsButton: 'Complete your details',
            descriptionLinkText: 'payments made to third parties',
            descriptionTextFirstPart: 'To comply with the law',
            descriptionTextSecondPart:
                'we are required to provide personal information about you to the Tax Authorities.',
            title: 'Alert! Important data needed for your payment is still missing.',
        },
        emptyState: 'You have not yet filled in any additional financial details.',
        export: {
            addres: 'Address',
            dateOfDisbursement: 'Date of disbursement',
            initials: 'Initials',
            nameInfix: 'Name infix',
        },
        isNaturalPersonLabel: 'I work as a natural person for Ksyos',
        modal: {
            infoLinkText: 'personal settings',
            infoText: 'You can change this information in your ',
            title: 'Your personal data',
        },
        multipleOrganizationSuccessMessage: 'The data has been saved for all organizations',
        naturalPersonQuestion:
            'Do you work as a natural person for Ksyos? This means that you are paid directly by Ksyos. This also includes self-employed persons, sole proprietorships, partnerships or VOF.',
        personalDataInfoFirstPart: 'To comply with the law ',
        personalDataInfoLink: 'paid amounts to third parties',
        personalDataInfoSecondPart: ' we are obliged to provide personal data about you to the Tax Office.',
        singleOrganizationSuccessMessage: 'Thank you! Your financial data at {0} is now complete.',
        title: 'Additional financial data',
    },
    episodeCategory: {
        activeSettings: 'Active settings',
        addEpisodeCategory: 'Add care path',
        addServiceForEpisodeCategory: 'Add {0} service',
        applicants: 'applicants',
        assignRoles: 'Assign roles',
        chooseEpisodeCategory: 'Choose a care path',
        chooseService: 'Choose a service',
        confirmCancelMessage: 'When you close all changes will be lost.',
        confirmCancelTitle: 'Are you sure you want to cancel adding a care path?',
        episodeCategories: 'Care paths',
        episodeCategory: 'Care path',
        episodeCategoryHeader: 'Start a Care path for this patient',
        executors: 'executors',
        foldInactiveCarePath: 'Fold details inactive care paths',
        foldInactiveEpisodeCategory: 'Fold inactive care paths',
        ggzContactInformation: 'Mental Health telephone availability',
        hideInactiveServiceGroups: 'Hide inactive service groups',
        inactiveEpisodeCategories: 'Inactive care paths',
        inactiveSettings: 'Inactive settings',
        noAdvancedSettings: 'There are no advanced settings.',
        organizationSettings: 'Organization settings',
        showInactiveCarePath: 'Show details inactive care paths',
        showInactiveEpisodeCategory: 'Show inactive care paths',
        showInactiveServiceGroup: 'Show 1 inactive service group',
        showInactiveServiceGroups: 'Show {0} inactive service groups',
    },
    episodeCategoryEnum: {
        allergology: 'Allergology',
        allergy: 'Allergy',
        audiology: 'Audiology',
        bariatrics: 'Obesity',
        cardiology: 'Cardiology',
        corona: 'Corona',
        dermatology: 'Dermatology',
        endocrinology: 'Endocrinology',
        general: 'General',
        homeconsultation: 'Home Consultation',
        labAanvraag: 'Lab request',
        mentalHealth: 'Mental Health',
        nephrology: 'Nephrology',
        neurology: 'Neurology',
        ophthalmology: 'Ophthalmology',
        orthopedics: 'Orthopedics',
        pilot: 'Pilot',
        pulmonology: 'Pulmonology',
        radiology: 'Radiology',
        somnology: 'Sleep',
        specialistConsultation: 'Specialist Consultation',
    },
    episodes: {
        adminPanel: 'Administration panel',
        codeCode: 'Code',
        codeSystem: 'System',
        codeSystemName: 'System Name',
        comment: 'Comment',
        description: 'Description',
        displayName: 'Name',
        emptyEpisode: 'This patient has no {0} episodes',
        episodes: 'Episodes',
        icpc: 'ICPC',
        overview: 'Overview episodes',
        statusCode: 'Status Code',
        valueDisplayName: 'Display Name',
    },
    errorCodes: {
        AUTOSUBSEQUENT_MISSING_PROVIDER: 'Missing recipient providers with auto request',
        BRIGHTPLAN_ADD_ACTION_FAILED: 'Something went wrong. Try again or report to Tech.',
        BRIGHTPLAN_AUTHENTICATION_FAILED: 'There is something wrong. Try again or report to Tech.',
        BRIGHTPLAN_GET_LOCATIONS_FAILED: 'Something went wrong getting the locations. Try again or report to Tech.',
        BRIGHTPLAN_GET_OPERATION_TYPES_FAILED:
            'Something went wrong getting the operation types. Try again or report to Tech.',
        BRIGHTPLAN_GET_PROFESSIONALS_FAILED:
            'Something went wrong getting the professionals. Try again or report to Tech.',
        BRIGHTPLAN_USERNAME_NOT_FOUND: "You don't have a BrightPlan account. Ask a collegue to create one for you.",
        CAN_NOT_REMOVE_OWN_ORGANIZATION: 'It is not allowed to remove your own organization',
        DATE_OCCUPIED: 'Timeframe is already occupied',
        DUPLICATE_RECORD: 'The record already exists',
        ERROR: 'Something went wrong',
        EXACT_BAD_CREDENTIALS:
            "Looks like we don't have the right data to connect to Exact. Please report a bug and one of our developers will look into how to solve it.",
        EXACT_CONNECTION_FAILURE:
            'Looks like something whent wrong while connecting to Exact. Refresh the page to make sure you have a valid Exact session. Still not successful? Please report a bug and one of our developers will look into how to solve it.',
        FORBIDDEN: 'Action not allowed',
        GROUP_ALREADY_ASSIGNED: 'Group has already been assigned',
        HTTP_403: 'You do not have sufficient permissions to access the item you requested.',
        IMAGE_MAX_FILESIZE_EXCEEDED:
            "Unfortunately it's not possible to upload an image with a file size over {0}. Select a smaller image.",
        IMPORT_WRONG_FILE_TYPE: 'Import failed, wrong file format',
        INSURER_SERVICE_HAS_LINKED_DECLARATION_ACTIVITY_PERIODS:
            'The insurance service cannot be removed because one or more declaration activity periods are linked to it.',
        INSURER_SERVICE_HAS_LINKED_PRICES:
            "The insurers ' service cannot be removed because it is linked to one or more price periods.",
        KSYOS_ROLE_REQUIRED: 'A Ksyos practitioner should have a Ksyos manager role',
        KSYOS_ROLE_REQUIRED_NO_RIGHTS:
            'A Ksyos practitioner should have a Ksyos manager role, but you do not have permissions to assign manager roles.',
        NO_REQUEST_RECIPIENT: 'There should be a request recipient',
        NOT_CONSUMER_SUBSEQUENT_SERVICE: 'No consumer of the subsequent service',
        OVERLAPPING_CONFIG: 'Overlapping configuration found',
        STILL_ATTACHED_TO_SERVICEORGANIZATIONSETTINGS:
            'Cannot delete it, still attached to service provider organizations',
        STILL_PROVIDERS_ATTACHED: 'Cannot delete it, still providers attached',
        TOO_MANY_ATTACHMENTS:
            'You have uploaded too many attachments. Please do not exceed the limit of {0} attachments',
        USER_ALREADY_REGISTERED: 'Your account is already registered',
        USER_HAS_NO_EMPLOYMENT: 'You can not yet do this, an employment was not yet created for you',
        USER_IS_NOT_ACTIVATED: 'You can not sign in. Your account is not activated',
        USER_NOT_REGISTERED: 'You can not yet sign in. You are not registered yet',
        USERNAME_EXISTS: 'User name already exists. Choose an other user name',
    },
    errorPages: {
        generalBody:
            'It seems that there is a problem with our system. We have immediately reported this problem to the webmaster so that we can resolve it as quickly as possible.',
        generalNeedHelp: 'Need help? Call',
        generalOpeningHours: 'Monday - Friday from 8:30 - 17:00 hours',
        generalSendEmail: 'Or send an email to info@ksyos.nl',
        generalTitle: 'Something went wrong',
        goToHome: 'Go to home',
        goToPatients: 'Find a patient',
        missingAuthorizationBody: "You don't have access to this page (anymore)",
        notFoundBody: 'We apologize for the inconvenience',
        notFoundTitle: 'De page you are looking for does not exist',
        organizationId: 'Organization ID',
        rumSessionId: 'Session ID',
        unauthorizedAction: 'You do not have permission to perform this action.',
        userId: 'User ID',
    },
    errorResponses: {
        authorization: "You don't have access to this page or file (anymore).",
        controller: 'An error happened. (Technical status: {0})',
        csrfTokenMissing: 'Missing security token.',
        csrfTokenNoMatch: 'The security token is incorrect.',
        internal: 'An internal server error happened.',
        joiValidation: 'There is a fault in the request. (Technical details: type "{0}", path "{1}")',
        noSession: 'Your session ended or you were not logged in. Please go to <a href="/home?view=login">login</a>.',
        notAllowed: "You don't have access to this page or file (anymore).",
        notFound: 'This page (or this file) does not exist.',
        unexpected: 'An unexpected error happened.',
        validation: 'There is a fault in the request. (Technical code: {0})',
    },
    evaluation: {
        afterwardsReferPatient: 'Will you still refer this patient to secondary care?',
        otherwiseReferPatient: 'Would you have referred this patient to secondary care without Ksyos?',
        otherwiseReferPatientTitle: 'Your question has been sent.',
        question: 'Could we ask you a couple questions?',
        reportHelpful: "Were the consultant's answers of use to you or your patient?",
        reportInstructive: "Have you learned from the consultant's answers?",
    },
    eventEcgCardioExaminationDurationEnum: {
        fourteenDays: '14 days',
        sevenDays: '7 days',
    },
    exact: {
        allBatchesComplete: 'All batches have been successfully completed',
        complete: 'Complete',
        credentialsMissing:
            'The login details for Exact are missing. Please report a bug and one of our developers will look into how to fix it.',
        exportDeclared: 'Soon we will send the data to Exact.',
        exportFailedGeneric:
            'Exporting the data to Exact was unsuccessful. Please try again later. Still not successful? Please report a bug and one of our developers will look into how to solve it.',
        exportFailedPermissions:
            'The export of the data to Exact did not succeed because you do not have permissions to do so in Exact. Please contact the administrator of Exact to change your permissions.',
        exportInProgress: 'Data transfer to Exact in progress. {0}% completed.',
        exportSuccess: 'Data export to Exact was completed successfully \\ (•◡•) /',
        integrationWorking: 'The integration with Exact is properly working.',
        logInToExport: 'Log in to Exact to export the payouts.',
        logout: 'Logout from Exact',
        onlyTrialExportAllowed: 'Only trial exports can be done without logging in.',
        rerun: 'Rerun',
        rerunning: 'Rerunning...',
        someBatchesUncomplete: 'Some batches are still uncomplete',
        uncomplete: 'Uncomplete',
        warning: 'Warning',
    },
    exchangePatientData: {
        aboveServices: 'for specialisms above',
        accessMedicalHistory: 'Access medical history',
        consentDeclarationTitle: 'Consent declaration patient',
        consentDeclarationTitleWarning: 'Consent declaration patient has not been entered',
        consentReadLess: 'Read less about this consent declaration',
        consentReadMore: 'Read more about this consent declaration',
        consentRequiredModalTitle: 'Review medical dossier patient consent',
        hasProcessesClosedByOtherRequesterOrganization:
            'To see the complete medical history you need consent of the patient',
        noConsent: 'no consent',
        selectAllServices: 'Select all specialisms',
        thePatientGives: 'The patient gives',
    },
    export: {
        allServices: 'All services',
        cancelDownload: 'Cancel download',
        compositePatientInvoice: 'Patient (composite)',
        declarationsExport: 'Declarations export',
        declarationsProcessExport: 'Export all declarations of process',
        declarationsProcessExportText:
            'All declarations that have been created with a reference-start date before or on the specified date are exported except for the DBCs where the reference end date is after today.',
        download: 'Download export',
        downloadProgress: 'Progress: {0} %',
        downloadReport: 'Download report',
        downloading: 'Exporting, one moment please.',
        episodeCategoryHasFixedServices: 'N.a. For {0} we declare a fixed set of services',
        export: 'Export',
        exportForDato: 'Export patient data for DATO registration',
        exportFundusExaminations: 'Export fundus examinations',
        exportFundusFiles: 'Export Fundus files',
        exportFundusRequests: 'Export fundusscreening requests',
        exportHistoryHide: 'Hide export history',
        exportHistoryShow: 'Show export history',
        exportingOneMomentPlease: 'Exporting, one moment please. Progress: {0} %',
        exports: 'Exports',
        fundusBestanden: 'Fundus files',
        invoicesExport: 'Invoices export',
        invoicesProcessExport: 'Export all invoices of process',
        oldDeclarationMethodOnly: 'old declaration method only',
        paymentExport: 'Payments export',
        profession: 'Role',
        professionChoice: 'Specific role',
        role: 'Role',
        roleAll: 'All roles',
        roleChoice: 'Roles',
        roleSpecific: 'Specific role',
        runDate: 'Export date',
        runNumber: 'Run number',
        selectOneOrMoreService: 'Select one or multiple services',
        serviceConsumerChoice: 'Services for consumers',
        serviceConsumerNo: 'Do not select on service consumers',
        serviceProviderChoice: 'Services for providers',
        serviceProviderNo: 'Do not select on service providers',
        serviceSupervisorChoice: 'Services for supervisors',
        serviceSupervisorNo: 'Do not select on service supervisors',
        servicesForDeclaration: 'Service(s) for declaration',
        singularPatientInvoice: 'Patient (singular)',
        specificService: 'Specific service',
    },
    exporter: {
        error: {
            columnName: 'Export Error',
            entryMessage:
                "Error: PID {0} cannot be exported because it contains an invalid value in the field '{1}'. Please contact team Hi Five for assistance.",
            pageName: 'Errors',
        },
    },
    featureFlags: {
        addFeatureFlag: 'Add feature flag',
        availableInFrontend: 'Available in frontend',
        confirmDelete: 'Are you sure you want to delete this feature flag?',
        confirmToggleOff: 'Are you sure you want to disable this feature flag?',
        confirmToggleOn: 'Are you sure you want to enable this feature flag?',
        featureFlagActive: 'Active',
        featureFlagDescription: 'Description',
        featureFlagDescriptionHelper:
            'Describe the the purpose of the feature flag, for example: "Enable new service for all users"',
        featureFlagKey: 'Flag name',
        featureFlagKeyHelper: 'e.g. ROLLOUT_NEW_SERVICE',
        featureFlagType: 'Type',
        featureFlagTypeValue: 'Value',
        header: 'Feature Flags',
        noFeatureFlags: 'No feature flags found',
    },
    finance: {
        activity: 'Activity',
        activityCode: 'Activity code',
        activityDate: 'Activity date',
        activityPaid: 'Paid',
        activityUnpaid: 'Unpaid',
        amount: 'Amount',
        bookYear: 'Book year',
        btw: 'BTW',
        chooseActivities: 'Choose the activities',
        chooseServices: 'Choose the services',
        confirmationMessageReprocess: 'Are you sure you want to reprocess {0} processes?',
        counterBooking: 'Counter booking',
        dateOfPayment: 'Scheduled date of payment',
        dateTooltip: 'Date of payout record. This date will be the same as the date the activity was performed.',
        declaration: 'Declaration',
        declarationDate: 'Declaration date',
        declarationInvoices: 'Financial (total)',
        declarations: 'Declarations',
        declare: 'Declare',
        declareToInsurer: 'Declare to insurer',
        declared: 'Declared',
        employmentTaxesDetailsExport: {
            createdBy: 'Created by',
            declarationYear: 'Declaration year',
            exportDate: 'Exportdate',
            exportFailed: 'Export unsuccessful',
            exportIsRequested: 'Export requested',
            requestNewExportModal: {
                alert: 'Watch out! Before clicking Export, ask your Teamlead Finance if the declaration has already taken place.',
                body: 'To ensure that we do not overload the system, this export is started at night.\nTomorrow morning the new export will be in the table and you can download the export.',
                header: 'Request new UBD export',
            },
            ubd: 'UBD',
            ubdExportAlert:
                'Make sure you process the mail notifications of legal form changes before you send the tax return to the Tax Office',
        },
        emptyState: 'Payouts are displayed on this page after the first payout has taken place.',
        expectedPayoutDate: 'Expected payout date',
        exportPaymentsActivities: 'Export all payouts from activities',
        exportStatus: 'Export status',
        exports: 'Exports',
        finance: 'Finance',
        financialReport: 'Financial report',
        financing: 'Financing',
        financingDetails: 'Financing details',
        financingType: 'Type of financing',
        financingTypeHeadings: {
            compositePatientInvoices: 'Composite patient invoices',
            declarations: 'Declarations',
            invoices: 'Invoices',
            singularPatientInvoices: 'Singular patient invoices',
        },
        flex: 'Flex',
        freeOfCharge: 'Free of charge',
        fundingAccordingToSettings: 'Funding according to settings',
        fundingReport: 'Funding report',
        fundingServicePart: {
            activities: 'Funded activities',
            all: 'Entire service',
            allInfo:
                'A process of this service is funded in its entirety. If a declaration or invoice is created according to this rule, no other funding methods can be applied to the same process afterwards.',
            invoiceServiceCode: 'Override servicecode for invoices',
            invoiceServiceName: 'Override servicename for invoices',
            invoiceServiceType: 'Override servicetype for invoices',
            longName: 'Funded part of the service',
            removeError:
                'Partial funding could not be removed because it is in use in an invoice setting or declaration setting',
            shortName: 'Partial funding',
            specific: 'Funds only specific activities',
            specificInfo:
                'If a declaration is created according to this rule, only the activities marked as "funded" will be funded. The other activities of the process can still be funded by other insurer agreements, which can lead to other declarations.',
        },
        hideActivities: 'Hide activities',
        inNameOf: 'In the name of',
        institution: 'Institution',
        insurerDeclarations: 'Insurer declarations',
        invoice: 'Invoice',
        invoiceDate: 'Invoice date',
        invoiced: 'Invoiced org/grp',
        invoicedPatient: 'Invoiced patient',
        invoices: 'Invoices',
        invoicesGroups: 'Invoices groups',
        invoicing: 'Invoicing',
        lessFilters: 'Less filters',
        moreFilters: 'More filters',
        noFinancialStatus: 'No financial information is known',
        noFinancing: 'No financing',
        noValidEmploymentsForPayments: 'You have no valid employments to generate payments.',
        numberOfPayouts: 'Quantity',
        old: 'Old',
        organizationType: 'Type of organisation',
        overviewPayouts: 'Overview payouts',
        paid: 'Paid',
        paidDate: 'Date paid',
        paymentReference: 'Payment reference/indication',
        payments: 'Payments',
        payout: 'Payout',
        payoutsExportTooltip: 'When left empty, the month in which the activity took place is taken',
        payoutsReport: 'Payouts report',
        performFinancingSearch: 'Search for a funding by selecting the searching filters above',
        period: 'Period',
        previousPayments: 'Previous payments',
        priceAgreement: 'Price agreement',
        processing: 'Processing',
        providerOrganization: 'Provider Organization',
        reasonForAccessModel: {
            label: 'Why do you need the privacy sensitive data?',
            placeholder: 'Provide a valid reason...',
            subTitle:
                'Some of the data you selected is privacy sensitive. You can only access this data with a valid reason.',
            title: 'Privacy sensitive data',
        },
        reasonNoPayment: 'Reason no payment',
        reasonNoPaymentShort: 'Reason no pay.',
        report: 'Report',
        reports: 'Reports',
        reprocessByShort: 'Rep. by',
        reprocessFinancing: 'Reprocess financing',
        reprocessPayout: 'Reprocess payout',
        reprocessed: 'Reprocessed',
        reprocessedOn: 'Reprocessed on',
        reprocessingFinancing: 'The processes will be reprocessed. It may take some time before this is ready.',
        reprocessingPayouts: 'The care activities will be processed again. It may take some time before this is ready.',
        requestExportAgain: 'Request export again',
        requesterGroup: 'Requester Group',
        runCreatedAt: 'Run datum',
        runNumber: 'Run',
        runNumberShort: 'Run no.',
        searchForTooltip: 'For multiple processes, paste a PID list separated with commas',
        sensitiveDataInfoText:
            'Privacy sensitive data is default not selected. You will have to provide a reason if you need this data.',
        showActivities: 'Show activities',
        specification: 'Specifications',
        specsAvailableOn: 'Around {0}, the specifications of this payment will be available here.',
        subtotal: 'Subtotal',
        suppressEmployeePayoutOverview: 'Block view of payouts for employees',
        suppressEmployeePayoutOverviewTooltip:
            'Use this setting in case the employee is paid in CS but the final payment goes to the organisation. In Exact the bank account number of the organisation is then linked to that of the employee.',
        toBeDeclared: 'To be declared',
        toBeInvoiced: 'To be invoiced org/grp',
        toBeInvoicedPatient: 'To be invoiced patient',
        toBePaid: 'To be paid',
        toIban: 'To IBAN',
        totalPayable: 'Total payable',
        totalPayableExclBTW: 'Total (excl BTW)',
        upcomingPayments: 'Upcoming payout',
    },
    fundingErrorCode: {
        all_rules_failed_custom_control_query: 'No matching declarationrule found because of custom control queries',
        canNotGetDateFirstProvidingActivity: 'Missing date for first care activity',
        cannotDetermineProcedurePerformedActivity: 'Cannot determine performed activity of procedure',
        cannotDetermineReportPerformedActivity: 'Cannot determine performed activity of report',
        cannotDetermineRequestPerformedActivity: 'Cannot determine performed activity of request',
        generalError: 'Unknown error',
        invalidAgb: 'Invalid AGB code in requester or supervisor practitioner',
        invalidInsurerSettings: 'Invalid insurer settings',
        invalidInvoiceSettings: 'Invalid invoice settings',
        missingCompensationCharge: 'Missing compensation charge',
        missingPerformerAgbCode: 'Missing AGB code at performer',
        missingPerformerRelationNumber: 'Relation number of performer is missing',
        missingPoortfunctionaris: 'Care program started without poortfunctionaris',
        missingReferrerAgbCode: 'Missing AGB code at referrer',
        missingRequesterRelationNumber: 'Relation number of requester is missing',
        multipleDeclarationsSameCareProductType: 'Multiple declarations with same careproducttype for single process',
        noDeclarationRules: 'No declaration rules available',
        noFundingResult: 'No funding result',
        noInsuranceHistory: 'No insurance history available',
        noInsurerServiceDeclarationActivities: 'No insurer declaration activities available',
        noInsurerSettings: 'No insurer settings',
        noPatientInvoiceSettings: 'No patient invoice settings',
        noRelationNumberAvailable: 'No relationnumber available',
        ozpDbcOverlapTooLarge: 'DBC/OZP overlap too large',
        serviceTypeNotSupported: 'Service type not supported when creating performed activity',
        uzoviNotLinkedToInsurer: 'Uzovi not linked to insurer',
        uzoviNotValid: 'Uzovi not valid',
        waitingForPreviousFundingRecord: 'Waiting for previous funding record',
        waitingForReProcessing: 'Waiting for reprocessing',
        zeroOrNegativeInvoiceAmount: 'Invoice amount less than or equal to zero',
    },
    fundingErrorInformationEnum: {
        conscientiousObjection: 'Conscientious objection',
        foreignInsurance: 'Foreign insured patient',
        invoicingAtGpPractice: 'Invoice GP practice',
        other: 'Other...',
        patientDied: 'Deceased patient',
        rmo: 'RMO',
        uninsuredPatient: 'Uninsured patient',
        wlz: 'WLZ',
    },
    fundingLog: {
        data: 'Data',
        fundingLog: 'Funding logs',
        fundingStep: 'Funding step',
        patientId: 'Patient ID',
        performedDatetime: 'Performed datetime',
    },
    fundingLogMutation: {
        doubledProcess: 'Doubled process',
        explainTheReason: 'Explain the reason',
        incorrectSettings: 'Incorrect settings',
        modalTitle: 'Financial log mutation reason',
        other: 'Other',
        reasonNoFunding: 'Reason no funding is set',
        selectAReason: 'Select a reason...',
        test: 'Test',
        unfunded: 'Unfunded',
        unpaid: 'Unpaid',
        writeHere: 'Write here...',
    },
    fundingSettings: {
        add: 'Add new funding setting',
        addFundingSetting: 'Add funding setting',
        alreadyFunded: 'Already funded',
        alreadyFundedExplanation:
            'Already funded: This service is provided free of charge if preceded by other service.',
        alternativeFunding: 'Alt. funding',
        alternativeFundingExplanation:
            'Alternative funding: This service has an alternative funding. Do not enter an amount for compensation.',
        fundingSetting: 'Funding setting',
        fundingSettingExplanation:
            'Funding setting: This is where you set if a the organization will be invoiced for a service.',
        invoice: 'Invoice',
        invoiceExplanation: 'Invoice: This organization recieves an invoice. Enter an amount for compensation',
    },
    fundingType: {
        declareToInsurer: 'Declaration to health insurer',
        freeOfCharge: 'Free of charge',
        fundingAccordingToSettings: 'Funding according to settings',
    },
    fundusNoShows: {
        checkSearchTerm: 'Please check your search term and/or change the filter',
        filterOnCareGroup: 'Filter on care group',
        filterOnGeneralPractitoner: 'Filter on general practitioner',
        importFileToSeeNoShows: 'Import a file to see any fundus no shows',
        noFundusShowsSubtible: 'As soon as there are no shows again, you will be notified of this',
        resultsNotFound: "We couldn't find any results for '{0}'",
        thereAreCurrentlyNoFundusNoShows: 'There are currently no fundus no shows',
    },
    fundusScreening: {
        brightPlanActionDescription:
            'During the examination, we take pictures of your eyes (retina). This allows us to detect damage to your eyes that may be there. The appointment takes about 10 minutes.',
        manualImport: {
            cell: 'cell',
            cells: 'cells',
            form: {
                callingFile: 'Calling file',
                excelFileButtonLabel: 'Choose Excel file',
                excelFileFieldLabel: 'Excel file',
                resetButtonLabel: 'Cancel',
                submitButtonLabel: 'Import',
                thirdPartyFieldLabel: 'Third party',
            },
            inviteType: {
                [FundusScreeningManualImportLogInvitationType.initialInvitation]: 'Oproep',
                [FundusScreeningManualImportLogInvitationType.repeatedInvitation]: 'Herhaaloproep',
            },
            logbook: {
                emptyState: 'No call files have been imported yet',
                modalTitle: 'Logbook processed call files',
                openLogbookButtonText: 'View logbook',
                tableColumns: {
                    careGroupName: 'Care group',
                    download: 'Download',
                    importDate: 'Import date',
                    importedBy: 'Imported by',
                    importedFilename: 'Imported file',
                    invitationType: 'Invite type',
                    thirdPartyName: 'Third party',
                },
            },
            pageSubtitle: 'Import from Excel',
            pageTitle: 'Import Fundus screening',
            successfulImport: 'Your Excel file has been imported',
            thirdParty: {
                [FundusScreeningThirdParty.vipLiveFundusManualImport]: 'VipLive',
                [FundusScreeningThirdParty.healthConnectedFundusManualImport]: 'Health Connected',
            },
        },
    },
    genderEnum: {
        female: 'Female',
        male: 'Male',
        other: 'Other',
        unknown: 'Unknown',
    },
    genderEnumInitial: {
        female: 'F',
        male: 'M',
        other: 'O',
        unknown: 'U',
    },
    genderSpirometerEnum: {
        female: 'Female',
        male: 'Male',
    },
    gradingSummaryEnum: {
        deviant: 'Deviant',
        normal: 'Normal',
        unjudgable: 'Unable to diagnose',
    },
    groupMeetingType: {
        bariGroupSession: 'Obesity group session',
        bariMdo: 'MDO',
    },
    groupSettings: {
        alreadyFinanced: 'Already financed',
        alreadyFinancedTooltip: 'This service is provided free of charge if preceded by other service',
        groupSettings: 'Group settings',
        isAlternativeFinancing: 'Alternative financing',
        noShowsImported: 'Import No shows',
        nonAvailable: 'No groups available',
        serviceGroupSettings: 'Service group settings',
        supervisionPercentage: 'Supervision percentage',
    },
    groups: {
        bankAccountNumber: 'Account number',
        details: 'Group details',
        exportGroups: 'Export groups ({0})',
        financialEmailAddress: 'Financial email address',
        financialEmailAddressTooltip: 'Email address for invoices and payout specifications',
        group: 'Group',
        groupExport: 'Group export',
        groupName: 'Group name',
        groupOverview: 'Group overview',
        groups: 'Groups',
        isActive: 'Active',
        nameGroup: "Group's name",
        nameOrTxcsId: 'Group name or ID',
        noActiveCarePaths: 'This group has no care paths',
        noGroupsAvailable: 'No groups available',
        subscriber: 'Account name',
    },
    help: {
        faq: 'FAQ',
        manual: 'Manual',
        support: 'Support',
    },
    hipRomTable: {
        endorotation: 'Endorotation',
        exoratation: 'Exorotation',
        extension: 'Extension',
        flexion: 'Flexion',
        leftHip: 'Left hip',
        rightHip: 'Right hip',
    },
    holter: {
        addDiary: 'Add diary',
        allActiveRecordings: 'Active',
        allEcgRecordings: 'All ECG recordings',
        batteryPercentage: 'Remaining battery charge',
        cancelExamination: 'Cancel examination',
        chooseDuration: 'Click and choose the recording duration...',
        clearEcgRecorder: 'Clear the ECG recorder',
        clearRecorder: 'Clear the recorder',
        configured: 'Configured',
        confirmationMessageClearHolter:
            'Are you sure that you want to clear this recorder? We will abort the procedure as well.',
        detectRecorder: 'Detect recorder',
        diary: 'Diary',
        ecgRecordersOverview: 'ECG recorder overview',
        fax: 'Fax',
        handedIn: 'Handed in',
        history: 'History',
        patientName: 'Name of patient',
        percentageCompleted: '% Completed',
        printDiary: 'Print diary',
        recorder: 'Recorder',
        recorderId: 'Serial number',
        recorders: 'Recorders',
        sendDiary: 'Send',
        sent: 'Sent',
        startDownloading: 'Start downloading',
        sureToClear: 'Are you sure. The ECG will be removed and the procedure will be aborted.',
        uploadDiaryLater: 'Upload the diary later',
        uploaded: 'Uploaded',
        withAnalyst: 'At analyst',
    },
    holterCardioExaminationDurationEnum: {
        oneDay: '1 day',
        threeDays: '3 days',
        twoDays: '2 days',
    },
    home: {
        action_inquiry: 'Inquire',
        action_performSelf: 'Perform it yourself',
        chooseStartResearch_action: 'What do you want to do?',
        chooseStartResearch_consultation: 'Which consult do you want to start?',
        chooseStartResearch_contact: 'What registration do you want to do?',
        chooseStartResearch_counseling: 'Which guidance do you want to start?',
        chooseStartResearch_examination: 'Which research do you want to start?',
        chooseStartResearch_inquiry: 'Which research do you want to request?',
        chooseStartResearch_referral: 'Which referral do you want to start?',
        noServices: 'No choices have been found for this service.',
        patientBarLabel: 'For which patient?',
        startOne: 'Get started with a',
        startService: 'Start',
        type_consultation: 'Consult',
        type_contact: 'Contact',
        type_counseling: 'Counseling',
        type_examination: 'Examination',
        type_inquiry: 'Inquiry',
        type_labAanvraag: 'Lab request',
        type_other: 'Other',
        type_referral: 'Referral',
        type_referralSmall: 'Referral',
    },
    homeConsultation: {
        addControlText: 'When submitted a request will be send to the patient.',
        editControlText: 'Request sent to the patient.',
        homeConsultation: 'Home consultation',
        photo1: 'Maak een overzichtsfoto',
        photo2: 'Maak een foto van dichtbij',
        photo3: 'Maak eventueel een derde foto die u nuttig lijkt',
        photo4: 'Maak eventueel een vierde foto die u nuttig lijkt',
        showControlText: 'The results',
    },
    identifierDeclarationOrRegistration: {
        addIdentifierDeclarationOrSupervisorToContinue: 'Add an AGB code or select a supervisor.',
        addIdentifierDeclarationToContinue: 'Add an AGB code.',
        addIdentifierRegistrationBigToContinue: 'Add a BIG-number.',
        addNewIdentifierDeclaration: 'Add new AGB code',
        chooseAProfession: 'Choose a profession.',
        enterValue: 'Enter value.',
        enterValueToContinue: 'Add this to continue',
        howToAddIdentifierDeclaration: 'How do you want to add the AGB code?',
        identifierDeclaration: 'AGB code',
        identifierDeclarationAndIdentifierRegistrationBig: 'AGB code or BIG-number',
        identifierDeclarationAndIdentifierRegistrationBigRequiredBecauseOfProfession:
            'You have selected profession which requires an AGB code and BIG-number.',
        identifierDeclarationOrReferenceRequired:
            "For this profession the use of a AGB code or the use of an colleague's AGB code is required",
        identifierDeclarationPractitionerId: "Colleague's AGB Code",
        identifierDeclarationRequiredBecauseOfProfession: 'You have selected a profession which requires a AGB code.',
        identifierHeader: 'BIG / KP & AGB',
        identifierRegistrationBig: 'BIG-code',
        identifierRegistrationKp: 'KP-number',
        identifierSavedIntoPractitionerProfile:
            'When you add a {0} it will be saved into the profile of the practitioner.',
        identifierSavedIntoPractitionerProfilePlural:
            'When you add a {0} these will be saved into the profile of the practitioner.',
        newIdentifierDeclaration: 'New AGB code',
        searchForNameOrAGB: 'Search by name or AGB code',
        searchOnNameOrIdentifierDeclaration: 'Search on name or AGB code',
        supervisorRequiredBecauseOfProfession: 'You have selected a profession which requires a supervisor.',
        usesIdentifierDeclarationPractitionerId: "Uses a colleague's AGB Code",
    },
    importActionsEnum: {
        renew: 'Renew',
        update: 'Update',
    },
    imports: {
        addFailed: "Adding file '{0}' failed, please try again",
        authenticationDevices: 'Hardware tokens',
        bankAccountConflicts:
            'The new account details have not been imported. There are accounts details in this file that do not match the account details known in ClientSafe. Correct the account details in Exact and import the file again',
        bankAccountDetails: 'Bank account details',
        bankAccountDetailsTooltipText:
            'Export the bank account information from Exact. Remove the first lines, so that the first row contains at least the columns: "Code", "Bankrekening" and "Tenaamstelling rekening". Export as a CSV file.',
        corruptedDataMessage: 'The file has an incorrect format and cannot be read out',
        dhd: 'DHD',
        episodeCategoryNotValid: 'Select a valid care path.',
        fundusScreening: 'Fundusscreening',
        import: 'Import',
        importDevicesSuccessMessage: 'new devices have been successfully registered',
        importSuccess: 'Successfully imported {0} records',
        importedNoShowsPeriods: 'Uploaded no show periods',
        infoAlertNoShow: 'At this moment there are no no-shows for your practice',
        insurerUzovis: 'Insurer uzovis',
        insurers: 'Insurers',
        invalidDataMessage: 'The file contains invalid data',
        migrationOrganizations: 'Migration organizations',
        migrationOrganizationsImportOrganizationNotFound:
            'Import failed, {0} is not an existing organization in CS. No updates have been done.',
        migrationOrganizationsImportSucces: 'Successfully imported',
        noBankAccountsUpdated: 'All bank accounts are up to date, 0 records were imported',
        noShowPatients: 'No show patients',
        noShows: 'No shows',
        notRequestedBankAccountConflicts: 'Bank details not requested and not matching',
        oneBankAccountImported: '1 record successfully imported',
        pkscFile: 'PKSC file',
        relationNumbers: 'Link exact relations',
        requestedBankAccountConflicts: 'Bank details requested but not matching',
        severalBankAccountsImported: '{0} records successfully imported',
        txscIdFile: "File with TxCS ID's",
        uzovi: 'UZOVI',
    },
    incomingMessages: {
        incomingMessages: 'Incoming messages',
        messageSender: 'Message sender',
        messageType: 'Message type',
        patient: 'Patient',
        pid: 'Process id',
        practitioner: 'Care provider',
        receivedAt: 'Received at',
        status: 'Status',
        statusMessage: 'Status message',
    },
    insuranceHistory: {
        endDate: 'End date',
        insuranceHistory: 'Insurance history',
        insurancePolicyNumber: 'Insurance policy number',
        message_IdentifierSocialSecurityNotFound:
            'No insurance information is available for the given social security number',
        message_IncorrectBirthDate: 'The given birthdate deos not match the given social security number',
        message_NoActiveInsurance: 'No active insurances could be found',
        message_NoUniquePersonIdentified:
            'There are multiple persons found that match the given criteria. Please fill in the socials ecurity identifier and try again',
        message_NotAllowed:
            'There is an active insurance found but you do not have permission to read the information of this insurance',
        message_ServiceDown: 'An error occured while fetching insurance information',
        message_UnknownInsurerCode: 'An active insurance was found but the insurer was unknown',
        message_VecozoError: 'An error occured while fetching insurance information',
        message_success: 'The insurance information has been retrieved',
        retrieve: 'Retrieve insurance history',
        startDate: 'Start date',
    },
    insurers: {
        access: 'Access',
        add: 'Add insurer',
        addCeilingYear: 'Add ceiling year',
        cannotRemoveInsurer: 'The insurer cannot be removed because there are one or more coupled uzovis.',
        careLimitation: 'Care limitation',
        careProductType: 'Care product type',
        ceilingAmount: 'Ceiling amount',
        contractedCareCannotBeStarted: 'Contracted care cannot be started',
        currentPrice: 'Current Price',
        declarationCode: 'Declaration code',
        declarationMethod: 'Declaration method',
        ensureTeamLeadsAreAware: 'ATTENTION! Ensure that all team leads are aware of this situation',
        exportUntilDate: 'Export until date',
        exportUntilDateTooltip: 'Declarations of processes created after this date are not exported',
        hasLinkedDeclarationRules:
            'The insurer cannot be removed because it is linked to one or more declaration rules.',
        insurer: 'Insurer',
        insurerUzoviOverlapMessage:
            'This uzovi is already connected with insurer {0} for the period. Remove or terminate this commitment first.',
        insurerUzovis: "Insurer uzovi's",
        insurers: 'Insurers',
        latestInsurer: 'Latest insurer',
        linkedUzovis: 'Linked uzovis',
        ovelappedTurnoverCeiling: 'A turnover ceiling has already been introduced for {0} {1} {2}',
        paymentPeriod: 'Payment expectation',
        paymentPeriodPlaceholder: 'Payment expectation in days',
        paymentPeriodTooltip:
            'The expected number of days after which the health care insurer will pay, after the claim has been submitted to Vektis. This number is used for the liquidity forecast.',
        periodOfCareLimitation: 'Period of care limitation',
        phoneForCareMediation: 'Telephone number for care mediation',
        phoneNumberCareMediation: 'Telephone number care mediation',
        supplementPriceActivity: 'Suplement price activity',
        turnoverCeiling: 'Turnover ceiling',
        turnoverCeilingTooltip:
            "The turnover ceiling is the maximum allowed amount that can be claimed from the health care insurer for one year. The amount is used in the 'Turnover Ceiling Reporting'.",
        year: 'Year',
    },
    intakeReport: {
        notYetCompleted:
            'You have not yet completed an intake report. Type or copy the intake report in the text field or put a file containing the intake report in the files box.',
    },
    interapy: {
        calculatedValue: 'Automatically calculated value',
        clientComplaintsDescription: 'Client complaints description',
        comorbidity: 'Comorbidity',
        courseOfComplaints: 'Course of complaints',
        depression: 'Depression',
        distress: 'Distress',
        dsmDisorder: 'DSM disorder',
        dsmPrevalentComplaint: 'DSM prevalent complaint',
        dsmSuspicion: 'DSM suspicion',
        durationOfComplaints: 'Duration of complaints',
        fear: 'Fear',
        indication: 'Indication',
        patientEmail: 'Patient email address',
        purposeOfTreatment: 'Purpose of treatment',
        riskOfDrugAbuse: 'Risk of drug abuse',
        riskOfPsychosis: 'Risk of psychosis',
        riskOfSuicide: 'Risk of suicide',
        sendPostMeasurement: 'Send post-measurement',
        seriousness: 'Seriousness',
        somatisation: 'Somatisation',
    },
    jobStatusEnum: {
        failed: 'Failed',
        running: 'Running',
        scheduled: 'Scheduled',
        success: 'Success',
    },
    jobs: {
        duration: 'Duration (seconds)',
        id: 'Id',
        jobs: 'Jobs',
        name: 'Job name',
        output: 'Output',
        params: 'Parameters',
        ranAfterDate: 'From date',
        ranBeforeDate: 'Until date',
        runNow: 'Run now',
        scheduledOn: 'Scheduled date',
        startedOn: 'Started',
        status: 'Status',
    },
    journals: {
        comment: 'Comment',
        completeJournalsOverview: 'Complete journals overview',
        entryCode: 'Code',
        entryCodeSystem: 'Code System',
        entryDisplayName: 'Name',
        episodes: 'Journal episodes',
        journals: 'Journals',
    },
    kisSetting: {
        kisSetting: 'KIS Settings',
        kisType: 'KIS Type',
        serverAddress: 'Server address',
        serverPort: 'Server port',
        username: 'Username',
        usesKis: 'Makes use of a KIS',
    },
    labResults: {
        codeDisplayName: 'Code',
        codeSystem: 'Code System',
        comment: 'Comment',
        description: 'Description',
        labResults: 'Lab Results',
        noLabResultsFound: 'No lab results were found.',
        overview: 'Overview labresults',
        selectAll: 'Select all',
        valueUnit: 'Unit',
        valueValue: 'Value',
    },
    login: {
        accountRegisteredHelp: 'You can now sign in.',
        dontRememberUserNameOrEmailAddress: "Don't remember your username or email address either?",
        error: {
            BAD_QUALITY_PASSWORD: 'Your chosen password is too easy to guess. Choose a different password.',
            NO_PASSWORD: 'Choose a password to continue',
            PASSWORD_CONTAINS_FREQUENTLY_USED_PASSWORD:
                "Make your password longer or don't use frequently used passwords.",
            PASSWORD_CONTAINS_PERSONAL_INFORMATION:
                "Make your password longer or don't use personal data in it (names, dates, etc)",
            PASSWORD_CONTAINS_REPEATED_SUBSEQUENCE:
                'Make your password longer or don\'t use easy to guess sequences (like "abab" or "1234") in it.',
            PERSONAL_INFORMATION_IN_PASSWORD:
                'Your chosen password contains personal information making it easy to guess. Choose a different password.',
            SHORT_PASSWORD: `Your password must consist of at least ${MINIMUM_PASSWORD_LENGTH} characters`,
            VERY_SHORT_PASSWORD: `Your password must consist of at least ${MINIMUM_PASSWORD_LENGTH} characters`,
        },
        installTotpSoftware: 'If you have not already installed Google Authenticator download it here:',
        linkAuthenticator: 'Link Google Authenticator',
        loginAgain: 'Login again',
        pickAPassword: 'Pick a password',
        register: 'Activate',
        registerYourAccount: 'Activate account',
        registerYourAccountHelp:
            'Activate your account with your username and your unique activation code of twelve letters.',
        registrationCode: 'Registration code',
        scanTotpQrcodeHelp:
            'Open the Google Authenticator app and scan the code below. Then enter the six digit code that appears to activate your account.',
        signIn: 'Sign in',
        signInHelpCall: 'Call',
        signInHelpOrMail: 'or mail',
        signInHelpQuestion: 'Need help?',
        signInHelpSchedule: '(8:30 - 17:00) ',
        totpToken: 'Google Authenticator code',
        totpTokenInactivity: "Can't fill in your code?",
        welcome: 'Welcome {0}, please enter the Google Authenticator code',
    },
    logs: {
        thirdPartyMessages: 'Third party messages',
    },
    maritalStatusEnum: {
        A: 'Annulled',
        D: 'Divorced',
        I: 'Interlocutory',
        L: 'Legally separated',
        M: 'Married',
        P: 'Polygamous',
        S: 'Never married',
        T: 'Domestic partner',
        U: 'Unmarried',
        UNK: 'Unknown',
        W: 'Widowed',
    },
    medications: {
        addMedication: 'Add medication line',
        addMedicationsFromHis: 'Medication retrieved from the HIS',
        addMedicationsManually: 'Manually entered medication',
        addRequestMedication: 'Add medication',
        completeMedicationRecord: 'Complete medication record',
        currentMedication: 'Current medication',
        description: 'Description',
        dosageInstruction: 'Dosage instruction',
        dosageUnit: 'Dosage Unit',
        dosageValue: 'Dosage Value',
        dose: 'Dose',
        endDate: 'End date',
        frequency: 'Frequency',
        medInstrCode: 'Instruction Code',
        medInstrName: 'Instruction Name',
        medInstrSystem: 'Instruction System',
        medication: 'Medication',
        medicationNamePlaceholder: 'Fill in the medication name...',
        medicationPresciptionPlaceholder: 'Fill in the prescription...',
        medications: 'Medications',
        mkcodeCode: 'Code',
        mkcodeSystem: 'Code System',
        mkcodeSystemName: 'Code Name',
        modalBody:
            'You have indicated that the patient is taking medication, but you have not filled in any medication. Are you sure you want to send the consultation without medication?',
        modalTitle: 'Send without medication',
        name: 'Name',
        numberOfMedicationsManuallyAdded: '({0} added)',
        numberOfMedicationsSelected: '({0} selected)',
        overview: 'Overview medication',
        patientUsesMedication: 'Patient uses/used medication',
        patientUsesMedicationTooltip:
            'Add relevant medication. For example medication for the treatment of cardiovascular diseases, diabetes, and psychomedication',
        quantity: 'Quantity',
        quantityCode: 'Quantity Code',
        quantityCodeSystem: 'Quantity System',
        quantityDisplayName: 'Quantity Name',
        quantityValue: 'Quantity Value',
        reason: 'Reason',
        sendMedications: 'Send medications',
        startDate: 'Start date',
    },
    months: {
        index: {
            abbreviation: {
                0: 'jan',
                1: 'feb',
                2: 'mar',
                3: 'apr',
                4: 'may',
                5: 'jun',
                6: 'jul',
                7: 'aug',
                8: 'sep',
                9: 'oct',
                10: 'nov',
                11: 'dec',
            },
        },
    },
    neurologyPatientTaskType: {
        remaining: 'Other',
        remainingOther: 'Other',
    },
    noPayoutReasonEnum: {
        NoActiveEmployment: 'No active employment',
        aborted: 'Process aborted',
        ledgerNumber: 'Missing ledger number',
        markedAsUnpaid: 'Marked as unpaid',
        missingAmount: 'Missing amount',
        noEmailAddress: 'No email address',
        payoutByTxcs: 'Payout by TxCS',
        providerName: 'Missing provider name',
        providerOrganization: 'Missing name provider organization',
        relationNumber: 'Missing relation number',
        waitingForConsultOrGrading: 'Waiting for consult or grading',
        waitingForFollowUpActivity: 'Waiting for follow-up activity',
        waitingForReProcessing: 'Waiting for reprocessing',
        waitingforOsasc2: 'Waiting for OSASC2',
        zeroAmount: 'Zero amount',
    },
    notFundedReason: {
        abortedProcess: 'Aborted process',
        financedByPreviousService: 'Financed by previous service',
        freeOfCharge: 'Free of charge',
        fundedByOldPatientInvoicing: 'Funded by old patient invoicing',
        isAlternativeFinancing: 'Alternative financing',
        manual: 'Manual',
        migration: 'Process migrated from TxCS',
        noConsentPatient: "The patient hasn't consented to the costs",
        noDeclarationSetForInsurerService: 'No declaration set for the insurer',
        noFundingDueToActivitySettings: 'Not funded according to activity settings',
        noInsurersConfigured: 'No insurers configured',
        notFunded: 'Not funded set',
        parallelDeclaration: 'Parallel declaration found',
        serviceNotFundable: 'Dienst not fundable',
        uninsuredOrNonBillableActivity: 'Uninsured or non-billable activity',
    },
    ophthalmology: {
        andere_bevindingen: 'Other findings',
        bsnCheckMatchingMessage: `An automatic check is performed to see if the patient's BSN matches the fundus photo name.`,
        geadviseerd_beleid: 'Advised policy',
        maculopathie: 'Maculopathie',
        noBsnMatchingValidationMessage: `The BSN of the selected photos does not match the BSN of the patient, therefore the photo selection was (partially) not added. Please adjust the selection and try again.`,
        retinopathie: 'Retinopathie',
        toelichting: 'Toelichting',
        unknownBsnMessage: `The patient's BSN is unknown. There is no automatic check that the correct photos are added.`,
        urgentie: 'Urgency',
    },
    ophthalmologyPatientTaskType: {
        remaining: 'Other',
        remainingOther: 'Other',
    },
    orderSupplies: {
        additionalNotes: 'Additional notes',
        deliveryLocation: 'Delivery location',
        order: 'Order',
        orderSuppliesForEpisodeCategory: '{0} - order supplies',
        placeOrder: 'Place order',
        product: {
            plural: 'Products',
            singular: 'Product',
        },
        selectProduct: 'Select product',
        selectProducts: 'Select product(s)',
        whatDoYouNeed: 'What do you need?',
    },
    organization: {
        accountRequests: 'Account requests',
        activeCarepaths: 'Active carepaths',
        add: 'Add organization',
        addAgbCode: 'Add extra AGB code',
        addCount: 'Add count',
        addOrganization: 'Add Organization',
        addOrganizationRelationship: 'Add organization relationship',
        addStaffMember: 'Add staff member',
        added: 'Data registered',
        address: 'Address',
        agbCode: 'AGB code',
        agbCodes: 'AGB code/start- end date',
        alias: 'Alias',
        areContactsAndTasksEnabled: 'Contacts and tasks',
        areContactsAndTasksEnabledTooltip:
            'Support in creating tasks and registering contacts for Ksyos employees. The occupation must be an outpatient employee.',
        autoFill: {
            agbOnlyNumbersError: 'AGB code may only contain numbers',
            copySelection: 'Copy selection',
            inactiveOrganizationError: 'Inactive organization',
            invalidAgbError: 'Invalid AGB code',
            noOrganizationFoundError: 'Geen organisatie gevonden',
            searchAgb: 'Search on AGB code',
            searchResult: 'Search result',
            searchVektis: 'Search on Vektis',
            selectAll: 'Select all',
        },
        bankAccountNumber: 'Bank account number',
        bankAccountStatus: 'Status bank account',
        canGetStarted: '{0} can get started with Ksyos',
        changeName: 'Edit organization name',
        changeNameAskStartDate: 'On what date should the new name apply?',
        changeNameExplanation:
            'A changed name has effects on the financial processes. We send an automated mail to the financial department to keep them up to date with the latest changes.',
        changeNameStartDate: 'Start date',
        chooseOrganization: 'Choose organization',
        city: 'City',
        cityOrZip: 'City or postcode',
        contactDetails: 'Contact details',
        dataErrorStats: {
            active: 'Active organizations',
            agbCodeErrorTitle: 'Organizations with AGB code errors',
            archived: 'Archived organizations',
            attentionRequired: 'Attention required',
            cleaningStatus: 'Cleaning status',
            duplicateAddress: 'Duplicate address',
            duplicateAgbCode: 'Duplicate AGB code',
            duplicateKvkNumber: 'Duplicate KVK number',
            duplicateNameStatutory: 'Duplicate statutory name',
            duplicateOrganizationTitle: 'Organization with duplicate data',
            expiredAgb: 'AGB code expired',
            inProgress: 'In progress',
            invalidAgb: 'AGB code invalid',
            missingAgb: 'AGB code missing',
            tooltips: {
                active: 'Number of active organizations',
                archived: 'Number of archived organizations',
                attentionRequired: 'Number of organizations with cleanup status: Attention required',
                duplicateAddress: 'Number of organizations with a duplicate address',
                duplicateAgbCode: 'Number of organizations with a duplicate AGB code',
                duplicateKvkNumber: 'Number of organizations with a duplicate KVK number',
                duplicateNameStatutory: 'Number of organizations with a duplicate statutory name',
                expiredAgb: 'Number of organizations with an expired AGB code according to Vektis',
                inProgress: 'Number of organizations with cleanup status: In progress',
                invalidAgb: 'Number of organizations where the AGB code is not present in Vektis',
                missingAgb: 'Number of general practices where the AGB code is missing',
                totalAgbErrors: 'Total number of organizations with AGB errors',
                totalDuplicateErrors: 'Total number of organizations with duplicate errors',
                unverified: 'Number of organizations without cleanup status',
                verified: 'Number of organizations with cleanup status: Verified',
            },
            totalAgbErrors: 'Total AGB errors',
            totalDuplicateErrors: 'Total duplicate errors',
            totalOrganizations: 'Total organizations',
            unverified: 'No attention required',
            verified: 'Verified',
        },
        details: 'Organization details',
        dossierNotAccessible: 'Dossier not accessible to general practitioner',
        edit: 'Edit organization',
        emailAddressPayouts: 'Email address for payouts',
        emailAddressRequestInbox: 'Email address request inbox',
        emailRequestFin: 'Email address request inbox & financial department',
        emergencyNumber: 'Emergency number',
        emergencyNumberTooltip:
            'If the practice number is also the emergency number, enter the practice number as emergency number as well.',
        existingOrganizations: 'Do you perhaps mean an existing organization?',
        exportData: 'Data to be exported',
        exportOrganizations: 'Export organizations',
        fallBackAnalysisOrganization: 'Fall back analysis organization',
        findOrganization: 'Find organization',
        foldInactiveOrganization: 'Hide inactive organizations',
        futureEmploymentsTitle: 'Practitioners with future rights',
        generalInformation: 'General details',
        healthcareInstitution: 'Healthcare institution',
        highPrivacyLevel: 'High level privacy',
        highPrivacyLevelTooltip: "If this is turned on, patient records are not viewable by the patient's GP",
        houseNumber: 'House number',
        houseNumberSuffix: 'House number suffix',
        identifierDeclaration: 'AGB code',
        inWhichOrganization: 'In which organization do you want to conduct the eye examination for this patient?',
        inactiveEmploymentsTitle: 'In the past working for this organization',
        isMijnKsyosActive: 'Pilot MijnKsyos',
        isMijnKsyosActiveTooltip:
            'Patients will be invited to participate in the MijnKsyos pilot. This provision applies only to optho shops and does not affect the use of MijnKsyos in the following care pathways: sleep, audiology, dermatology.',
        kgoStatus: 'KGO status',
        kgoStatuses: {
            attentionRequired: 'Attention required',
            inProgress: 'In progress',
            verified: 'Verified',
        },
        ksyosEmployees: 'Ksyos employees',
        kvkNumber: 'KVK number',
        location: 'Location',
        modal: {
            deactivateKsyosEmployment: 'The employee will no longer be able to perform any activities.',
            logbookAction: 'Action',
            logbookActionDescription: {
                insert: 'Added',
                update: 'Updated',
            },
            logbookChangedBy: 'Changed by',
            logbookDateOfChange: 'Date of change',
            logbookTitle: 'Logbook patient number changes',
        },
        name: 'Organization name',
        nameOrAgbCode: 'Organization name or AGB code',
        nameOrTxcsId: 'Organization name or ID',
        nameOrganization: "Organization's name",
        nameStatutory: 'Statutory name',
        namesHistory: 'Previous names',
        newCount: 'New count',
        noActiveCarePaths: 'This organization has no care paths',
        noActiveCareProviders: 'This organization has no care providers',
        noPatientCount: 'No counts added',
        noSearchResultOrganization: "We didn't find any organizations based on your search term.",
        notes: 'Notes',
        notifications: {
            canOnlyBeReceivedByOrganization: 'Can only be received on organization email',
            consultation: 'Notifications consultation',
            determineSettingsAsOrganization:
                "If you set 'Can only be received on organization email' to Yes, then care providers can no longer enter their own email address in their notification settings.",
            examination: 'Notifications examination',
            notifications: 'Notifications',
            organizationEmailNotSet:
                'You did not set an organization email. Would you like to receive notifications there?',
            receiveOn: 'Receive on',
            referral: 'Notifications referral',
            setOrganizationEmail: 'Then enter it here.',
        },
        numberOfPatients: 'Number of registered patients...',
        organization: 'Organization',
        organizationConsumer: 'Organization, applicant',
        organizationData: 'Data at the organization',
        organizationEmailAddress: 'Organization email address',
        organizationGroup: 'Organisation/Group',
        organizationId: 'OrganizationId',
        organizationLevelPayoutsStartDate: 'Start date of payouts based on 2025 contract',
        organizationLevelPayoutsStartDateTooltip:
            "This is a temporary financial setting that determines the renewed payments for the 2025 contracts. The date is usually the start date of the organization's new contract.",
        organizationList: 'Organization list',
        organizationOverview: 'Organization overview',
        organizationPatientCount: 'Number of registered patient for the organization',
        organizationPayoutManagers: {
            logbookAction: 'Action',
            logbookActionDescription: {
                delete: 'Delete',
                insert: 'Insert',
            },
            logbookModificationDate: 'Modification date',
            logbookModifiedBy: 'Modified by',
            logbookPractitionerName: 'Name',
            logbookTitle: 'Organization managers changes logbook',
            noOrganizationManagersMessage:
                "This organization does not have an organization administrator. Add this role under the caregiver's personal settings.",
            title: 'Organization managers',
        },
        organizationRelationships: 'Organization relationships',
        organizationSpecialist: 'Organization, specialist',
        organizationStats: 'Organisation statistics',
        organizationStructureType: 'Organization structure type',
        organizationStructureTypeHeader: 'Type',
        organizationType: 'Type of organization',
        organizations: 'Organizations',
        patientCount: 'Patient count',
        patientCountExplainer:
            'The amount remains valid for all following reference dates until a new date and amount is registered.',
        patientCountReferenceDateExplainer: 'Reference date for new patient count…',
        pendingApprovalOf: 'Pending approval of {0}',
        phoneNumber: 'Phone number',
        practitionerAddedWillNotUseCS: '{0} can be selected as requester and will then receive ZorgMail',
        practitionerAddedWillUseCS: '{0} will receive an activation email at {1}',
        practitionerDataAtOrganization: 'Care provider data at the organization',
        practitioners: 'Care providers',
        practitionersWithFutureEmployments: 'Practitioners with employments in the future',
        practitionersWithPastEmployments: 'Practitioners with employments in the past',
        previousNames: 'Previous name',
        referenceDate: 'Reference date',
        rejectedAccountRequestAt: 'Rejected at {0}',
        rejectedAccountRequests: 'Rejected account requests',
        relationNumber: 'Relation number',
        relationNumberAbbeviation: 'Rel. no.',
        reviewMatch: 'Review match',
        save: 'Save',
        select: 'Select organization',
        showInactiveOrganization: 'Show Inactive organizations',
        showRoles: 'Show roles',
        state: 'State',
        statutoryName: 'Statutory name',
        street: 'Street',
        streetName: 'Street name',
        subscriber: 'Account holder',
        successful: 'Successful!',
        txcsId: 'Organization ID',
        type: 'Type',
        url: 'Website',
        vektisCompare: {
            allDataMatches: 'All data matches Vektis',
            copyDetails: 'Copy details',
            dataDoesntMatch: 'This data of AGB code {0} does not match Vektis',
            inactiveOrganizationError: 'The organization with AGB code {0} is inactive',
            invalidAgbCode: 'The AGB code {0} is invalid',
            noAgbProvided: 'There is no AGB code filled in for this organization. Fill this out first',
            noOrganizationFound: 'No organization found for AGB code {0}',
        },
        verification: 'Verification',
        viewOrganization: 'View organization',
        zip: 'Postcode',
    },
    organizationDataErrorType: {
        agbStartDateMismatch: 'Vektis start date does not match',
        duplicateAddress: 'Duplicate address',
        duplicateAgbCode: 'Duplicate AGB code',
        duplicateKvkNumber: 'Duplicate KVK number',
        duplicateNameStatutory: 'Duplicate statutory name',
        expiredAgb: 'AGB code expired',
        invalidAgb: 'AGB code invalid',
        missingAddress: 'Address missing',
        missingAgb: 'AGB code missing',
        missingKvkNumber: 'KVK number missing',
        missingNameStatutory: 'Statutory name missing',
        tooltips: {
            agbStartDateMismatch: 'The start date of the AGB code falls before the start date in Vektis',
            duplicateAddress:
                'The address of this organization matches one or more organizations. Establish a relationship between the organizations or change the address.',
            duplicateAgbCode:
                'The AGB code of this organization matches one or more organizations. Establish a relationship between the organizations or change the AGB code.',
            duplicateKvkNumber:
                'The KVK number of this organization matches one or more organizations. Establish a relationship between the organizations or change the KVK number.',
            duplicateNameStatutory:
                'The statutory name of this organization matches one or more organizations. Establish a relationship between the organizations or change the statutory name.',
            expiredAgb: 'According to the AGB register, the AGB code has expired',
            invalidAgb: 'The AGB code was not found in the AGB register',
            missingAddress: 'The postcode and/or house number is missing',
            missingAgb: 'The AGB code is missing and is required for this type of organization',
            missingKvkNumber: 'The KVK number is missing and is required for this type of organization',
            missingNameStatutory: 'The statutory name is missing and is required for this type of organization',
            vektisAddressMismatch: 'The postcode and/or house number does not match with Vektis',
            vektisKvkNumberMismatch: 'The KVK number does not match with Vektis',
            vektisNameStatutoryMismatch: 'The statutory name does not match with Vektis',
        },
        vektisAddressMismatch: 'Vektis address does not match',
        vektisKvkNumberMismatch: 'Vektis KVK number does not match',
        vektisNameStatutoryMismatch: 'Vektis statutory name does not match',
    },
    organizationLogs: {
        effectiveDate: 'Effective date',
        linkToLogs: 'Show logbook changes',
        modalTitle: 'Logbook organization details changes',
        modificationDate: 'Date change',
        modificationType: 'Change Type',
        modifiedBy: 'Changed by',
        modifiedField: {
            agbCodes: 'AGB codes',
            identifierDeclaration: 'AGB code',
            kgoStatus: 'KGO status',
            kvkNumber: 'KVK number',
            nameStatutory: 'Statutory name',
            organizationName: 'Organization name',
            organizationType: 'Organization type',
        },
        newValue: 'New',
        oldValue: 'Old',
    },
    organizationRelationshipTypeEnum: {
        isBranchOf: 'Is branch of',
        isDuplicateOf: 'Is duplicate of',
        isDuplicatedBy: 'Is duplicated by',
        isHoedOf: 'Is HOED of',
        isParentOf: 'Is parent of',
        isPartOfHoed: 'Is part of HOED',
    },
    organizationStructureTypeEnum: {
        branch: 'Branch',
        hoed: 'HOED',
        organization: 'Organization',
    },
    organizationSwitchModal: {
        confirmSwitch: 'Are you certain you want to switch?',
        fromTo: 'You are leaving {0} and will continue in {1}.',
        leavingSelectedPage: 'With this you are also leaving the currently selected page.',
        leavingSelectedPatient: 'With this you are also leaving the currently selected patient.',
        switchOrganization: 'Switching organization',
    },
    organizationTypeEnum: {
        addictionCareCentre: 'Addiction Care Centre',
        audiologistCenter: 'Audiologist Center',
        centerForMedicalSpecialistCare: 'Center for medical specialist care',
        dentistPractice: 'Dentist Practice',
        dietitianPractice: 'Dietitian practice',
        ggzInstitution: 'GGZ institution',
        gpPractice: 'GP Practice',
        gpWeekendCentre: 'GP Weekend Centre',
        hearingAidCenter: 'Hearing aid center',
        hospital: 'Hospital',
        independentPractice: 'Independent practice',
        ksyosHospital: 'Ksyos Hospital',
        medicalDiagnosticCenterLaboratory: 'Medical Diagnostic Center (Laboratory)',
        medicalSpecialistCompany: 'Medical Specialist Company',
        nursingFacility: 'Nursing Facility',
        optometryPractice: 'Optometry practice',
        other: 'Other',
        pharmacy: 'Pharmacy',
        physicalTherapyPractice: 'Physical therapy practice',
        psychiatryCenter: 'Psychiatry practice',
        psychologyPracticePsychotherapyPractice: 'Psychology Practice / Psychotherapy Practice',
        skinTherapyPractice: 'Skin Therapy Practice',
        speechTherapyPractice: 'Speech therapy practice',
        testOrganization: 'Test organization',
    },
    orthOProcedure: {
        complaints_and_or_reason_of_visit: 'Complaints and or reason of visit',
        convergencetest: 'Convergencetest',
        convert_to_first_ortho: 'Convert to first examination',
        convert_to_subsequent_ortho: 'Convert to follow-up research',
        dynamic_skiascopy: 'Dynamic skiascopy',
        family_history: 'Family history',
        four_dot_test: 'Four Dot-test',
        general_health_and_medication: 'General health and medication',
        general_observation_face: 'General facial observations',
        glasses_bagolini: 'Glasses of Bagolini',
        light_reflex: 'Light reflex',
        no_previous_ortho:
            'The patient has had no first Orthoptic examination in the past 6 months. You can ignore this message when the first Orthoptic examination was done outside of Ksyos.',
        ocular_history: 'Ocular history',
        previous_ortho: 'An Orthoptic examination has already started in the last 6 months',
        prismatest_15: '15^ prismatest',
        pupil_reactions: 'Pupil reactions',
        referrer_other: 'Other referrer',
        reverse_prismatest: 'Reverse prismatest',
        stereotest: 'Stereotest',
    },
    orthOVerwijzerEnum: {
        consultationBureau: 'Consultation bureau',
        generalPractitioner: 'General practitioner',
        optician: 'Optician',
        optometrist: 'Optometrist',
        other: 'Other',
        ownInitiative: 'By patient initiative',
        schoolDoctor: 'School doctor',
    },
    osas: {
        MRA: 'MRA',
        PAP: 'PAP',
        SPT: 'SPT',
        alternativeAddressCheckboxText: 'Send to alternative address',
        deliveryAddressRecorder: 'Delivery address recorder',
        ess: 'ESS',
        firstConsultSaved: 'The first consultation has been saved.',
        fosq10: 'FOSQ-10',
        giveToThePatient: 'Give the sleep recorder itself to the patient',
        giveToThePatientAdminDetails: 'Given by practice',
        giveToThePatientKsyos: 'No, I will give recorder to patient',
        giveToThePatientKsyosAdminDetails: 'Given by practice',
        handoverSleepRecorder: 'The first consultation has been saved. Give your patient the sleep recorder.',
        hsdq: 'HSDQ',
        lifestyle: 'Lifestyle',
        nightowl: '(i.c.m. een app op de smartphone van de patiënt)',
        patientHasSmartphone: 'Patient has a smartphone',
        questionnairesSentTo: 'Questionnaires sent to',
        reasonChanged: 'Reason for change',
        sendByKsyos: 'Yes, send recorder to patient',
        sendByKsyosAdminDetails: 'Sent by Ksyos',
        sendByNsi: 'Send the sleep recorder to the patient by the NSI',
        sendSleepRecorder: 'Send sleep recorder',
        sleepRecorderType: 'Sleep recorder type',
        startOsasc2: 'Start OSASC2',
        watchPatGetSerialNumberError: 'Could not be retrieved from CloudPAT',
        watchPatSerialNumberNotFilledIn: 'Niet ingevuld',
        watchpat: '(geen app of smartphone nodig)',
    },
    osasNotitie: {
        filesLabel: 'File(s)',
        informGeneralPractitionerLabel: 'Inform the General Practitioner',
        informHeader: 'Inform',
        noteTextLabel: 'Note',
        noteTextPlaceholder: 'Give the note a description',
        noteTitleLabel: 'Note title',
        noteTitlePlaceholder: 'Give the note a short title...',
    },
    osasPermission: {
        cantCreateOsasvcOsasc2Missing:
            'The follow-up consultation cannot be started because the second outpatient clinic examination has not been closed or is cancelled for this patient.',
        cantCreateOsasvcOsasoMissing:
            'The follow-up consultation cannot be started because the sleep examination is not closed or has been cancelled for this patient.',
        patientPermission: 'The patient gives permission for the referral',
        patientPermissionExtraDetails_1: 'With patient we mean patient or authorized person.',
        patientPermissionExtraDetails_2: 'The healthcare costs are included in the full own risk',
        patientPermissionExtraDetails_2_fat: 'Own risk:',
        patientPermissionExtraDetails_3:
            'The patient will receive an invoice for the healthcare costs. The costs can be declared by the insurer. A possible personal contribution will be cancelled.',
        patientPermissionExtraDetails_3_fat: 'Invoice:',
        patientPermissionExtraDetails_4:
            'Relevant medical data will be shared with the involved practitioners and organisations.',
        patientPermissionExtraDetails_4_fat: 'Data:',
        patientPermissionExtraDetails_5:
            'Email and/or phone number will be shared with the involved practitioners and organisations. These will be used to make an appointment, send an invoice and additional information about this service.',
        patientPermissionExtraDetails_5_fat: 'Contact data:',
        patientQuestionnaire:
            'The patient would like to help with improving healthcare and will receive a questionnaire by email.',
        patientQuestionnaireExtraDetails_1: 'Experiences of patients help us improve our services.',
        patientQuestionnaireExtraDetails_2: 'The patient receives a one-time questionnaire by email.',
        referralPermission: 'Permission for the referral',
    },
    osasQuestionnaires: {
        absolutely: 'Absolutely',
        afterLunch: 'Sitting down after lunch without alcohol',
        annoyingItchy: 'When I lie down in bed I have an annoying tickling, burning or itching sensation in my legs',
        concerned: 'I am worried about the consequences of my poor sleep (for example, on my health)',
        confusedHalfAwake:
            'I regularly wake up confused in the middle of the night and then make sweeping movements. I remember almost nothing about it later',
        consequencesAfterBadNight:
            'Especially after a bad night, I suffer from one or more of these consequences during the day: tiredness, sleepiness, bad mood, poor concentration, memory problems, lack of energy',
        difficultyBeingActiveAtNight: 'Activity in the evening',
        difficultyBeingActiveInTheMorning: 'Activity in the morning',
        difficultyConcentratingOnWork: 'Focus',
        difficultyDrivingAVehicleLongDistances: 'Long-distance driving',
        difficultyDrivingAVehicleShortDistances: 'Short-distance driving',
        difficultyRememberingThings: 'Remember',
        difficultyVisitingFriendsAndFamily: 'Receive a visit',
        difficultyWakingUp:
            'I only fall asleep towards the morning and then have great difficulty waking up in the morning. At the weekend I sleep late',
        difficultyWatchingVideoOrFilm: 'Watching movies',
        dryMouth: 'I wake up in the morning with a dry mouth',
        fallAsleepRepeatedly: 'During the course of the day I repeatedly fall asleep',
        fatigueDuringTheDay: 'I suffer from tiredness during the day',
        functionLessWell: 'Because I get too little sleep, I function less well during the day',
        haveRelationshipsDeteriorated: 'Influencing relationships',
        iDontFeelRestedAfterNap: "I don't feel rested after a daytime nap",
        iDontGetEnoughSleep: "I don't get enough sleep, despite having plenty of opportunity for a long night's sleep",
        inAPublicEnvironment: 'Sitting in a public establishment',
        kickingLegMovements: 'I have kicking leg movements in my sleep that I cannot suppress',
        lieAwake: 'I lie awake at night for a long time',
        moreThanTenHours:
            'I usually sleep more than 10 hours at night, have trouble waking up in the morning and also take naps during the day',
        moveLegs: "While I'm asleep I move my legs or arms",
        needForIntimacy: 'Intimacy',
        nightmares: 'I suffer from nightmares or anxious dreams',
        normalTime:
            "I sleep badly because I can't manage to go to sleep at a normal time and wake up at a normal time in the morning",
        notAtAll: 'Not at all',
        poorSleep: "The quality of my sleep is poor and I don't feel rested in the morning",
        questionnaireReport: 'Questionnaires export',
        restingInTheAfternoon: 'During lying rest in the afternoon',
        restlessLegs: 'If I have "restless legs", I can suppress this feeling by walking or stretching my legs',
        screaming: 'I wake up suddenly at night, screaming and/or sweating profusely and make a frightened impression',
        sittingInACarAsPassenger: 'As a passenger in the car',
        sleepAttacks: "I get sleep attacks during the day that are so intense I can't resist them",
        sleepBadlyDuringDay: 'If I have to stay awake at night, I sleep badly during the day',
        sleepWalking: 'I suffer from sleepwalking',
        snoreLoudly: 'I snore loudly during sleep',
        sometimes: ' Sometimes yes, sometimes not',
        sometimesInjured: 'I have injured myself while sleeping, without remembering it afterwards',
        stopBreathing: 'I stop breathing during sleep',
        suffocation: "I wake up at night feeling like I'm suffocating",
        talkingToSomeone: 'Sitting and talking to someone',
        timeVaries: 'My time of falling asleep varies greatly from day to day',
        troublesFallingAsleep: 'I have trouble falling asleep at night',
        unintentionallyFallAsleepDuringDay:
            'I fall asleep unintentionally during the day, especially in a boring situation (for example, a boring TV programme)',
        urgeToMoveLegs: 'When I sit quietly, especially in the evening, I feel the urge to move my legs',
        usuallyNot: 'Usually not',
        usuallyYes: 'Usually yes',
        vividDream: 'I regularly have a vivid dream in which I am attacked and then defend myself with wild movements',
        waitingInTheCarInTraffic: 'In the car in a traffic jam',
        whenReading: 'While sitting and reading',
        whenWatchingTv: 'While watching television',
    },
    outclinicDashboard: {
        editSnooze: 'Edit snooze date',
        hideNonAlarms: `Hide PIDs without alarm`,
        hideSnoozed: `Hide snoozed PIDs`,
        late: 'Late',
        modalConfirmRemove: 'Are you sure that you want to remove the snooze?',
        modalDatepickerLabel: 'Snooze until date',
        modalTitle: 'Snooze for PID {0}',
        modalTitleEdit: 'Snoozed PID {0}',
        modalTitleRemove: 'Remove snooze for PID {0}',
        noResults: 'There are no processes available within your search criteria',
        notApplicable: 'not applicable',
        planned: 'Planned',
        removeSnooze: 'Remove snooze',
        snoozeProcess: 'Snooze proces',
        snoozedUntil: 'Snoozed until {0}',
        title: 'Dashboard {0}',
        toBePlanned: 'To be planned',
        toDo: 'To do',
    },
    outpatientClinic: {
        tasks: {
            allTasks: 'All tasks',
            assign: 'Assign',
            assignSelectedTasksTo: 'Assign selected {0} to',
            assignTo: 'Assign to',
            category: 'Category',
            categoryName: {
                consultants: 'Consultants',
                consults: 'Consults',
                inactivity: 'Inactivity',
                planning: 'Planning',
                referrals: 'Referrals',
                remaining: 'Unknown',
            },
            changesSuccessfullySaved: 'Changes successfully saved',
            editExpectedDateCompleted: 'Edit expected date completed',
            editPerformer: 'Edit performer',
            errorFetchingTasks: 'There was an error retrieving the tasks. Please try again later.',
            newExpectedDateCompleted: 'New expected date completed',
            plural: 'tasks',
            selectedAmountTasks: 'You selected {0} {1}',
            singular: 'task',
            taskManagement: 'Task management',
        },
    },
    patient: {
        add: 'Add patient',
        addBSN: 'Enter the SSN too',
        addBSNAndBirthDate: 'We can find the patient with the SSN and birth date',
        addBirthDate: 'Enter the birth date too',
        addCareProvider: 'Add care provider',
        address: 'Address',
        age: 'Age',
        alwaysMakeReadable: 'Always make readable',
        birthDatePlaceholder: 'MM/DD/YYYY',
        birthDateSearch: 'Fill in the date of birth first',
        birthDateShort: 'Birth date',
        blurHeader: 'Nobody can read along',
        blurMessage: 'We have hidden your list of patients to protect the privacy of your patients',
        brp: 'Registration',
        careProvider: 'Care provider',
        careProvider_unknown: 'Care provider unknown',
        careProviderEdit: 'Care provider edit',
        careProviderOfPatient: 'Care provider of patient',
        careProviderOrganization: 'Care providing organization',
        careProviderPractitioner: 'Care providing practitioner',
        careProvidersTeam: 'Care providers team',
        createNew: 'No, add new patient',
        createNewPatient: 'Do you want to add a new patient?',
        createPatient: 'Create patient',
        creatingMedicalRecord: 'Creating medical record...',
        dateSearch: 'Date format: (DD-MM-YYYY)',
        deceasedDate: 'Deceased date',
        deliveryAddress: 'Delivery address',
        details: 'Patient details',
        detailsCounselling: 'Patient details',
        displayOwnPatientsOnly: 'Only display my own patients',
        downloadMedicalRecord: 'Download medical record',
        duplicateName:
            'An existing patient has the same name and birthdate. make changes to the information or open the existing dossier of the ',
        duplicateNameSearchBar:
            'An existing patient has the same name and birthdate. Open the existing dossier of the ',
        duplicateSocialSecurity:
            'An existing patient has the same social security number. Fill in another social security number or open the existing dossier of the ',
        duplicateSocialSecuritySearchBar:
            'An existing patient has the same social security number, but a different birthdate. Open the existing dossier of the ',
        editCareProvider: 'Edit care provider',
        editCareProvidersTeam: 'Edit care providers team',
        editPatient: 'Edit patient details',
        editPatientConsent: 'Edit access permissions',
        emailForContact: 'Email address for getting in contact',
        emergencyNumber: 'Emergency number',
        extraSearch: 'Additional search',
        fillInManually: 'Fill in manually',
        foundPatient: 'Patient found',
        foundPatients: 'Patients found',
        getNameAndInsurance: 'Registration- and insurancedata',
        goToPrivateMode: 'Go to privacy mode',
        healthInsurer: 'Health insurer',
        homePhoneNumber: 'Home number',
        houseLetterShort: 'Letter',
        houseNumberSuffixShort: 'Suffix',
        iHaveCheckedTheData: 'I have verified the data',
        identifierInsurance: 'Insurance',
        identifierSocialSecurity: 'SSN',
        identifierSocialSecurityLong: 'Social security number',
        insuranceOrAddressSearch: 'Continue searching via insurance number or address',
        insurancePolicyNumber: 'Insurance policy number',
        insurancePolicyNumberShort: 'Policynr.',
        isDeceased: 'Deceased',
        makeReadable: 'Make readable',
        maritalStatus: 'Marital status',
        mergeChoice: 'Use which value',
        mergeChoiceClientSafe: 'CS',
        mergeChoiceHIS: 'HIS',
        mergeCurrentValues: 'Current value in ClientSafe',
        mergeExplanation:
            'The patient that you imported from your HIS already existed in ClientSafe. However, the data coming from your HIS is different from the data that is stored in ClientSafe. Choose which data should be used for the relevant fields.',
        mergeField: 'Category',
        mergeNewHisValues: 'Value imported from HIS',
        mergeSSO: 'Merge data',
        moreDetails: 'More details',
        nameCareProvider: 'Name care provider',
        nameFamily2: 'Second name',
        nameInfix2: 'Second name infix',
        nameUsual: 'Familiar name',
        newPatient: 'New patient',
        newPatientBody: 'We will automatically fill in the data where possible',
        newPatientHeader: 'Register your patient',
        noBSN: 'SSN unknown?',
        noBSNPleaseCheckData: 'No SSN found. Please verify the data.',
        noMatchVecozoTryAgain: 'Combination not found. Try again or fill in patient details manually.',
        noResult:
            "We didn't find any patients based on this {0}. You can search on SSN and birthdate to create a patient.",
        noResultBirthdate: 'birthdate',
        noResultBsn: 'SSN',
        noResultName: 'name',
        noResultsBody: 'You can find this patient by searching on SSN and birthdate',
        noResultsHeader: 'No patients found within your organization.',
        noSearchResultPatientslist: "We didn't find any patients based on your search",
        noValidInsurance: 'No valid insurance',
        patient: 'Patient',
        patientEmailAddress: 'Email address patiënt',
        patientHomePhoneNumber: 'Home number patient',
        patientMobilePhoneNumber: 'Mobile number patient',
        patientName: 'Patient Name',
        patientPermissionCheckbox: 'I have permission from the patient to change the data',
        patientPhoneNumber: 'Phone number patient',
        patientSearchList: 'Search patient on SSN, birthdate, name...',
        patientSelectOrAdd: 'Did you mean this patient',
        patientUnconfirmed: 'Unconfirmed patients',
        patients: 'Patients',
        policyNumber: 'Policy number',
        practice: 'Practice',
        practiceData: 'Practice data',
        practiceEmergencyNumber: 'Emergency number practice',
        practiceName: 'Practice name',
        practiceNumber: 'Phone number practice',
        proceed: 'Yes, continue with this patient',
        recover: 'Recover',
        registerDeath: 'Register death',
        registerGP: 'Register with general practitioner',
        registerPatient: 'Register patient',
        results: 'Results',
        save: 'Save patient data',
        savePatientData: 'Save patient data',
        search_in_organization: 'Search only in my own organization',
        searchByPatientSocialSecurity: 'Search patient by SSN',
        searchName: 'Search with first and surname',
        searchPatient: 'Search patient',
        selectGP: 'Type and choose a general practitioner',
        unRegisterGP: 'Unregister',
        vecozo: 'Insurance details',
        whoIsCareProvider: 'Who is the care provider?',
        zip: 'Postcode',
    },
    patientAccess: {
        chooseNotification: 'Choose a notification...',
        notPossibleToAccess:
            "It is currently not possible to access any patient's carepath. Contact the Privacy Officer or Tech's teamlead.",
        notification: 'Notification',
        notificationDetails: 'Notification details',
        patientAccessLog: 'Patient access log',
        reason: 'Reason',
        reasonForAccess: 'Reason to view data of patient',
        reasonForAccessDefaultOptions: {
            checkRequest: 'Check request',
            failedExamination: 'Failed examination',
            internalImprovingService: 'Internal use - improving the service',
            patientmixUp: 'Patient mix-up',
            requestRhythmStripsRequestGP: 'Request for rhythm strips at the request of the GP',
            requestRhythmStripsRequestMedicalSpecialist:
                'Request for rhythm strips at the request of the medical specialist',
            uncertaintyResult: 'Uncertainty about the result',
            viewTechnicalNote: 'View technical note',
        },
    },
    patientDataRequest: {
        clientSafeCarepaths: 'Carepaths from ClientSafe',
        concernsGgz: 'concerns GGZ file',
        concernsNsi: 'concerns NSI file',
        mergeError: 'Merge failed',
        noFile: 'No file',
        notInPdf: 'Not in PDF',
        pdfClientSafe: 'PDF ClientSafe',
        pdfTXCS: 'PDF TxCS',
        present: 'Present',
        requestFile: 'Request file',
    },
    patientDataRequestStatusEnum: {
        open: 'Open ',
        processed: 'Processed',
    },
    patientInvoiceServicePrices: {
        addPricePeriod: 'Add price period',
        patientInvoicing: 'Patient invoicing',
    },
    patientSatisfactionQuestions: {
        doneBetter: 'What can be done better or differently?',
        enoughInformation: 'Did you get enough information from Ksyos about the sleep examination?',
        explanationClear: 'Was the explanation of the doctor about the sleep examination clear?',
        explanationUnderstandable: 'Was the explanation of the doctor understandable?',
        feltHelped: 'Did you feel well helped?',
        opportunityToAskQuestions: 'Were there enough opportunities to ask questions?',
        repeatDigitalExamination: 'Would you like to do a digital examination again, based on your experiences?',
        satisfiedWith: 'What are you satisfied with?',
        scoreFreeText: 'Explanation',
        scoreKsyosSleep: 'Which grade do you give Ksyos Slaap?',
        sleepExaminationFreeText: 'Explanation',
        videoConsultFreeText: 'Explanation',
    },
    patientSso: {
        birthDate: 'dd-mm-yyyy',
        birthDateLabel: 'input date of birth',
        findPatientNoBsn: 'Find patient data without SSN',
        gender: 'Select gender',
        houseNumber: 'Input house number',
        identifierSocialSecurity: 'Input social security identifier',
        insurancePolicyNumber: 'Input policy number',
        nameFamily: 'Input family name',
        nameInfix: 'Input infix',
        patientNotFound: 'We could not find the patient',
        patientNotFoundInvalid:
            'We could not find the patient, check whether the entered data is correct and try again.',
        patientNotFoundServiceDown: 'We could not verify all data. Fill in the fields below if known.',
        patientNotFoundServiceDownNoBsn:
            'We could not collect the patient data at this time. Fill in the fields below if known.',
        radioLabel_address: 'Postcode and house number',
        radioLabel_identifierSocialSecurity: 'SSN',
        radioLabel_insurance: 'Policy number',
        radioLabel_other: 'No known data, manualy create a patient',
        searchOption: 'Retrieve patientdata by',
        searchPatient: 'Search for the patient by populating the fields below',
        withoutBsnFindPatient: 'Even without SSN we can often collect patient data for you, fill in the fields below',
        zip: 'Input postcode',
    },
    payoutActivityType: {
        biometrician: 'Examination',
        grader: 'Grading',
        performer: 'Perform',
        requester: 'Request',
        specialist: 'Consultation',
        supervisor: 'Quality control',
    },
    payoutType: {
        biometrician: 'Biometrician',
        grader: 'Grader',
        performer: 'Performer',
        requester: 'Requester',
        specialist: 'Specialist',
        supervisor: 'Supervisor',
    },
    pdfTemplate: {
        attachment: {
            pdfEncryptedWarning: 'PDF is encrypted and cannot be displayed',
        },
    },
    personalSettings: {
        addBothToContinue: 'Add both to continue.',
        addIdentifier: 'Add {0}',
        addRights: 'Add rights to {0}',
        alertMissingServiceOrganizationSettings:
            'Settings for the new rights you added are missing on organization level. Add these settings here to add the new rights to the care provider.',
        carePathDetails: {
            activeSince: 'Active since',
            automaticallyAssigned: 'Automatically assigned',
            careGroup: 'Care group',
            carePath: 'Carepath',
            carePathDetails: 'Details carepath',
            dateOfRightRequest: 'Date of request',
            requestedByPractitioner: 'Requested by practitioner',
            rights: 'Rights',
            serviceRightObtained: 'Service right obtained',
        },
        contactDetails: 'Contact details',
        email: 'Email',
        emailAlt: 'Alternative email for notifications',
        emailNotifications: 'Email notifications',
        emergencyNumber: 'Emergency number',
        extraEdifacts: 'Extra EDIFACT messages',
        followUpMeasurementGgz: 'Default post-measurement',
        ggz: {
            availability: 'Availability',
            availabilityExplanation:
                'Configure when you are available to be called for phone consults. This way other care providers know when you are available.',
            consultation: 'GGZ consultation',
            examination: 'GGZ examination',
            phoneAvailability: 'Phone availability',
            phoneAvailabilityExplanationAsAccountManager: 'Add the contact hours of the practitioner',
        },
        notifications: {
            addAnotherWorkEmail:
                'You can still set a second work email. Would you like to receive notifications there?',
            addAnotherWorkEmailAccountManager:
                'A second work email can still be set. Would you like to receive notifications there?',
            consumer: {
                consultation: 'Consultation result ({0})',
                contact: 'Contact result ({0})',
                counseling: 'Counseling result ({0})',
                examination: 'Examination result ({0})',
                other: 'Result ({0})',
                referral: 'Rejected referral / Intake or discharge report available ({0})',
                referralSmall: 'Rejected referral small ({0})',
            },
            emailNotificationSettingAtOrganizationalLevel: 'here.',
            personalEmail: 'personal email',
            provider: {
                consultation: 'New consult question ({0})',
                contact: 'New contact request ({0})',
                counseling: 'New counseling request ({0})',
                examination: 'New examination request ({0})',
                other: 'New process ({0})',
                referral: 'New referral ({0})',
                referralSmall: 'New referral small ({0})',
            },
            receiveAt: 'Receive at',
            receiveOn: 'Receive on',
            sendOnDeviantResult: 'Only deviant results',
            sendOnNormalAndDeviantResult: 'Both deviant and normal results',
            setWorkEmail: 'Then enter it here.',
            setWorkEmailAccountManager: 'Then enter the work email here',
            settingsDeterminedByOrganization:
                'These settings are determined on organization level. To adjust these settings contact the Ksyos servicedesk at 020 600 0060.',
            settingsDeterminedByOrganizationManager: 'These settings are set at the organizational level.',
            settingsDeterminedByOrganizationManagerEdit:
                'These settings are set at the organizational level. You can change the settings ',
            supervisor: {
                examination: 'New supervision ({0})',
            },
            workEmail: 'work email',
            workEmailAlt: 'alternative work email',
            workEmailNotSet: 'You did not set a personal work email. Would you like to receive notifications there?',
            workEmailNotSetManager:
                'No personal work email has been set. Would you like to receive notifications there?',
        },
        organizationStamp: {
            addStamp: 'Add organization stamp',
            addStampExplanation:
                'You have not yet added an organization stamp. Do you have an organization stamp? Then add a picture of your organization stamp here.',
            formTitle: 'Practitioner organization stamp',
            isUploading: 'Organization stamp is uploading...',
            modalHeader: 'Add organization stamp to colleagues',
            modalText:
                "Some doctors within the organization don't have an organization stamp. Would you like to add this organization stamp to their personal settings as well?",
            modalYes: 'Yes, add organization stamp',
            save: 'Add organization stamp',
        },
        preferenceList: {
            addFavoriteOrganizations:
                'Are there certain organizations that you often refer a {0} to? Add your favorites to your referral list here.',
            addOrganizations: 'Add organizations',
            applicant: 'applicant',
            closeConfirmationModalMessage: 'If you exit all selected organizations will not be added to your list',
            closeConfirmationModalTitle: "Are you sure you don't want to add the selected organizations?",
            copyPreferencesFromColleagues: 'Copy preferences from colleagues',
            deletePreferenceListCancelMessage: 'Cancel',
            deletePreferenceListConfirmMessage: 'Delete',
            deletePreferenceListMessageFour: 'You are unable to save this service without a preference list.',
            deletePreferenceListMessageOne:
                'This service provider is able to select a service consumer. For this feature a preference list is necessary. ',
            deletePreferenceListMessageThree:
                'You configured that this service provider is able to select a service consumer. This requires a preference list with service consumers. ',
            deletePreferenceListMessageTwo:
                'If you delete the preference list, the service provider can no longer select any service consumers.',
            deletePreferenceListTitle: 'Delete preference list',
            nationWide: 'Nationwide network',
            preferenceList: 'Preference list',
            preferenceLists: 'Preference lists',
            searchNationWide: 'Search in nationwide network',
            searchOnCityZipCodeOrState: 'city, postcode or state...',
            searchOnNameOrOrganization: 'Search for name, or organization...',
            searchOnNameSpecializationOrOrganization: 'Search for name, specialization or organization...',
            searchOnSpecialistOrOrganization: 'Search for specialist or organization...',
            searchOnSpecializationOrOrganization: 'Search for specialization or organization...',
            searchOnSpecializationSpecialistOrOrganization: 'Search for specialization, specialist or organization...',
            searchPreferredAndNationWide: 'Search in preferred and nationwide lists',
            specialistProfession: 'Specialist profession',
            specialization: 'Specialization',
        },
        save: 'Save',
        selectSupervisor: 'Select supervisor',
        sendZorgMailOnExaminationRequest: 'Send on examination request',
        sendZorgMailOnReferralIntake: 'Send on referral intake',
        sendZorgMailOnReferralRequest: 'Send on referral',
        specializationsTooltip:
            'The specializations are shown in the preference list and the country wide network. When sending a request the consumer can select a provider by specialization.',
        subsequentServiceTooltip:
            'You can only start a subsequent service if a practitioner has rights for that service',
        yourZorgMail: 'Your ZorgMail',
        zorgMail: 'ZorgMail',
        zorgMailNumberExplanation:
            'With a ZorgMail number filled in, you will automatically receive an EDIFACT message for results of examinations, consults and at a discharge after referral.',
        zorgMailPlaceholder: 'Type ZorgMail number',
    },
    physicalExamination: {
        consultationPlace: 'This consultation takes place',
        physicalExamination: 'Physical examination',
        retrognathism: 'retrognathism',
        tongueSize: 'Tongue size',
        tonsilSize: 'Tonsil size',
    },
    practitioner: {
        add: 'Add practitioner',
        addExisting: 'Add existing practitioner',
        addMultiple: 'Add practitioners',
        addNew: 'Add new practitioner',
        addOrRemoveMultiple: 'Add or remove practitioners',
        adminSettings: 'admin settings',
        chooseAtLeastOneRole: 'Choose at least one role',
        chooseRole: 'Click and choose a role',
        confirmCancelAddKsyosEmployeeTitle: 'Are you sure you want to cancel adding an employee?',
        confirmCancelAddPractitionerTitle: 'Are you sure you want to cancel adding a practitioner?',
        confirmCancelMessage: 'When you close all changes will be lost.',
        consumingService: 'Consuming service',
        consumingServices: 'Consuming services',
        couldNotFindAnyone: 'We could not find anyone.',
        details: 'Practitioner details',
        dutch: 'Dutch',
        employment: 'Employment',
        english: 'English',
        expiresOn: 'Expires on',
        exportPractitioners: 'Export practitioners',
        findPractitioner: 'Find practitioner',
        hasDeclarableQualification: 'Care provider has declarable qualification',
        identifierDeclaration: 'AGB code',
        identifierHeader: 'BIG / KP & AGB',
        identifierRegistrationBig: 'BIG-code',
        identifierRegistrationKp: 'KP-number',
        inYourOrganization: 'in your organization',
        isActive: 'Actief',
        isManager: 'Ksyos manager',
        language: 'Select language',
        lastActivityDate: 'Date of last activity',
        lastLogin: 'Last login',
        nameOrAgbOrUid: 'Name, AGB code or Ksyos-number',
        nameOrId: 'Name or ID',
        namePractitioner: "Practitioner's name",
        noActiveCarePaths: 'This practitioner does not have any active carepaths',
        noEmployments:
            'Pay attention, the practitioner does not yet have an employment with an organization. The practitioner can not log in without one.',
        noSearchResultGeneralPractitioner: "We didn't find any general practitioners based on your search term.",
        password: 'Password',
        practitioner: 'Practitioner',
        practitionerDataErrorDetails: {
            clientSafeLastName: 'ClientSafe lastname',
            vektisLastName: 'Vektis lastname',
        },
        practitionerDataErrorType: {
            expiredAgb: 'AGB code expired',
            invalidAgb: 'AGB code invalid',
            missingAgb: 'AGB code missing',
            tooltips: {
                expiredAgb: 'According to the AGB register, the AGB code has expired',
                invalidAgb: 'The AGB code was not found in the AGB register',
                missingAgb: 'The AGB code is missing and is required for this type of practitioner',
                vektisNameMismatch: 'The last name of the practitioner does not match the name in the AGB register',
            },
            vektisNameMismatch: 'Vektis name mismatch',
        },
        practitionerEdit: 'Edit practitioner',
        practitionerInactiveWarning: 'This practitioner is inactive',
        practitioners: 'Practitioners',
        providingService: 'Providing service',
        providingServices: 'Providing services',
        registeredOn: 'Registered on',
        registration: 'Registration',
        registrations: 'Registrations',
        remove: 'Remove practitioner',
        repeatPassword: 'Repeat password',
        returnToOrganizationOverview: 'Return to organization overview',
        role: 'Select profession ...',
        save: 'Save Practitioner',
        searchAgainOrAddNew: 'Search again or add a new practitioner.',
        showInactivePractitioners: 'Show inactive practitioners',
        statusLanguage: 'Status and language',
        supervisingService: 'Supervising service',
        theRegistrationCodeIs: 'The registration code is',
        theRegistrationCodeIsExpired: 'The registration code has expired',
        txcsLogin: 'TxCS Login',
        uid: 'Ksyos-number',
        unregistered: 'Unregistered',
        withoutActiveEmployment: 'Without active employment',
    },
    practitionerListStatusBadges: {
        accountWithoutLogin: 'Account without login',
        activationEmailIsGoingToBeSentOn: 'Activation email is going to be sent on {0}',
        activationEmailWasSentOn: 'Activation email was sent on {0}',
        endedBy: 'Ended by {0}',
        noCarePathRights: 'No carepath rights',
        noZorgmail: 'No Zorgmail',
    },
    practitionerSso: {
        links: 'Links',
        noSsoAdmin: 'The practitioner has no active links.',
        noSsoPractitioner: 'You have no active links.',
        singleSignOn: 'Single sign-on links',
    },
    preferredServiceOrganizationProvider: {
        message_wrong: 'Organizations could not be added',
    },
    procedure: {
        aborted: 'Abort',
        attachments: 'Attachments',
        cancelProcedure: 'Cancel',
        confirmCancelProcedure: 'Are you sure you want to cancel this procedure?',
        customControlStatus: 'Service specific',
        downloadDiary: 'Download diary',
        downloadRecording: 'Download recording',
        editPeformer: 'Procedure edit biometrician',
        files: 'Files',
        finishedSupervision: 'Supervision finished',
        gradingSent: 'Grading sent to consumer',
        hideProcedure: 'Hide the examination',
        linkToExamination: 'Concerns the examination performed on',
        new: 'New examination',
        noServiceConsumerMessage:
            'GP ({0}) of patient is not a consumer of this service. For more information, contact Ksyos.',
        performer: 'Performer',
        procedure: 'Examination',
        saveAndSend: 'Save and send',
        saveDraft: 'Save as draft',
        sendExtraAttachments: 'Send extra attachments',
        sentToGrader: 'Sent to grader',
        showProcedure: 'Show the complete examination',
        status_aborted: 'Aborted',
        status_completed: 'Completed',
        sureToCancelAttachments: 'Do you want to cancel sending extra attachments? The attachments will not be saved.',
        useExamination: 'Pick research',
        useExaminationQuestion: 'Do you want to do this consultation on the basis of a previously received research?',
        userNoBiometrician: 'User is no a Biometrician and can not add a examination',
    },
    processActivity: {
        applyModification: 'Apply correction',
        cancel: {
            remove: 'Are you sure you want to remove {0}?',
            removeDefault: 'Are you sure you want to remove?',
            stopEditing: 'Are you sure you want to stop editing {0}?',
            stopEditingDefault: 'Are you sure you want to stop editing?',
            stopModifying: 'Are you sure you want to cancel correcting {0}?',
            stopModifyingDefault: 'Are you sure you want to cancel correcting?',
        },
        modify: 'Correct',
        modifyNoChangesWarning: 'Change at least one field to save a correction',
    },
    processActorProfession: {
        bariNurse: 'Nurse specialist',
        basicPsychologist: 'Psychologist',
        dietitian: 'Dietitian',
        lifestyleCoach: 'Lifestyle coach',
        medicalSpecialist: 'Medical specialist',
        osasNurse: 'OSAS nurse',
        outpatientAssistant: 'Outpatient assistant',
    },
    processDetails: {
        accepted: 'Accepted',
        analyst: 'Analyst',
        answeredAt: 'Answered',
        answeredBy: 'Answered by',
        attachments: 'Attachments',
        cancelEdits: 'Cancel changes',
        careGroup: 'Care group provider',
        checkUploadJson: 'Pay attention: check the ecgFile(s) and fill the size and totalEcgSize',
        closedAt: 'Closed',
        confirmationRerunStep: 'Are you sure you want to rerun this step?',
        consultBy: 'Question by',
        contactBy: 'Contact by',
        counselor: 'Counselor',
        coupledProcesses: 'Coupled processen',
        coupledRecorder: 'Coupled recorder',
        createdAt: 'Created',
        dateConfigured: 'Date configured',
        dateUploadFinished: 'Upload finished',
        dateUploadStarted: 'Upload started',
        diary: 'Diary',
        dischargeBy: 'Discharge by',
        dischargedAt: 'Discharged',
        doUpdateSteps: 'Do update steps',
        durationExamination: 'Duration examination',
        emailWarning: 'Message cannot be sent, there is no email address',
        examinedAt: 'To grader',
        examinedBy: 'Examination by',
        export: 'Export',
        failedSteps: 'Failed steps',
        financialStatus: 'Financial status',
        generateConfiguredByKsyos: 'Generate ConfiguredByKsyos.dat',
        generateUploadJson: 'Generate upload.json',
        gradedAt: 'Graded',
        gradedBy: 'Grading by',
        intakeAt: 'Intake report',
        intakeBy: 'Intake by',
        newUploadReport: 'New report uploaded',
        notAvailable: 'Not available',
        notYetAccepted: 'Not yet accepted',
        notYetAnswered: 'Not yet answered',
        notYetClosed: 'Not yet closed',
        notYetDischarged: 'Not yet discharged',
        notYetExamined: 'Not yet examined',
        notYetGraded: 'Not yet graded',
        notYetIntake: 'No intake report',
        notYetInvited: 'Not yet invited',
        notYetRequested: 'Not yet requested',
        notYetSecondRoundAnswer: 'Not yet answered',
        notYetSecondRoundQuestion: 'Not yet sent',
        notYetSent: 'Not yet sent',
        notYetStarted: 'Not yet started',
        notYetSupervised: 'Not yet supervised',
        paper: 'Paper',
        performed: 'Performed',
        performedByOrganization: 'Performed by organization',
        performedByPractitioner: 'Performed by employee',
        performedOn: 'Performed on',
        referredBy: 'Referred by',
        requestedAt: 'Requested',
        requestedBy: 'Request by',
        rerunStep: 'Rerun step',
        resend: 'Resend',
        resendSuccess: 'The messages are prepared for sending.',
        resendThirdPartyMessages: 'Resend third party messages',
        resendThirdPartyMessagesTitle: 'Resend messages to third parties',
        secondRoundAnswerAt: '2nd Round answered',
        secondRoundQuestionAt: '2nd Round',
        sendEdifact: 'Send EDIFACT',
        sentAt: 'Sent',
        showCurrent: 'Show current',
        showEdited: 'Show edited',
        stepName: 'Step name',
        steps: 'Steps',
        supervisedAt: 'Supervised',
        supervisedBy: 'Supervision by',
        technicalStatus: 'Look at technical status',
        therapielandIdFieldLabel: 'Program ID',
        thirdPartyMessages: 'Look at third party messages',
        unknown: 'Unknown',
        updateActiveToCanceled:
            'In case you want to cancel the process, use the cancel button above. This will ensure that the process is canceled cleanly.',
        updateConfirm: 'Are you sure you want to edit the status of the process?',
        updateOk: 'The process has been updated',
        updateWarning:
            'You will only update the status of the process. The process will be in an unclear situation. You are responsible for resolving that.',
        updatedAt: 'Last update',
        updatesDone: 'Updates successfully executed',
        updatesFailed: 'Updating failed',
        verifyUpdateSteps: 'Verify update steps',
        view: 'View',
        viewFinancialStatus: 'Look at financial status',
        zorgmailGeneralPractitioner: 'Zorgmail general practitioner',
    },
    processSearchField: {
        automaticRight: 'Right automatically assigned',
        groupName: 'Group name',
        identifierSocialSecurity: 'Patient (SSN)',
        organization: 'Organization (Name/ID)',
        organizationName: 'Organization name',
        patientBirthdate: 'Patient (Birthdate)',
        patientName: 'Patient (Name)',
        practitioner: 'Involved practitioner (Name/ID)',
        processId: 'Process ID (ClientSafe)',
        processNumber: 'Process number (ClientSafe)',
        relationNumber: 'Relation number',
        requesterPractitionerUid: 'Requester (ID)',
        txcsProcesId: 'Process ID (TxCS)',
    },
    processThirdPartyMessages: {
        cancelled: 'Cancelled',
        failed: 'Failed',
        finishedDateTime: 'Finished at',
        firstScheduledDateTime: 'Created at',
        messageSubtype: 'Subtype',
        messageType: 'Message type ',
        nextScheduledDateTime: 'Send at',
        numberOfFailures: 'Number of failures',
        patientName: 'Name Patient',
        paused: 'Paused',
        process: 'Process',
        recipient: 'Recipient',
        results: 'Results',
        retrying: 'Retrying',
        scheduled: 'Will be sent',
        sending: 'Being sent',
        sent: 'Sent',
        status: 'status',
        viewResults: 'View results',
    },
    processThirdPartyMessagesEnum: {
        cancelled: 'Cancelled',
        failed: 'Failed',
        paused: 'Paused',
        retrying: 'Retrying',
        scheduled: 'Will be sent',
        sending: 'Being sent',
        sent: 'Sent',
    },
    processType: {
        consultationRequest: 'this consultation',
        contact: 'this contact',
        counseling: 'this counseling',
        diagnosticReport: 'this grading',
        examinationRequest: 'this request',
        procedure: 'this examination',
        referral: 'this referral',
        referralSmall: 'this referral',
        supervision: 'this supervision',
        supervisionBiometricsSummary: 'this supervision',
    },
    professionEnum: {
        addictionSpecialist: 'Addiction specialist',
        assistant: 'Assistant',
        audiologist: 'Audiologist',
        bariNurse: 'Bariatrics specialist nurse',
        basicPractitioner: 'Basic practitioner',
        basicPsychologist: 'Basic psychologist',
        clinicalPsychologist: 'Clinical psychologist',
        cognitiveBehavioralTherapist: 'Cognitive behavioral therapist',
        contactLensSpecialist: 'Contact lens specialist',
        dentist: 'Dentist',
        dietitian: 'Dietitian',
        emdrTherapist: 'EMDR therapist',
        exerciseTherapist: 'Exercise therapist',
        functionAssistant: 'Function assistant',
        generalPractitioner: 'General Practitioner',
        generalPractitionerAios: 'General Practitioner (AIOS)',
        generalPractitionerHidha: 'General Practitioner (HIDHA)',
        generalPractitionerMentalHealthSpecialist: 'General Practitioner - GGZ specialist',
        generalPractitionerWaarnemer: 'General Practitioner (waarnemer)',
        gzPsychologist: 'GZ-Psychologist',
        haptotherapist: 'Haptotherapist',
        hearingCareProfessional: 'Hearing care professional',
        hospitalSecretarialAssistant: 'Hospital secretarial assistant',
        imhSpecialist: 'IMH specialist',
        laborant: 'Laborant',
        lifestyleCoach: 'Lifestyle coach',
        lossMentor: 'Loss mentor',
        medicalSpecialist: 'Medical Specialist',
        nurse: 'Nurse',
        nurseConsultant: 'Nurse consultant',
        nursePractitioner: 'Nurse practitioner',
        nursePractitionerMentalHealth: 'Nurse practitioner - mental health',
        nursePractitionerSomatic: 'Nurse practitioner - Somatic',
        nurseSpecialist: 'Nurse specialist',
        obstetrician: 'Obstetrician',
        occupationalTherapist: 'Occupational therapist',
        optician: 'Optician',
        optometrist: 'Optometrist',
        optometristIo: 'Optometrist i.o.',
        orthoptist: 'Orthoptist',
        osasNurse: 'OSAS nurse',
        other: 'Other',
        outpatientAssistant: 'Outpatient Assistant',
        pharmacist: 'Pharmacist',
        phdStudent: 'PhD-student',
        physicianAssistant: 'Physician Assistant',
        physicianAssistantOphthalmology: 'Physician assistant ophthalmology',
        physiotherapist: 'Physiotherapist',
        podiatrist: 'Podiatrist',
        practiceManager: 'Practice Manager',
        psychologistSecretarialAssistant: 'Psychologist secretarial assistant',
        psychosocialHypnotherapist: 'Psychosocial Hypnotherapist',
        psychotherapist: 'Psychotherapist',
        relationshipAndFamilyTherapist: 'Relationship and family therapist',
        remedialEducationalist: 'Remedial educationalist',
        resident: 'Resident',
        sexologist: 'Sexologist',
        skinTherapist: 'Skin Therapist',
        socialPedagogicalCareProvider: 'Social pedagogical care provider (SPH)',
        socialPsychiatricNurses: 'Social psychiatric nurse',
        socialWorker: 'Social worker',
        specialistGeriatricMedicine: 'Specialist geriatric medicine',
        speechTherapist: 'Speech Therapist',
        systemTherapist: 'System therapist',
        technicalOphthalmicAssistant: 'Technical ophthalmic assistant (TOA)',
        teenageCoach: 'Teenage coach',
        visualTherapist: 'Visual therapist',
    },
    professionEnumPlural: {
        addictionSpecialist: 'Addiction specialists',
        assistant: 'Assistants',
        bariNurse: 'Bariatrics specialist nurses',
        basicPractitioner: 'Basic practitioners',
        basicPsychologist: 'Basic psychologists',
        clinicalPsychologist: 'Clinical psychologists',
        cognitiveBehavioralTherapist: 'Cognitive behavioral therapists',
        contactLensSpecialist: 'Contact lens specialists',
        dentist: 'Dentists',
        dietitian: 'Dietitians',
        emdrTherapist: 'EMDR therapists',
        exerciseTherapist: 'Exercise therapists',
        functionAssistant: 'Function assistants',
        generalPractitioner: 'General Practitioners',
        generalPractitionerAios: 'General Practitioners (AIOS)',
        generalPractitionerHidha: 'General Practitioners (HIDHA)',
        generalPractitionerMentalHealthSpecialist: 'General Practitioners - GGZ specialists',
        generalPractitionerWaarnemer: 'General Practitioners (waarnemer)',
        gzPsychologist: 'GZ-Psychologists',
        haptotherapist: 'Haptotherapists',
        hearingCareProfessional: 'Hearing care professionals',
        hospitalSecretarialAssistant: 'Hospital secretarial assistants',
        imhSpecialist: 'IMH specialists',
        laborant: 'Laborants',
        lifestyleCoach: 'Lifestyle coaches',
        lossMentor: 'Loss mentors',
        medicalSpecialist: 'Medical Specialists',
        nurse: 'Nurses',
        nurseConsultant: 'Nurse consultants',
        nursePractitioner: 'Nurse practitioners',
        nursePractitionerMentalHealth: 'Nurse practitioners - mental health',
        nursePractitionerSomatic: 'Nurse practitioners - Somatic',
        nurseSpecialist: 'Nurse specialists',
        obstetrician: 'Obstetricians',
        occupationalTherapist: 'Occupational therapists',
        optician: 'Opticians',
        optometrist: 'Optometrists',
        optometristIo: 'Optometrists i.o.',
        orthoptist: 'Orthoptists',
        osasNurse: 'OSAS nurses',
        other: 'Other',
        outpatientAssistant: 'Outpatient Assistants',
        pharmacist: 'Pharmacists',
        phdStudent: 'PhD-students',
        physicianAssistant: 'Physician Assistants',
        physicianAssistantOphthalmology: 'Physician assistants ophthalmology',
        physiotherapist: 'Physiotherapists',
        podiatrist: 'Podiatrists',
        practiceManager: 'Practice Managers',
        psychologistSecretarialAssistant: 'Psychologist secretarial assistants',
        psychosocialHypnotherapist: 'Psychosocial Hypnotherapists',
        psychotherapist: 'Psychotherapists',
        relationshipAndFamilyTherapist: 'Relationship and family therapists',
        remedialEducationalist: 'Remedial educationalists',
        resident: 'Residents',
        sexologist: 'Sexologists',
        skinTherapist: 'Skin Therapists',
        socialPedagogicalCareProvider: 'Social pedagogical care providers (SPH)',
        socialPsychiatricNurses: 'Social psychiatric nurses',
        socialWorker: 'Social workers',
        specialistGeriatricMedicine: 'Specialists geriatric medicine',
        speechTherapist: 'Speech Therapists',
        systemTherapist: 'System therapists',
        technicalOphthalmicAssistant: 'Technical ophthalmic assistants (TOA)',
        teenageCoach: 'Teenage coaches',
        visualTherapist: 'Visual therapists',
    },
    professionalSummary: {
        all: 'All',
        allergies: 'allergies',
        allergiesAdd: 'Add allergies',
        allergiesRead: 'Allergies',
        current: 'Current',
        diagnosticDeterminations: 'diagnostic determinations',
        diagnosticDeterminationsAdd: 'Add diagnostic determinations',
        diagnosticDeterminationsRead: 'Lab results',
        episode: 'episode',
        episodesAndJournals: 'episodes and journal lines',
        episodesAndJournalsAdd: 'Add episodes and journal lines',
        episodesAndJournalsRead: 'Episodes and journal lines',
        loadError:
            'The {0} can not be loaded. Please contact the service desk on 020 - 600 00 60 so we can help you further.',
        loading: 'Professional summary is being retrieved...',
        medication: 'medications',
        medicationAdd: 'Add medications',
        medicationRead: 'Medications',
        noAllergiesFound: 'No allergies were found',
        noEpisodesFound: 'No episodes were found',
        noLabResultsFound: 'No lab results were found',
        none: 'None',
        professionalSummary: 'Professional summary',
        selectionCount: '({0} selected)',
    },
    profile: {
        absence: 'Absence',
        absentFrom: 'Absent from',
        account: 'Account',
        accountActivated: 'Account activated',
        accountDetails: 'Account details',
        accountStatusNotActivated: 'Account not activated',
        accountStatusTerminated: 'No valid employment records',
        activationEmailResentTo: 'The activation email has been resent to {0}',
        activationEmailSent: 'Activation email sent',
        activationEmailSentTo: 'The activation email will be sent to',
        activationEmailUpdated: 'The activation email is succesfully updated and will be sent on {0}',
        addPeriod: 'Add period',
        addProvider: 'Add a provider',
        archive: 'Archive',
        areYouSure: 'Please confirm your choice',
        btnChangePassword: 'Change password',
        cannotUndoDeactivation: 'Attention: After the end date has passed, the deactivation can no longer be undone',
        changeAccountDetails: 'Changing account details',
        changePassword: 'Change password',
        changePassword2: 'Change password',
        changePasswordCurrentCredentialsText: 'First enter your current password and Google Authenticator code.',
        changePasswordNewPasswordText:
            'Now pick a new password. A good password is hard to guess for both humans and computers. Avoid common words. You can for example choose a few uncommon words and combine them with a symbol of your choice. A score bar will appear underneath that estimates how good your chosen password is. The minimal required score is 80%.',
        closeRequestMessage:
            'Requests such as a survey or a consultation you submitted may be automatically terminated once they have been completed without explicit approval by you. You can specify which types of requests can be closed immediately.',
        closeRequests: 'Close requests',
        closeRequestsAuto: 'Close request automatically',
        currentPassword: 'Current password',
        deactivate: 'Deactivate',
        defaultOrganization: 'Default organization',
        defaultOrganizations: 'Default organizations',
        defaultProviders: 'Default providers',
        disableMfaTemporarily: 'Temporarily disable 2-step verification for 72 hours',
        emailsNotMatch: 'The provided email addresses do not match',
        employmentPeriod: 'Employment(s)',
        errorPasswordNotCorrect: 'Incorrect password',
        errorPasswordsNotEqual: 'Passwords are not equal',
        explainDeactivate: 'The care provider cannot use the account when you confirm the deactivation.',
        generalSettings: 'General settings',
        holidayAbsence:
            'Are you going on a holiday or will you not be available for another reason? Please make it known, so other people can see when you are available.',
        isEmailAddressCorrect: "Is this email address correct? It can be changed in the practitioner's profile.",
        ksyosAccount: 'Ksyos account',
        lastActivationEmailSentAt:
            'This practitioner is not yet active with Ksyos. The last activation email was sent at {0}.',
        myAccount: 'My account',
        newPassword: 'New password',
        newPasswordRepeat: 'Repeat new password',
        noConsumer: 'There are no default consumers saved for the service "{0}".',
        noProvider:
            'There are no default providers saved for the service "{0}". If a request is made for this service, one can choose from all available providers.',
        notificationSaved: 'Notification settings saved',
        passwordChanged: 'Password is changed',
        practitionerNotActive: 'This practitioner is not yet active with Ksyos.',
        practitionerShouldLogin: 'Will the healthcare provider actively use Clientsafe?',
        practitionerShouldLoginText:
            'When the caregiver gets access to one or more care paths, an activation email will be sent and the caregiver can activate their own account. When the account is activated the healthcare provider can log in and use the services.',
        profilePhoto: 'Profile photo',
        provider: 'Provider',
        recommended: 'Recommended',
        resendActivationEmail: 'Resend activation email',
        security: 'Security',
        sendToEmployment: 'Notification to employment',
        sendToOrganization: 'Notification to organization',
        sendToPrimary: 'Notification to primary email',
        stop: 'Make inactive',
        twoStepVerification: 'Two-step verification',
        verificationApp: 'Verification app',
        viewProfile: 'View profile',
    },
    radiologyPatientTaskType: {
        remaining: 'Other',
        remainingOther: 'Other',
    },
    readers: {
        explanation:
            'With reading rights, practitioners can see all care paths of all active services (of an organization).',
        readingService: 'Reading service',
        readingServices: 'Reading services',
    },
    referral: {
        change: 'Change',
        clickToAddOrganizationStamp: 'Click here to save this referral and add a new organization stamp',
        downloadPatientInformation: 'Download and print PDF',
        gpHasNoOrganizationStamp:
            'You want to refer this patient on behalf of {0}. You still need an organization stamp to be able to make the referral.',
        medicalData: 'Medical data',
        patientInformation: 'Information for your patient',
        referral: 'Referral',
        referralAccepted: 'You have accepted the referral',
        referredTo: 'Referred to',
        referrer: 'Referrer',
        sendViaEmail: 'Send via email',
        toRefer: 'Refer',
        youHaveNoOrganizationStamp: "We have noticed that you don't have an organization stamp available in Ksyos",
    },
    request: {
        abortProcess: 'Abort process',
        abortProcessAdmin: 'Are you sure you want to cancel process with ID {0} from patient {1}?',
        abortProcessConfirmation: 'Reason for aborting process',
        abortProcessMessage: 'Abort confirmation message',
        abortProcessesAdmin:
            'The examination that you are trying to cancel has a succeeding consultation. Are you sure to cancel the examination (PID: {0}) and the consultation (PID: {1})?',
        absentUpToAndIncluding: 'Absent up to and including',
        action_acceptAnalysis: 'Accept analysis',
        action_addConsultReport: 'Answer consult',
        action_addDischargeReport: 'Discharge report',
        action_addExaminationReport: 'Add report',
        action_addIntakeReport: 'Intake report',
        action_addProcedure: 'Start examination',
        action_changeEmailAddressInvitations: 'Change email address',
        action_changeProcedurePerformer: 'Edit biometrician',
        action_changeRecipient: 'Assign to other employee',
        action_evaluateConsult: 'Evaluate consult',
        action_getAccessToCounseling: 'Get access',
        action_openConsultReport: 'Consult report',
        action_openDischargeReport: 'Discharge report',
        action_openExaminationReport: 'Open report',
        action_openIntakeReport: 'Intake report',
        action_openProcedure: 'Open examination',
        action_openReport: 'Open report',
        action_takeAnalysisFromColleague: 'Take analysis from colleague',
        activityDetails: 'Activity details',
        addedFiles: 'Added files',
        allProcesses: 'All processes',
        alreadyReprocessing: 'Request {0} was already on the list to be reprocessed.',
        alreadyScheduledQuestionnaires:
            'There is already a questionnaire scheduled for this patient ({0}). If you proceed, the questionnaires that were scheduled will be cancelled.',
        askQuestionToSpecialist: 'Ask a question to the medical specialist',
        assign: 'Assign',
        assignOther: 'Assign to other care provider',
        assignTo: 'Assign to',
        attachment: 'Attachment',
        autoRights: '(Right has been automatically assigned)',
        cancelledRequest: 'Cancelled request',
        cancelledRequestAfterSendingMail: 'This request was cancelled after we sent you the notification mail.',
        cannotAbortProcessesAdmin:
            'Proces {0} of patient {1} cannot be cancelled. It is dependent on the following processes: {2}. First cancel these processes.',
        careProviderIsAlsoRequester: 'Is the care provider also the requester?',
        carryOnProcess: 'Carry on with process',
        change: 'Change',
        changeConsultType: 'Change consulttype',
        checkCareProvider: 'Check whether the correct general practitioner has been selected',
        checkGeneralPractitioner: "Check general practitioner's organization",
        chooseGeneralPractitioner: 'Choose general practitioner',
        chooseOrganization: 'Choose organization',
        choosePractitionerOrOrganizationOrCity: 'Search practitioner, organization or place',
        chooseSearchOrganization: 'Start typing and search, or select from the list...',
        closeProcess: 'Close process',
        closedByConsumer: 'Closed by consumer',
        closedRequests: 'Closed requests',
        colleagueRequests: 'At colleague',
        confirmCancelContact: 'Are you sure you want to cancel this contact?',
        confirmCancelCounseling: 'Are you sure you want to cancel this counseling?',
        confirmCancelReferral: 'Are you sure you want to cancel this referral?',
        confirmCancelRequest: 'Are you sure you want to cancel this request?',
        confirmCancelSupervision: 'Are you sure you want to cancel this supervision?',
        confirmCloseProcess: 'Are you sure to close {0} process(es)?',
        confirmabortProcess: 'Confirm abort process',
        consult: 'Consult',
        consultationResults: 'Result',
        contactAdmin: 'Contact the administration for the necessary corrections.',
        contactWithRequesterOptionsHeader: 'Contact with the specialist',
        contactWithRequesterOptionsLabel: 'Contact possibilities',
        contactWithRequesterOptionsPlaceholder: 'Provide how and when the specialist can reach you with any questions.',
        contactWithRequesterOptionsRequestedHeader: 'Contact with the requester',
        counselingReports_notImplemented: 'Counseling reports not implemented',
        createConsult: 'Create a consult',
        date: 'Date',
        dateContact: 'Contact date',
        dateSent: 'Sent',
        declarations: 'declaration records are created in run',
        deleteContactModalText: 'Choose Yes if you want to delete the contact. Choose No if you want to keep it.',
        deleteContactModalTitle: 'Are you sure you want to remove the contact?',
        description: 'Description',
        diagnostic: 'Diagnostic request',
        directProcedureAdded: 'Procedure started without request',
        draftReferral: 'New referral',
        editRecipient: 'Change recipient',
        editRequest: 'Edit request',
        emptyClosedRequestsWorklist: 'You have no finished tasks yet',
        emptyColleagueRequestsWorklist: 'Your colleagues have no pending tasks',
        emptyMyRequestsWorklist: 'You have no new tasks',
        emptyOngoingRequestsWorklist: 'You have no pending tasks',
        endHours: 'End hours',
        endMinutes: 'End minutes',
        endTime: 'End time',
        examinationResults: 'Examination results',
        exportedRecords: 'For this aborted process (PID: {0})',
        feedbackProcessesClosed: '{0} proces(ses) are succesfully closed.',
        feedbackProcessesNotClosed: '{0} proces(ses) are not closed due to an incorrect process status.',
        files: 'Files',
        fillLabValues: 'Fill in the lab values...',
        filterStatus: 'Filter on status:',
        fundingError: 'Funding error',
        fundingErrorInformation: 'Funding Error Information',
        fundingErrorInformationAbbreviation: 'Funding error info',
        fundingSetTo: 'Funding set to',
        goTo: 'Go to',
        headerMeasurement: 'Measurement',
        hideConsult: 'Hide the consult',
        hideContact: 'Hide the contact',
        hideCounseling: 'Hide the counseling',
        hideDetails: 'Hide the details',
        hideReferral: 'Hide the referral',
        hideRequest: 'Hide the request',
        history: 'History',
        indication: 'Indication / question',
        invitationSendTo: 'Invitation send to',
        invoiceDeclSurpressed: 'Invoice and decl. surpressed',
        invoices: 'invoice records are created in run',
        labResearchHeader: 'Laboratory research',
        lastUpdated: 'Updated',
        mostRecentTasks: 'Most recent open tasks',
        myRequests: 'My tasks',
        new: 'New request',
        newConsult: 'New consult',
        newCounseling: 'New counseling',
        newPatient: 'New patient',
        noConsumerForRequest: 'No consumers available, please contact Ksyos servicedesk.',
        noProcesses: 'No processes were found within your organization that meet the specified criteria.',
        noProvider: 'No providers available for this service',
        noProviderOrganization: 'No providers available.',
        noProviderPractitioner: 'No practitioner available for this services.',
        noProvidersForRequest: 'No providers available, please contact Ksyos servicedesk.',
        noResultFoundForSearch: 'We could not find any results based on your query',
        noSearchResultWorklist: "We didn't find any tasks based on your search term",
        noSupervisorForRequest: 'There are no supervisors in your organization registered for this service.',
        ongoingRequests: 'Pending',
        open: 'Open',
        openProcesses: 'Open processes',
        openRequests: 'Open requests',
        patientEmail: 'Patient email address',
        patientName: 'Patient',
        patientTasks: 'Patient tasks',
        payoutDetails: 'Payment details',
        payoutSetTo: 'Payout {0} set to',
        payouts: 'payout records are created in run',
        phoneNumberRequester: 'Phonenumber requester',
        procedureState_unknown: 'Unknown procedure state',
        proceedWithOnlyAdditionalFiles: 'You did not add any fundus photos yet. Do you still want to submit {0}?',
        proceedWithoutFiles: 'You did not add any attachments. Do you still want to submit {0}?',
        processAlreadyBeingProcessed: 'Note: This consultation is already being processed',
        processAlreadyCompleted: 'Note: This consultation has already been completed',
        processAlreadyTakenByColleague: 'The {taskName} task is already taken by a colleague',
        processDetails: 'Process details',
        processId: 'Id',
        processes: 'Processes',
        question: 'Question',
        questionPlaceholder: 'Ask your question',
        questionnairesNotStarted:
            'The patient has received a questionnaire on ({0}) and has not yet started. If you continue, the patient will not be able to complete this questionnaire.',
        questionnairesStartedAndFinished:
            'The patient received a questionnaire on {0} and completed it. Are you sure you want to send a new questionnaire?',
        questionnairesStartedButNotFinished:
            'The patient has received a questionnaire on {0} and has started. If you continue, the patient will not be able to complete this questionnaire.',
        reasonNoInvoiceDecl: 'Reason no invoice and decl.',
        reasonNoInvoiceOrDeclaration: 'Reason no funding',
        reasonNoInvoiceOrDeclarationAbbreviation: 'Reason no fund.',
        recipient: 'Recipient',
        referralLetter: 'Referral letter',
        referralReasons: 'Referral reasons',
        referralResults: 'Reports',
        reportType_wrong: 'Wrong report type',
        reprocessDeclarations: 'Reprocess declaratations',
        reprocessMessage:
            'The declarations of the selected requests will be reprocessed. It may take some time before this is finished.',
        request: 'Request',
        requestConsult: 'Request a consult',
        requestCriteria: 'Request criteria',
        requestDate: 'Date of request',
        requestedBy: 'Requested',
        requester: 'Requester',
        requesterSelectedRouting: 'The grader for this examination has already been selected by the requester.',
        requests: 'Requests',
        resendQuestionnaires: 'Resend questionnaires',
        resendQuestionnairesModalTitle: 'Resend FOSQ-10 and ESS questionnaires',
        saveSend: 'Save and submit',
        searchInPatientTasks: 'Search by name, date of birth or task',
        searchInWorklist: 'Search through your tasks by date of birth, name, care provider or process ID...',
        searchProcessIdTooltip: 'Search by processId of a request',
        searchProcesses: 'Search processes',
        searchProcessesTooltip: 'For more processes, write a comma-separated list of processIds',
        searchTextTooltip: 'Search by request type, description, birthdate, process or name patient',
        selectCounselor: 'Select a counselor',
        selectGrader: 'Select grader for examination',
        selectableOnOrganization: 'Only selectable on organization',
        sendTo: 'Send to',
        sentTo: 'Sent to',
        serviceType_unknown: 'Unknown service type',
        showAgenda: 'Show calendar',
        showCancelled: 'Show cancelled',
        showCompleted: 'Show completed',
        showConsult: 'Show the complete consult',
        showContact: 'Show the contact',
        showCounseling: 'Show the complete counseling',
        showDetails: 'Show the details',
        showDraft: 'Show drafts',
        showOrganization: 'Show all in current organization',
        showReferral: 'Show the referral',
        showRejected: 'Show rejected',
        showRequest: 'Show the complete request',
        showSuspended: 'Show suspended',
        startHours: 'Start hours',
        startMinutes: 'Start minutes',
        startTime: 'Start time',
        startWith: 'Start with a',
        startWithOther: 'Start with your other tasks:',
        startedBy: 'Requested by',
        status: 'Status',
        status_aborted: 'Aborted',
        status_accepted: 'Accepted',
        status_active: 'Active',
        status_active_consult_recipient_closed: 'Report received',
        status_active_consult_recipient_open: 'Report sent',
        status_active_consult_recipient_open_answer: 'Answer sent',
        status_active_consult_recipient_open_question: 'Question received',
        status_active_consult_requester_closed: 'Report accepted',
        status_active_consult_requester_open: 'Report available',
        status_active_consult_requester_open_answer: 'Answer received',
        status_active_consult_requester_open_question: 'Question sent',
        status_active_discharge_recipient_closed: 'Discharge received',
        status_active_discharge_recipient_open: 'Discharge sent',
        status_active_discharge_requester_closed: 'Discharge accepted',
        status_active_discharge_requester_open: 'Discharge available',
        status_active_intake_recipient_closed: 'Intake received',
        status_active_intake_recipient_open: 'Intake sent',
        status_active_intake_requester_closed: 'Intake accepted',
        status_active_intake_requester_open: 'Intake available',
        status_active_procedure_aborted: 'Examination aborted',
        status_active_procedure_completed: 'Examination completed',
        status_active_procedure_inProgress: 'Examination in-progress',
        status_active_procedure_report_recipient_closed: 'Report received',
        status_active_procedure_report_recipient_open: 'Report sent',
        status_active_procedure_report_requester_closed: 'Report accepted',
        status_active_procedure_report_requester_open: 'Report available',
        status_active_procedure_suspended: 'Examination suspended',
        status_cancelled: 'Cancelled',
        status_colleague: 'Located at colleague',
        status_completed: 'Completed',
        status_draft: 'Draft',
        status_reason_aborted: 'Reason for aborting',
        status_reason_cancelled: 'Reason for canceling',
        status_rejected: 'Rejected',
        status_requested_recipient: 'To accept',
        status_requested_requester: 'Sent',
        status_suspended: 'On hold',
        status_unknown: 'Unknow report status',
        statusAction_aborted: 'Abort request',
        statusAction_accepted: 'Accept',
        statusAction_active: 'Process',
        statusAction_addIntakeReport: 'Create intake report',
        statusAction_addLetterOfDischarge: 'Create letter of discharge',
        statusAction_archiveReferral: 'Archive',
        statusAction_becomeRequesterPractitioner: 'Assign process to me',
        statusAction_cancelled: 'Cancel',
        statusAction_changeService: 'Which consultation should it be?',
        statusAction_completed: 'Close request',
        statusAction_convertToConsult: 'Convert to consult',
        statusAction_convertToReferral: 'Convert to referral',
        statusAction_print: 'Print',
        statusAction_printReferral: 'Print referral',
        statusAction_printRequest: 'Print request',
        statusAction_rejected: 'Reject',
        statusAction_requested: 'Send request',
        statusAction_resendReferral: 'Refer again',
        statusAction_suspended: 'On hold',
        statusAction_takeFromColleague: 'Take from colleague',
        statusAction_takeFromOrganization: 'Process',
        subsequentService: 'Follows from earlier process',
        successabortProcess: 'Process is aborted',
        supervisedBy: 'Supervised by',
        supervision: 'Supervision',
        supervisions: 'Supervisions',
        taskType: 'Task type',
        typeAndChoose: 'Type and select from the list...',
        typeOfConsult: 'What type of consultation should it be?',
        typeOfReferral: 'What type of referral should it be?',
        unpaid: 'Unpaid',
        updated: 'Updated',
        viewAllTasks: 'View all your tasks',
        whoIsTheRequester: 'Who is the requester?',
        worklist: 'Worklist',
        worklistPageHeader: 'Get ready!',
    },
    requestMessage: {
        appointmentConfirmation: 'Appointment confirmation',
        closeConsultation: 'Close consultation',
        confirmation: 'Confirmation',
        message: 'Message',
        messageForRequester: 'Message for the requester',
        multipleMessages: '{0} messages',
        notifyWhen: 'Notify the requester about the date of the appointment',
        optionallySendMessageToGP: 'Optionally you can send a message to the doctor when you take out the insurance',
        sendAMessage: 'Send a message',
        sendMessage: 'Send message',
        sendTo: 'Send to',
        singleMessage: '{0} message',
    },
    restEcgMeterSoftwareNamesEnum: {
        CardioPerfect: 'CardioPerfect',
    },
    service: {
        GGZBmind: 'Minddistrict',
        GGZBther: 'Therapieland',
        GGZOembl: 'Embloom',
        GGZOint: 'Interapy',
        activeServices: 'Active services',
        addRight: 'Add right',
        addService: 'Add new service',
        addServiceButton: 'Add service',
        compensation: 'Compensation',
        compensationPayout: 'Compensation',
        compensationPayouts: 'Compensations',
        consumingInOrganization: 'Consuming in',
        contractFunding: 'Funding by contract',
        customControl: 'Custom control',
        customControlVersion: 'Custom control version',
        details: 'Service data',
        doNotDeclareSet: 'Do not declare set',
        doNotDeclareSetTooltip: 'No patient invoice either',
        fileUpload: 'Add files',
        icd10: 'Use ICD-10',
        inactiveServices: 'Inactive services',
        isFundable: 'Service is funded (default: not checked)',
        isFundableTooltip: 'Fundable in the new funding system',
        isStandaloneGraderNotAllowed: 'Separate Grader not possible',
        isStandaloneGraderNotAllowedTooltip:
            'In this service, the grader must also be biometrist so that the research can be taken over by the grader.',
        isSuppressNotifications: 'Suppress notifications',
        isSuppressZorgmail: 'Suppress ZorgMail',
        ledgerNumber: 'General ledger',
        noDefault: 'No default service available',
        noGeneralPractitionerInvolved: 'No review and feedback from general practitioner',
        noServices: 'There are no active services available',
        noServicesSelected: 'No services selected',
        notificationOnCloseSubsequentService: 'Notification after subsequent service',
        notificationOnCloseSubsequentServiceTooltip:
            'The notification of this service will be sent at the end of the subsequesnt service.',
        provider: 'Service provider',
        providingForOrganization: 'Providing for',
        rights: 'Rights',
        runFundingAfterEachActivity: 'Run funding after every completed activity',
        runFundingAfterEachActivityTooltip:
            'Check this setting if DBC’s should be funded before the complete care path is finished',
        sendZorgmailOnCloseSubsequentService: 'Healthcare email when closing the following service',
        sendZorgmailOnCloseSubsequentServiceToolTip:
            'The healthcare email will only be sent at the end of the subsequent service.',
        service: 'Service',
        serviceType: 'Service type',
        serviceWorkflow: 'Service Workflow',
        services: 'Services',
        specialism: 'Specialization',
        stopService: 'Stop {0}',
        type: 'Type',
        type_consultation: 'Consultation',
        type_contact: 'Contact',
        type_counseling: 'Counseling',
        type_examination: 'Examination',
        type_inquiry: 'Inquiry',
        type_invitation: 'Invitation',
        type_other: 'Other',
        type_referral: 'Referral',
        type_referralSmall: 'Referral',
        useMedicationRequest: 'Medication in request',
        useNewFundingSystem: 'Uses the new funding system',
        useNewFundingSystemTooltip:
            'This boolean decides if this service is funded using the old or new system (not checked: old system)',
        zorgmailType: 'ZorgMail type',
    },
    serviceActivityType: {
        answer: 'answer',
        answerPronoun: 'this answer',
        assessment: 'assessment',
        assessmentPronoun: 'this assessment',
        consultation: 'consultation',
        consultationPronoun: 'this consultation',
        evaluation: 'evaluation',
        evaluationPronoun: 'this evaluation',
        examination: 'examination',
        examinationPronoun: 'this examination',
        inquiry: 'inquiry',
        inquiryPronoun: 'this inquiry',
        question: 'question',
        questionPronoun: 'this question',
        referral: 'referral',
        referralPronoun: 'this referral',
    },
    serviceConsumer: {
        checkIfEmptyOrganizationEmailField:
            'If no email address is entered, the notifications will be sent to the generic organization email address. This is currently: ',
        compensation: 'Compensation',
        compensationPayoutRequest: 'Payout Request',
        consumer: 'Consumer',
        consumers: 'Consumers',
        createContact: 'Can create contact',
        createRequest: 'Can create request',
        editMessage: 'Show the overlapping configuration',
        emailAddressOrganizationInbox: 'Email address organization inbox',
        emailAddressOrganizationInboxAlt: 'Alternative email address',
        emailAddressOrganizationInboxAndAlt: 'Email address organization inbox & Alternative email address',
        emptyGenericOrganizationEmailField: 'No generic organization email address was configured',
        graderSelector: 'Can select grader',
        isAutoCloseRequest: 'Auto close completed requests',
        isKetenDbc: 'Keten-DBC question',
        noEmploymentChosen: 'Choose an employment to view this email address',
        overlapMessage: 'Overlapping configuration found',
        restEcgMeterSoftwareName: 'Rest ECG device software name',
        sendFollowUpMeasurementGgz: 'Send post-measurement by default',
        sendNotificationEmployment: 'Notification to email address of employment practitioner',
        sendNotificationEmploymentAlt: 'Notification to alternative email address of employment practitioner',
        sendNotificationOrganizationInbox: 'Notification to organization inbox',
        sendNotificationPractitioner: 'Notification to primary email address of practitioner',
        sendNotificationPractitionerWhenDeviant: 'Only when examination is deviant',
        sendZorgMailOnExaminationRequest: 'ZorgMail upon request examination',
        sendZorgMailOnReferralIntake: 'ZorgMail upon intake referral',
        sendZorgMailOnReferralRequest: 'ZorgMail upon request referral',
        serviceConsumer: 'Service consumer',
        serviceConsumers: 'Service consumers',
        serviceConsumersGroupSetting: 'Service consumers group setting',
        serviceConsumersGroupSettings: 'Service consumers group settings',
        spirometerSoftwareName: 'Spirometer software name',
        usingRestEcgMeter: 'Using rest ECG device',
        usingSpirometer: 'Using spirometer',
    },
    serviceConsumersGroupSettings: {
        addSupervisor: 'Add new Supervisor',
        noSupervisor: 'No supervisors selected',
        overlapMessage: 'Overlapping configuration found',
        selectedSupervisor: 'Select supervisor for the service',
        supervisor: 'Supervisor',
        supervisors: 'Supervisors',
    },
    serviceDeclarationActivities: {
        activityType: 'Activity type',
        amount: 'Amount',
        appliesWhenCustomControlDataQuery: 'Query',
        appliesWhenCustomControlDataQueryLabel: 'Query (old funding module)',
        careProductType: 'Care product type',
        codeActiviteit: 'Activity code',
        codeAfsluitreden: 'Closing reason',
        codeDiagnose: 'Diagnosis code',
        codeSpecialisme: 'Specialism',
        codeZorgtype: 'Care type',
        cpapTreatment: 'CPAP treatment (new funding module)',
        defaultIcd10: 'Default ICD10 code',
        editMessage: 'Show the overlapping declaration rule',
        icd10Text: 'CD10 code in declaration record if it does not arise from handling request',
        isHonorariumDeclareren: 'Declare fee',
        isKostendeelDeclareren: 'Declare cost share',
        overlapMessage:
            'Overlapping declaration rule(s) found! Multiple valid declaration will result in multiple declarations. This can be necessary in some cases',
        queryText: 'A query for specific data in request, research or report. Field can be left blank',
        spirogramIncludingReversibilityTest: 'Spirogram incl. reversibility test (new funding module)',
        uzoviMessage: 'Rule has to apply to one or more UZOVI codes',
    },
    serviceDeclarationRules: {
        addDeclarationActivity: 'Add declaration activity',
        addDeclarationSetting: 'Add declaration setting',
        careProductType: 'Care product type',
        declarationActivities: 'Declaration activities',
        declarationActivity: 'Declaration activity',
        declarationSetting: 'Declaration setting',
        declarationSettings: 'Declaration settings',
        exportUntilDate: 'Export until',
        exportUntilDateTooltip: 'Declarations for processes created after this date will not be exported',
        insurerServiceDeclarationRule: 'Insurer declaration rule',
        insurerServiceDeclarationRules: 'Insurer declaration rules',
        serviceDeclarationRule: 'Declaration rule',
        serviceDeclarationRules: 'Declaration rules',
        supplementPriceActivity: 'Supplement price activity',
    },
    serviceGraderPool: {
        addProvider: 'Add service grader pool providers',
        canGradeForAnalyst: 'Grades holter analyses for',
        chooseOption: 'Choose an option',
        editMaximumGradings: 'Edit maximum amount of gradings',
        editServiceGraderPool: 'Edit grader pool',
        maxPerDay: 'Max amount per day',
        maxPerMonth: 'Max amount per month',
        name: 'Pool name',
        newServiceGraderPool: 'New grader pool',
        noServiceGraderPoolProviders: 'No service providers selected',
        perDay: 'Per day',
        perMonth: 'Per month',
        provider: 'Provider',
        selectSerivceGraderPoolProvider: 'service grader pool providers',
        selectServiceGraderPool: 'Select services grader pools',
        serviceGraderPool: 'Grader pool',
        serviceGraderPoolDetails: 'Service grader pool details',
        serviceGraderPoolProvider: 'Service grader pool provider',
        toolTipText:
            'The analyzes of the Holter recordings are done by various parties. Select here for which party graders in this pool can grade the analyses.',
    },
    serviceGroup: {
        counseling: 'What kind of system will the organization use within counseling?',
        examination: 'What kind of system will the organization use within examination?',
    },
    serviceOrganizationRoleEnum: {
        dietetics: 'Dietetics',
        hospital: 'Hospital',
        lifestyleCoaching: 'Lifestyle coaching',
        policlinic: 'Policlinic',
        psychology: 'Psychology',
    },
    serviceOrganizationSetting: {
        canPerformGlaucoomExamination: 'OCT scan present for glaucoma examination',
        currentCareGroup: 'Current care group',
        diagnosticEquipment: 'Diagnostic equipment',
        emailNotificationOnlyToOrganization: 'Send email notifications only to organization email address',
        emailNotifications: 'Email notifications',
        hideCareGroupLogbook: 'Hide care group logbook',
        hideRequestedExaminationTaskFeature: 'Hide tasks',
        holterAnalystOrganization: 'Analyst organization',
        isRequestRecipient: 'Organization can be selected as request recipient',
        isSendingDeviceToPatientsAddress: "Organization sends holters to patients' homeaddress",
        isUsingHideRequestedExaminationTaskFeature: 'Hide planned tasks in tasklist',
        ksyosHolterAnalystOrganization: 'Ksyos analyst organization',
        optoConnectPracticeId: 'Optoconnect id',
        organizationIsRequestRecipientForSubsequentService:
            'The organization must be request recipient, because in the previous step the organization was selected as recipient for the automatic subsequent service.',
        'organizationRole(s)': 'Organization role(s)',
        receiveZorgmailOnConsultOpen: 'Receives zorgmail when sending consult',
        requestRecipientTitle: 'Request recipient',
        'role(s)OfOrganization': 'Role(s) of organization',
        selectCareGroup: 'Select care group',
        selectOptoConnectPracticeId: 'Fill in Optoconnect id',
        selectSoftware: 'Select software',
        selectSomnologyOrganizationType: 'Select type',
        sendBittiumRecordingsTo: 'Send Bittium recordings to',
        sendingHolter: 'Sending holter',
        showCareGroupLogbook: 'Show care groep logbook',
        software: 'Software',
        somnologyCanReferPatientWithoutSmartphone: 'Can refer patient without smartphone',
        somnologyOrganizationType: 'Somnology organization type',
        validate: 'Validation',
        validateAssetName: 'Validation filenames',
        validationOnField: 'Validation on',
        zorgMail: 'ZorgMail',
        zorgMailNumber: 'ZorgMail number',
    },
    serviceOrganizationSettingRoleDistributions: {
        addOrganization: 'Add {0} organization',
        cannotDeleteRole: 'Cannot delete role {0}, because it is a partner organization for {1}.',
        cannotDeleteServiceOrganizationSetting: 'Cannot delete service, because roles are distributed at {0}',
        distributeInEqualPercentages: 'Distribute in equal percentages',
        distributionOrganizations: 'Distribution organizations',
        noPartnerOrganizationsAvailable: 'No partner organizations available',
        partnerOrganizations: 'Partner organizations',
        percentageHigherThan0: 'Percentage must be higher than 0.',
        percentageRequired: 'Percentages for organizations are required.',
        totalPercentageNot100: 'The sum of the percentages must be 100%.',
    },
    serviceProvider: {
        alreadyFinancedTooltip: 'This service is provided free of charge if preceded by other service',
        analyst: 'Analyst',
        biometrician: 'Biometrician',
        canSelectCareProviderAsServiceConsumer: 'Can also select GP as recipient',
        changedError: 'There was an error',
        changedSuccess: 'Assigned successfully',
        checkIfEmptyOrganizationEmailField:
            'If no email address is entered, the notifications will be sent to the generic organization email address. This is currently: ',
        compensation: 'Compensation',
        compensationPayout: 'Compensation payout',
        compensationPayoutGlaucoma: 'Payout glaucoma',
        compensationPayoutProcedure: 'Payout Biometrics',
        compensationPayoutReport: 'Payout Examination/Consult',
        compensations: 'Examination / Consult & Payout Biometrics',
        consumerSelector: 'Can select recipient',
        editMessage: 'Show the overlapping configuration',
        emailAddressOrganizationInbox: 'Email address organization inbox',
        emailAddressOrganizationInboxAlt: 'Alternative email address',
        emailAddressOrganizationInboxAndAlt: 'Email address organization inbox & Alternative email address',
        emptyGenericOrganizationEmailField: 'No generic organization email address was configured',
        grader: 'Grader',
        graderSelector: 'Can select grader',
        isAlternativeFinancing: 'Alternative financing',
        isAnalyst: 'Is analyst',
        isBiometrician: 'Is biometrician',
        isGrader: 'Is grader',
        isRequestRecipient: 'Is request recipient',
        messageNoServiceProviderAndOrganizationRecipient:
            'Attention: the organization and the provider do currently not receive the request',
        messageNoServiceProviderRecipient: 'Attention: There is currently no provider who can receive the request',
        messageOrganizationNoRecipient: 'Attention: Organization is currently not a recipient for request',
        messagePayout: 'The agreement for payouts is determined at the organization level',
        noEmploymentChosen: 'Choose an employment to view this email address',
        optoConnectPracticeId: 'OptoConnect practiceID',
        organizationCompensations: 'Payout for Examination/Consult & Biometrics',
        overlapMessage: 'Overlapping configuration found',
        preferredConsumer:
            'At least one preferred service consumer should be added, when the service provider can select a recipient.',
        provider: 'Provider',
        providers: 'Providers',
        receiveZorgmailOnConsultOpen: 'ZorgMail upon consultation answer',
        requestCreator: 'Can create examinations',
        restEcgMeterSoftwareName: 'Rest ECG meter software name',
        selectSpirometerSoftwareName: 'Select spirometer software',
        sendBittiumRecordingsTo: 'Send Bittium recordings to',
        sendInvoice: 'Send invoice',
        sendNotificationEmployment: 'Notification to email address of employment practitioner',
        sendNotificationEmploymentAlt: 'Notification to alternative email address of employment practitioner',
        sendNotificationOrganizationInbox: 'Notification to organization inbox',
        sendNotificationPractitioner: 'Notification to primary email address of practitioner',
        serviceOrganizationSetting: 'Service Organization setting',
        serviceOrganizationSettings: 'Service provider organization settings',
        serviceProvider: 'Service provider',
        serviceProviders: 'Service providers',
        somnologyOrganizationType: 'Sleep organization type',
        specializationProvider: 'Specialization care provider',
        specializations: 'Specialization(s)',
        spirometerSoftwareName: 'Spirometer software name',
        usingRestEcgMeter: 'Using rest ECG meter',
        usingSpirometer: 'Using spirometer',
    },
    serviceRightStopModal: {
        bodyEmployment:
            'Are you certain? The care provider will no longer be able to use the account when you confirm the deactivation.',
        bodyEpisodeCategory:
            'Are you certain? When you stop this carepath, the practitioner will no longer be able to perform any work that requires any {0} services ({1}).',
        bodyOrganization:
            'Are you certain? When you make the organization inactive, the practitioners within it will not be able to perform any work for this organization.',
        bodyRight:
            'Are you certain? When you turn of the rights, the practitioner will no longer be able to perform any work that requires {1} {0} rights',
        bodyServiceGroup:
            'Are you certain? When you stop this service, the practitioner will no longer be able to perform any work that requires {0}',
        serviceRightType: {
            ServiceConsumer: 'Requester',
            ServiceProvider: 'Performer',
            ServiceReader: 'Reader',
            ServiceSupervisor: 'Supervisor',
        },
        titleEmployment: 'Make practitioner inactive',
        titleMakeOrganizationInactive: 'Archive organization',
        titleRemoveRights: 'Remove {0} {1} rights',
        titleRight: 'Turn off {0} {1} rights',
        titleServiceGroup: 'Stop {0} ',
        warningEmployment: 'Be carefull, the practitioner might still have active processes.',
        warningOrganization: 'Be carefull, the organization might still have active processes.',
        warningRight: 'Be carefull, the practitioner might still have active processes that require {1} {0} rights.',
        warningRightServiceReader:
            "Be carefull, if the reader rights are stopped, the practitioner won't have reader rights within the entire {0} carepath.",
        warningServiceGroup: 'Be carefull, the practitioner might still have active processes within {0}.',
    },
    serviceSettings: {
        practitioners: {
            activeSince: 'Active since',
            name: 'Name',
            priceAgreement: 'Price agreement',
            profession: 'Profession',
            rights: 'Rights',
        },
    },
    serviceTypeEnum: {
        consultation: 'Consultation',
        contact: 'Contact',
        counseling: 'Counseling',
        examination: 'Examination',
        inquiry: 'Inquiry',
        invitation: 'Invitation',
        other: 'Other',
        referral: 'Referral',
        referralSmall: 'Referral OSAS',
    },
    setting: {
        settings: 'Settings',
    },
    sms: {
        dermaSnelOverdueToRecipientPractitionerReminder:
            'A general practitioner is waiting for your assessment of a new Fast Consultation. Can you take a look?',
    },
    somnologyOrganizationTypeEnum: {
        ksyosMedicalCenter: 'Ksyos Slaap',
        nsi: 'Nsi',
    },
    somnologyPatientTaskType: {
        choiceOfTreatment: 'Choice of treatment',
        cpap: 'CPAP',
        cpapAanvraag: 'CPAP request',
        cpapAnnualControl: 'CPAP, Annual control',
        cpapClassA: 'CPAP, Classification A',
        cpapClassB: 'CPAP, Classification B',
        cpapClassC: 'CPAP, Classification C',
        cpapClassD: 'CPAP, Classification D',
        cpapControlDoctor: 'CPAP, Control doctor',
        cpapEvaluation: 'CPAP, Evaluation',
        cpapO2Measurement: 'CPAP, O2 measurement',
        cpapOther: 'CPAP, Other',
        followUpResearchFailed: 'Follow-up research failed',
        followUpResearchSuccessful: 'Follow-up research successful',
        lifestyle: 'Lifestyle',
        lifestyleControlDoctor: 'Lifestyle, Control doctor',
        lifestylePGControl: 'Lifestyle, PG Control',
        mra: 'MRA',
        mraAanvraag: 'MRA request',
        mraClassA: 'MRA, Classification A',
        mraClassB: 'MRA, Classification B',
        mraClassC: 'MRA, Classification C',
        mraClassD: 'MRA, Classification D',
        mraOther: 'MRA, Other',
        onderzoekWithoutProgress: 'Examination without progress',
        pGControl: 'PG control',
        planAppointment: 'Plan appointment',
        practitionerControl: 'Practitioner control',
        proefPeriodeAfbreken: 'Stop trial period',
        proefPeriodeContinueren: 'Continue trial period',
        proefPeriodeSuccesvol: 'Trial period succesvol',
        questionFromMedicalSpecialist: 'Question from medical specialist',
        reference: 'Reference',
        remaining: 'Other',
        remainingOther: 'Other',
        researchFailed: 'Research failed',
        researchSuccessful: 'Research successful',
        spt: 'SPT',
        sptAanvraag: 'SPT request',
        sptAnnualControl: 'SPT, Annual control',
        sptClassA: 'SPT, Classification A',
        sptClassB: 'SPT, Classification B',
        sptOther: 'SPT, Other',
        sptPGControl: 'SPT, PG Control',
        zDIntake: 'ZD intake',
    },
    specialismCodeProfession: {
        '0301': 'Ophthalmologist',
        '0302': 'ENT specialist',
        '0310': 'Dermatologist',
        '0313': 'Internist',
        '0320': 'Cardiologist',
        '0322': 'Pulmonologist',
        '0326': 'Allergologist',
        '0329': 'Psychiatrist',
        '1900': 'Audiologist',
    },
    spirometerSoftwareNamesEnum: {
        CardioPerfect: 'CardioPerfect',
        CareFusion: 'CareFusion',
    },
    stopServiceOrganizationSettings: {
        activeProcessesWarning: 'Be carefull, the organization {0} might still have active processes for {1}',
        stopAll: 'Stop all {0} services',
        sureToStop: 'Are you certain? When you stop {0}, the practitioners can no longer perform any work for {1}.',
        theCarePath: 'the care path',
        theService: 'the service',
    },
    subsequentService: {
        autoSubsequentServices: 'Auto subsequent services',
        isDeviation: 'Only in case of deviation',
        noOrganizationsAvailable: 'No providers available',
        noProvidersAvailable: 'No providers available, try another organization',
        providerOrganization: 'Service providing organization',
        providerPractitioner: 'Service providing practitioner',
        selectOrganization: 'Select a service provider organization for the subsequent service',
        selectProvider: 'Select a service provider for the subsequent service',
        subsequentService: 'Subsequent service',
        subsequentServiceNeedsToBeActive:
            'Only when the practitioner has rights for the subsequent service can that service be started',
        subsequentServices: 'Subsequent services',
    },
    supervision: {
        assessmentBiometrics: 'Assessment biometrics',
        biometrics: 'biometrics',
        diagnosticReportRemark: 'Grader remark',
        edit: 'Edit supervision',
        findings: 'Supervision findings',
        grading: 'grading',
        hideSupervision: 'Hide the supervision',
        list: 'Supervision requests',
        percentage: 'Supervision percentage',
        percentageBiometrician: 'Supervision percentage Biometrician',
        percentageGrader: 'Supervision percentage Grader',
        procedureRemark: 'Biometrician remark',
        qualityCheck: 'Quality check',
        qualityProcedure: 'Quality check procedure',
        qualityReport: 'Quality check result',
        remarkBiometrics: 'Remarks quality control biometrics',
        remarkGrading: 'Remarks quality control diagnosis',
        remarksProcedure: 'Remarks biometrician',
        showSupervision: 'Show the complete supervision',
        start: 'Start supervising',
        supervision: 'Supervision',
        takeOver: 'Take over supervising',
    },
    supervisors: {
        checkIfEmptyOrganizationEmailField:
            'If no email address is entered, the notifications will be sent to the generic organization email address. This is currently: ',
        compensationPayOut: 'Compensation payout',
        editMessage: 'Show the overlapping configuration',
        emailAddressOrganizationInbox: 'Email address organization inbox',
        emailAddressOrganizationInboxAlt: 'Alternative email address',
        emailAddressOrganizationInboxAndAlt: 'Email address organization inbox & Alternative email address',
        emptyGenericOrganizationEmailField: 'No generic organization email address was configured',
        noEmploymentChosen: 'Choose an employment to view this email address',
        overlapMessage: 'Overlapping configuration found',
        payout: 'Payout',
        sendNotificationEmployment: 'Notification to email address of employment practitioner',
        sendNotificationOrganizationInbox: 'Notification to organization inbox',
        sendNotificationPractitioner: 'Notification to primary email address of practitioner',
        supervisingService: 'Supervising service',
        supervisingServices: 'Supervising services',
        supervisor: 'Supervisor',
        supervisorNeededInfoTooltipText:
            "Please note that the AGB code of chosen supervisor has priority over practitioner's own AGB code. If the practitioner AGB code does not allow consultation, a supervisor with a valid AGB code (e.g. a general practitioner or psychiatrist) must be selected",
        supervisors: 'Supervisors',
    },
    supervisorsAssessmentBiometricsEnum: {
        good: 'Good',
        moderate: 'Moderate',
        poor: 'Poor',
    },
    support: {
        anythingElse: 'Anything else you want to say? (Do not include patient data)',
        body: 'Our Servicedesk members are at the ready!',
        contactServiceDesk: 'Contact the servicedesk.',
        exportFileColumns: {
            analysisCompletedDate: 'Analysis Completed Date',
            clarificationQARequesterSentDateTime: 'Date Question Sent',
            dateSent: 'Date Request Sent',
            diaryType: 'Diary Type',
            episodeCategoryName: 'Episode Category Name',
            graderName: 'Grader Name',
            holterAnalystOrganizationName: 'Holter Analyst Organization',
            holterSendDate: 'Holter Send Date',
            holterSetDate: 'Holter Set Date',
            numberOfDaysActive: 'Number of Days Active',
            performerOrganizationName: 'Performer Organization Name',
            performerOrganizationTxcsId: 'Performer Organization Id',
            processId: 'PID',
            recipientOrganizationName: 'Recipient Organization Name',
            recipientOrganizationTxcsId: 'Recipient Organization Id',
            recipientPractitionerName: 'Recipient Practitioner Name',
            recipientPractitionerUid: 'Recipient Practitioner Id',
            recorderType: 'Recorder Type',
            requesterOrganizationName: 'Requester Organization Name',
            requesterOrganizationTxcsId: 'Requester Organization Id',
            requesterPractitionerName: 'Requester Practitioner Name',
            requesterPractitionerUid: 'Requester Practitioner Id',
            responseAnalyse: 'Response Analyse',
            responseGrading: 'Response Grading',
            serviceName: 'Service Name',
            startUploadDate: 'Start Upload Date',
            status: 'Status (CS)',
            uploadProgress: 'Upload Progress (%)',
        },
        feedback: 'Feedback',
        giveFeedback: 'Give us some feedback',
        happyForFeedback: 'Ksyos is very happy with your feedback!',
        header: 'Handle it yourself',
        openingHours: 'Monday - Friday from 7:30 - 17:00 uur',
        rateQuestion: 'How do you rate the Ksyos platform?',
        send: 'Send',
        sendEmail: 'Or send an email to info@ksyos.nl',
        submit: 'Submit',
        submitted: 'You request has been submitted. We will contact you as soon as possible',
        support: 'Support',
        supportTeamviewerBody: 'For remote support our Servicedesk member will ask you to install TeamViewer.',
        supportTeamviewerHeader: 'Do you need help? You can share your screen with us.',
        thanks: 'Thank you!',
        yourQuestion: 'Your question or remark',
    },
    tcohProcedureTypeEnum: {
        eventEcg: 'Event',
        holter: 'Holter',
    },
    therapieland: {
        saveTreatments: 'Save',
        tooltipTextBegeleiding:
            'Your colleague is the counselor of this patient. That is why you cannot see the progress in Therapieland.',
        typeToFindTreatments: 'Start typing to find a treatment',
    },
    thirdParty: {
        apiToken: 'API token',
        baseUrl: 'Base url',
        connectionFailed: 'Failed',
        connectionOk: 'Test ok',
        secretKey: 'Secret key',
        testConnection: 'Test connection',
    },
    ticketTypeEnum: {
        jiraTicket: 'Jira ticket number',
        oplossenKlacht: 'Resolving complaint ticket number',
        perfectviewTicket: 'Perfectview ticket number',
    },
    ticketTypeEnumShort: {
        jiraTicket: 'Jira',
        oplossenKlacht: 'Resolve complaint',
        perfectviewTicket: 'Perfectview',
    },
    timeout: {
        login: 'Log in again',
        messageLoggedOut:
            'Because you have not been active in the platform for {0} minutes, we have logged you out automatically. Log in again to continue where you left off.',
        messageSessionWarning:
            'When you are inactive for {0} minutes in the platform, you will be logged out automatically. Click the button to stay logged in.',
        reset: 'Stay logged in',
        titleLoggedOut: 'Your session has expired',
        titleSessionWarning: 'Your session has almost expired',
    },
    toast: {
        answerSendSuccess: 'Your answer is sent',
        answerSentToOrganizationSuccess: 'Your answer has been sent to {0}',
        cancelDefaultMessage: 'Cancelled',
        cancelExistingSuccess: 'Editing of your {0} has been cancelled',
        cancelModificationSuccess: 'Correction canceled',
        cancelSuccess: 'Your {0} is cancelled',
        changesSaveSuccess: 'Your changes have been saved',
        changesSaveWarning: 'An error has occured while saving your changes',
        clarificationSendSuccess: 'Your clarification is sent',
        confirmedRequestDefaultMessage: 'Your form is closed',
        confirmedRequestDefaultMessageAndSentToHis: 'Your form is closed, we will send this result to your HIS',
        consentSaved: 'Consent has been saved',
        consultRequestSubmit: 'The consult has been sent to {0}. Follow the status from tasks > pending',
        consultTypeChanged: 'The consult has been converted to a {0} consult',
        convertConsult: 'The results of the examination have been added to your consult',
        copyTextSuccess: 'Text has been copied to clipboard',
        draftDefaultMessage: 'Stored as draft, use your task list to follow the status',
        draftSuccess: 'Your {0} is stored as draft',
        duplicatedMdoPatient: 'You cannot add a patient to the same MDO twice',
        modificationSuccess: 'Correction successfully applied',
        procedureSubmitted: 'Your {0} has been sent for grading',
        questionSendSuccess: 'Your question is sent',
        questionnaireScheduled: 'Questionnaire successfully scheduled',
        removeSuccess: `Your {0} has been removed`,
        removeSuccessDefault: `Removed`,
        requestConfirmed: 'Your {0} is closed',
        requestConfirmedAndSentToHis: 'Your {0} is closed, we will send this result to your HIS',
        requestMessageSend: 'The message has been send to {0}',
        savedSuccess: 'Your {0} is saved',
        sentSuccess: 'Your {0} is sent',
        submitDefaultMessage: 'Sent, use your task list to follow the status',
        submitExaminationToOphthalmologist: 'Your examination has been sent to the ophthalmologist',
        submitSuccess: 'Your {0} is submitted',
        submitSuccessSimple: 'Your {0} is submitted',
    },
    toesturenKsyosMedicalCenter: {
        bewegen: 'Moving',
        leefstijl: 'Lifestyle',
        lekker_slapen: 'Sleeping comfortably',
        ontspanning: 'Relaxation',
        piekeren: 'Worry',
        slaap_en_shifts: 'Sleep and Shifts',
        slaaprestrictie: 'Sleep restriction',
    },
    tooSupervision: {
        assessmentBiometrics: "Review photo's",
        assessmentCompleteness: 'Integrity provided information',
        assessmentDiagnosis: 'Qualilty of the preliminary diagnosis',
        assessmentQuestion: 'Quality of the questionnaires',
        feedbackTitle: 'Feedback quality examination',
        remarkBiometrics: 'Remarks quality control biometry',
        remarkGrading: 'Remarks quality control diagnosis',
        supervisionAlert:
            "There is feedback on the examination and the findings. You can find it under the heading: 'Feedback quality examination'.",
    },
    twoFactorAuthenticationType: {
        none: 'None yet',
        verificationApp: 'Verification App',
        verificationDevice: 'Hardware Token',
    },
    txcs: {
        conflict: 'There is a mismatch in the patient information',
        conflictIdentifierSocialSecurity: 'To be used Social Security number',
        conflictIdentifierSocialSecurityText:
            'The selected patient does not have a Social Security number known to Ksyos. We will assign the following Social Security number from your HIS to this patient.',
        conflictText:
            'The data from your HIS does not match the data registered within Ksyos, you can choose which data is correct here:',
        fromHIS: 'Data from the HIS:',
        inKSYOS: 'Data registered within Ksyos:',
        matchingPatientInformation: 'The following patient information matches:',
        sbvzError: 'The personal data of the given patient could not be found.',
        selectCorrectInformation: 'You can select the correct information below',
        selectPractitioner: 'Select the general practitioner',
        vecozoError: 'The insurance data of the given patient could not be found.',
    },
    unavailableServices: {
        dermasnelc: {
            closedOutsideOfBusinessHours: {
                alertAndTooltip:
                    'This service is only available on weekdays between 8:00 and 17:00. However, you can start a regular consultation now.',
            },
        },
        dermasnelcsc: {
            closedOutsideOfBusinessHours: {
                alertAndTooltip:
                    'This service is only available on weekdays between 8:00 and 17:00. However, you can start a regular consultation now.',
            },
        },
        holter: {
            youngerThanEighteen: {
                alertAndTooltip: 'Our cardiologs do not review examinations for patients under the age of 18',
            },
        },
        osas: {
            youngerThanEighteen: {
                alertAndTooltip: 'Our somnologists do not review examinations for patients under the age of 18',
            },
        },
        respialler: {
            youngerThanEighteen: {
                alertAndTooltip: 'Our allergologists do not review examinations for patients under the age of 18',
            },
        },
        tcch: {
            youngerThanEighteen: {
                alertAndTooltip: 'Our cardiologists do not review consultations for patients under the age of 18',
            },
        },
        tccr: {
            youngerThanEighteen: {
                alertAndTooltip: 'Our cardiologists do not review consultations for patients under the age of 18',
            },
        },
        tcoh: {
            youngerThanEighteen: {
                alertAndTooltip: 'Our cardiologists do not review holter examinations for patients under the age of 18',
            },
        },

        tcor: {
            youngerThanEighteen: {
                alertAndTooltip: 'Our cardiologists do not review examinations for patients under the age of 18',
            },
        },
    },
    units: {
        packYears: 'pack years',
    },
    upload: {
        busyUploading: 'Busy uploading...',
        chooseFile: 'Choose a file',
        dhd: 'Import Dutch Hospital Data',
        noCsvFormat: 'No CSV format',
        orStartTheCardiograph: 'Or start the cardiograph',
        orStartTheSpirometer: 'Or start the spirometer',
        renew: 'Renew',
        selectCsvFile: 'Select CSV file',
        startOrAddECG: 'Start a new Rest ECG or add a previous ECG',
        startRestEcg: 'start a rest ECG recording',
        startSpirogram: 'start a spirogram recording',
        upload: 'Upload',
        uploadToAnalyst: 'Sending to analyst',
        uploading: 'Uploading',
        uzovi: 'Import UZOVI Data',
        uzoviImported: 'Geimporteerde Records',
        viaKsyosDesktop: 'via Ksyos desktop',
        wrongFileFormat: "The file '{0}' is the wrong format",
        wrongFirstRow: 'First row does not contain "Exact Relatienummer;TxCS ID nr;"',
        wrongTxcsId:
            "There is no organization in CS with TxCS ID no: '{0}'. Find out why. If necessary, remove this organization from the list to be imported",
    },
    uzovi: {
        add: 'Add UZOVI',
        code: 'UZOVI code',
        codeOrName: 'Code or name',
        label: 'Label',
        stored: 'Not Available',
        uzovi: 'UZOVI',
        uzovis: 'UZOVIs',
    },
    validation: {
        EXTERNALID_EXISTS: 'Serial number already exists',
        IDENTIFIER_DECLARATION_EXISTS: 'This number is already in use',
        IDENTIFIER_REGISTRATION_BIG_EXISTS: 'This number is already in use',
        IDENTIFIER_REGISTRATION_KP_EXISTS: 'This number is already in use',
        NO_MATCH_DATE: 'Birthdate does not match the SSN registered in Client Safe',
        NO_MATCH_DATE_BSN: 'Combination SSN and birthdate does not exist',
        NO_PASSWORD: 'Choose a password to continue',
        NO_VALID_INSURANCE: 'No valid insurance',
        OTP_SECRET_DOES_NOT_MATCH_TOTP_TOKEN: 'Incorrect Google Authenticator code',
        REGISTRATION_CODE_EXPIRED: 'Your registration code has expired',
        RELATION_NUMBER_EXISTS: 'This number is already in use',
        REQUIRED: 'required',
        SHORT_PASSWORD: `Your password must contain at least ${MINIMUM_PASSWORD_LENGTH} characters`,
        USERNAME_EXISTS: 'This username is already in use',
        USERNAME_NOT_FOUND: 'This username is not known to us. Forgot your username?',
        VERY_SHORT_PASSWORD: `Your password must contain at least ${MINIMUM_PASSWORD_LENGTH} characters`,
        WRONG_PASSWORD: 'The combination of password and username is incorrect.',
        WRONG_PASSWORD_WARNING: 'The combination of password and username is incorrect.',
        WRONG_REGISTRATION_CODE: 'Incorrect registration code',
        WRONG_TOTP_TOKEN: 'Incorrect Google Authenticator code',
        agbCodeRequired: 'Must contain a number with length of 8 digits',
        agbStartDateMismatch: 'The start date falls before the start date of {0} in Vektis',
        any_allowOnly: 'Value is not valid',
        any_custom: 'Choose one of the options',
        any_empty: 'Please fill in this field',
        any_emptySupport: 'Please fill in this field',
        any_invalid: 'The value is invalid',
        any_only: 'This is a required field',
        any_required: 'Please fill in this field',
        any_selected: 'Choose one of the options',
        array_base: 'Please select at least one option',
        array_length: 'Has to have at least 1 item',
        array_max: 'Select maximum {max} items',
        array_min: 'Select al least 1',
        asset_required: 'You have to add an asset',
        bmi: 'BMI must be a number between {min} and {max}',
        boolean_base: 'Choose one of the options',
        bothTimesRequired: 'Both times are required',
        bsnAlreadyExists: 'SSN is already linked to a patient',
        bsnIsNeededToContinueOork: "Ksyos cannot facilitate the driver's license examination when no SSN is provided.",
        changeEmailAddress: 'You did not change the email address. Please adjust before resending.',
        chooseCurrentDateOrBefore: 'Choose the current date or a date from the past',
        date_afterEndServiceRight: 'The chozen dat may not be after the end date of the service',
        date_base: 'Must be a date',
        date_beforeStartServiceRight: 'The chozen dat may not be before the start date of the service',
        date_future: 'Date cannot be in the future',
        date_past: 'Date cannot be in the past',
        date_valid: 'Date cannot be invalid',
        dateOverlap: 'Date period overlaps with another period',
        datePeriodInvalid: 'Date period is invalid, the end date must be later than the start date',
        datePeriodPast: 'Date period cannot be in the past',
        dateRanges_hasOverlap: 'Overlapping date ranges are not allowed',
        dateRanges_noActiveItem: 'At least one active item is required',
        duplicateAgbCode: 'This AGB code is already registered in the Ksyos platform',
        duplicateEmailTherapieland:
            'This email address is already being used by someone else to receive Therapieland counseling. Please use another email address to continue.',
        duplicateTreatment: 'The patient already followed this treatment.',
        duplicateTreatmentModal:
            'One or more of the programs in this counseling have already been sent to this email address. Please choose a different email address.',
        duplicatedEmailTherapieland:
            'This email address is already in use. Please choose another email address to continue.',
        duplicatedUserName: 'Username is already in use',
        email_required: 'An email address is required',
        endTime_required: 'Enter end time',
        endTimeBeforeStartTime: 'End time can only be later than start time',
        enter_question: 'Type your question',
        excelParser: {
            any_invalid: 'Invalid value',
            columnsNotFound: "Couldn't find the following columns: '{0}'",
            excelContainsMultiplePatientsWithSameBsn: 'Excel contains multiple patients with the same BSN',
            fileSizeMustBeLessThan: 'The file size must be less than {0} MB',
            groupWithIdentificationNumberNotFound: 'Group with identification number "{0}" not found',
            groupWithNameNotFound: 'Group with name "{0}" not found',
            identifierDeclarationRequired: 'Invalid AGB code',
            invalidFileText: 'The file you are trying to upload is invalid. You can only upload Excel files here.',
            invalidFileTitle: 'Adding the file failed',
            koi_date: 'Date not in the correct format (dd-mm-yyyy)',
            koi_enum: 'Invalid value',
            multipleGroupsWithSameNameFound: 'Multiple groups with name "{0}" have been found',
            multipleOrganizationsWithSameAgbCodeFound: 'Multiple organizations with AGB code "{0}" have been found',
            number_base: 'Invalid number',
            organizationWithAgbCodeNotFound: 'Organization with AGB code "{0}" not found',
            string_base: 'Invalid text',
            string_elfproef: 'Invalid BSN',
            string_email: 'Invalid email address',
            string_length: 'Text does not have the correct length',
            vipLiveReferralDateOlderThanOneYear: 'The Verwijsdatum not be older than 1 year',
            worksheetNotFound: 'Worksheet "{0}" not found',
        },
        expiredAgb: 'This AGB code has expired',
        failedToGetUpdatedInvitationProgress:
            'Progress cannot be shown accurately. Go to Therapieland to see the progress.',
        failedToSendAllInvitations:
            "Failed to send all programs to the patient. Please try again by starting a new counseling. If that doesn't work, please contact the Ksyos servicedesk.",
        failedToSendInvitation:
            "Failed to send this program to the patient. Please try again by starting a new counseling. If that doesn't work, please contact the Ksyos servicedesk.",
        fev1: 'needs to be a number between 0 and 9 with two decimals',
        fileRequired: 'Upload a file',
        fundusScreening: {
            any_only: 'must be one of the following values {{#valids}}',
            array_max: 'must contain a {{#limit}} item(s)',
            date_base: 'must be a date',
            number_base: 'must be a number',
            numberAsString_notANumber: 'must be a number',
            numberAsString_notAString: 'must be a number',
            string_base: 'must be a text',
            string_empty: 'must be a text',
        },
        general: 'Something went wrong, please try again',
        graderRequired: 'Grader is required',
        greater: '"{0}" must be greater than "{1}"',
        height: 'Height must be a number between {min} and {max}',
        identifierDeclarationOrReferenceRequired:
            "For this profession the use of a AGB code or the use of an colleague's AGB code is required",
        identifierDeclarationOrSupervisorRequired: 'Either enter an AGB code or select a supervisor',
        identifierDeclarationRequired: 'Must contain a number with length of 8 digits',
        identifierDeclarationRequiredToRetrieveFundusPatientList:
            'Enter AGB code to retrieve fundus patient list from HealthConnected',
        identifierRegistrationBigNumberRequiredBackend: 'Must contain a number with length of 11 digits',
        identifierRegistrationBigNumberRequiredFrontend: 'Must contain a number with length between 9 and 11 digits',
        identifierRegistrationKpNumberRequired: 'Must contain a number with length of 11 digits',
        image_required: 'Choose one of the images',
        invalidAgb: 'This AGB code is invalid',
        invalidPhoneNumber: "The phone number doesn't seem to be correct. Check if there are at least 10 digits",
        invalidTime: 'The time is invalid',
        koi_date: 'Input the date (dd-mm-yyyy)',
        koi_dateSameOrBefore: 'Date cannot be in the future',
        koi_endDate: 'End date must be larger than or equal to Start date',
        koi_enum: 'Choose one of the options',
        koi_isNotToday: 'Date must be today',
        koi_missingStartDate: 'Start date is missing',
        koi_time: 'Input the time.',
        koi_timeWithoutSeconds: 'Please enter a time',
        koi_todayOrAfter: 'Date cannot be in the past',
        koi_todayOrBefore: 'Date cannot be in the future',
        min: '"{0}" must be larger than or equal to "{1}"',
        minOneHourTimePeriodRequired: 'A time period must be one hour or longer',
        noCurrentServiceOrganizationGroupEmbloom:
            'There is no current valid group selected. You will not be able to use Embloom',
        noMatchDateBsn:
            'We could not find a patient matching this combination of birthdate and social security number. Please check your input.',
        noMatchDateBsnTextCreate:
            "The given birthdate and identifier social security don't match. Are you sure you want to create a patient with this data?",
        noMatchDateBsnTextUpdate:
            "The given birthdate and identifier social security don't match. Are you sure you want to overwrite the patientdata?",
        noMatchPatientFilters: 'No patient found in your organization.',
        noServiceGraderPoolProviders: 'No graders added',
        nonExistingBSN: 'SSN does not exist',
        not_serviceConsumer_subsequent_service:
            'The practitioner does not have active consumer rights for the auto subsequent service. Therefore this cannot be set.',
        not_valid_iban: 'This is not a valid IBAN.',
        notValidWeekend: 'Cannot do it on Saturday or Sunday',
        number_base: 'Must be a number',
        number_integer: 'Number must be an integer',
        number_max: 'Number must be smaller',
        number_min: 'Number must be bigger',
        number_unsafe: 'Number must be smaller',
        numberAsString_decimalComma: 'Must have a comma as decimal separator',
        numberAsString_decimalPoint: 'Must have a point as decimal separator',
        numberAsString_greater: 'Number must be bigger',
        numberAsString_leadingZero: 'No leading zeroes allowed',
        numberAsString_less: 'Number must be smaller',
        numberAsString_max: 'Number must be smaller',
        numberAsString_maxDecimals: 'Too many decimal places',
        numberAsString_min: 'Number must be bigger',
        numberAsString_minDecimals: 'Too few decimal places',
        numberAsString_negativeZero: 'Must be a number',
        numberAsString_noDecimals: 'Must not have decimals',
        numberAsString_notANumber: 'Must be a number',
        numberAsString_notAString: 'Must be a number',
        object_and: 'Please fill in this field',
        object_anyFilledIn: 'Choose one of the options',
        object_base: 'Please fill in this field',
        object_childAnyFilledIn: 'Choose one of the options',
        oneDayPeriodRequired: 'Select at least one day with a minimum time period of one hour',
        onePhoneNumberRequired: 'Enter at least 1 phone number',
        organizationAndGroupSet: 'Only a organization or a group can be set, not both at the same time.',
        passwordNotGoodEnough: 'Must score 80% or more',
        permission_required: 'Provide permission to continue',
        phoneNumberCareMediationWithoutDate: 'You need to enter a date before saving the phone number',
        practitionerAlreadyExisting: 'This practitioner already exists',
        requiredForGp: 'Required',
        requiredForRole: 'Required',
        selectAtLeastOneSubCategory: 'You have to select at least one subcategory if you select a main category',
        selectTreatment: 'Select at least 1 program',
        serviceActivity_same: 'The activity cannot be dependent on itself',
        startTime_required: 'Enter start time',
        string_base: 'Please fill in this field',
        string_elfproef: 'Not a valid SSN',
        string_email: 'Invalid email address',
        string_empty: 'Please fill in this field',
        string_guid: 'Invalid UUID',
        string_kvkNumber: 'Enter a valid KVK number',
        string_ledgerNumber: 'Must consist of four digits',
        string_max: 'The text contains too many characters',
        string_min: 'The text contains too few characters',
        string_newPasswordRepeat: 'Must be equal to your chosen password',
        string_numeric: 'Must consist of digits',
        string_pattern: {
            base: 'Please fill in this field',
        },
        string_regex: 'The provided value fails to match the required pattern',
        string_registrationCode: 'Must consist of twelve letters',
        string_totpToken: 'Must consist of six digits',
        string_trim: 'No leading and trailing spaces allowed',
        string_zipCodeNL:
            'This postal code does not exist. Check your input and use a valid zip code with 4 digits and 2 letters.',
        timeParadox: ' must be after "{0}"',
        timePeriodsOverlap: 'Time periods should not overlap',
        userNameRegex: 'Valid characters are A-Z a-z 0-9 @ . _ -',
        weeks_required: 'Enter number of weeks',
        weight: 'Weight must be a number between {min} and {max}',
        zorgMailNumberRequired: 'Must contain a number of 9 digits',
    },
    versionChecker: {
        newVersionAvailableText:
            'You are currently working in an outdated version. Click "refresh page" to use the latest version.',
        newVersionAvailableTitle: 'New version available',
        refreshApplication: 'Refresh page',
    },
    viewer: {
        fullScreen: 'Fullscreen',
        openNewTab: 'Open in a new tab',
        viewer: 'Viewer',
    },
    waitingTime: {
        applicationWaitingTime: 'Application waiting time',
        explanation:
            'Waiting time that you edit here applies to all care providers that use Ksyos within the organization.',
        manageWaitingTimes: 'Manage waiting times',
        total: 'Number of',
        treatmentWaitingTime: 'Treatment waiting time',
        waitingTimeForReferrals: 'Waiting time for referrals',
        waitingTimes: 'Waiting times',
        week: 'week',
        weeks: 'weeks',
    },
} as const;
export type EN = typeof en;
export type ENGeneric = GenericNestedObject<typeof en>;
